import { useCallback, useEffect, useRef, useState } from 'react';

import { voiceFlow } from 'services/api';
import { createCompletedTurnAudioCapture, CompletedTurnAudioCapture } from './completedTurnAudio';
import { createBufferedNarrationPlayback, BufferedNarrationPlayback, decodeNarrationPcm16 } from './bufferedNarrationPlayback';
import { createNarrationRealtimeTransport, describeNarrationProviderError, isRetryableNarrationTransportError, NarrationRealtimeTransport, NarrationRealtimeTransportError } from './narrationRealtimeTransport';
import { AssistantTranscriptEntry, buildAssistantTranscriptEntries } from './voiceAssistantTranscripts';
import { newLiveId } from './liveVoiceProtocol';
import { clearIncompleteVoiceDrafts, clearVoiceDraft, restoreVoiceDraft, storeVoiceDraft } from './voiceDraftRecovery';
import { conversationalVoiceRecoveryInstructions, exactVoicePassageInstructions } from './voiceNarration';
import { voiceGeneratedContentFailureInstructions, voiceRecoveryActivitySource, voiceRecoveryIsActivity, voiceRecoveryQuestionSource, voiceSaveRecoveryInstructions } from './voiceRecovery';
import { describeVoiceConnectionFailure, VoiceConnectionFailure, VoiceConnectionHttpError, VoiceConnectionStage } from './voiceConnectionRecovery';
import type {
  Question,
  VoiceAssistantTurnDirective,
  VoiceFlowContentItem,
  VoiceFlowCostEstimate,
  VoiceFlowDebugPayload,
  VoiceFlowItemState,
  VoiceFlowProgress,
  VoiceFlowSessionBootstrap,
  VoiceFlowTurn,
  VoiceFlowTurnResult,
  VoiceSessionContext,
  VoicePendingConfirmation,
} from 'types';

type ConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';
type MicPermissionState = 'unknown' | 'granted' | 'denied';
type VoiceUiMode = 'normal' | 'supportive_distress' | 'crisis';
type AssistantPhase = 'idle' | 'listening' | 'responding';
type AssistantTransport = 'rtc' | 'signed_ws';

type DebugTranscriptEntry = {
  id: string;
  text: string;
  timestamp: string;
  isLive?: boolean;
};

type AssistantDebugEntry = AssistantTranscriptEntry;

type DebugRealtimeEvent = {
  id: string;
  receivedAt: string;
  type: string;
  payload: Record<string, unknown>;
};

type CompletedTurnSnapshot = {
  questionId: number | null;
  targetContentId: string;
  targetRevision?: string;
  userTranscript: string;
  assistantTranscript: string;
  assistantResponseId?: string;
  inputAudioItemId: string;
  speechGeneration: number;
  connectionAttemptId?: number;
  speechGroupId?: string;
  confirmation?: VoicePendingConfirmation | null;
  runtimeGeneration?: number;
  transcriptionModel?: string;
  capturedAt?: string;
};

type QueuedAssistantResponse = {
  instructions: string;
  context: AssistantResponseContext;
  replay?: boolean;
  resetUser?: boolean;
  disconnectAfterResponse?: boolean;
  overlapRecoveryCount?: number;
};

type PendingAssistantOnlyResponse = {
  instructionBlock: string;
  responsePurpose?: string;
  disconnectAfterResponse?: boolean;
  responseContext: AssistantResponseContext;
};

type SpokenHostMessage = {
  text: string;
  resolve: () => void;
  reject: (error: Error) => void;
  onInterrupted?: () => void;
  owner?: { host: string; sessionId: number; questionId: number | null; targetRevision?: string; generation: number;
    speechGeneration: number; intent: number };
  responseContext?: AssistantResponseContext;
};

type InterruptedPresentation = {
  context: AssistantResponseContext;
  request?: QueuedAssistantResponse;
  speechGeneration: number;
  noSpeechSeen: boolean;
  failureGeneration?: number;
  taskCommandAction?: string | null;
  bufferedCapture?: AssistantResponseCapture;
  assistantResponseId?: string;
};

type AssistantResponseContext = {
  readonly sessionId: number;
  readonly questionId: number | null;
  readonly targetRevision?: string;
  readonly runtimeGeneration: number;
  readonly connectionAttemptId: number;
  readonly responsePurpose?: string;
  readonly spokenResponseMode: 'conversational' | 'exact';
  readonly narrationToken?: string | null;
  readonly narrationText?: string | null;
  readonly narrationContentId?: string;
  readonly passiveNarration?: boolean;
  readonly commandAction?: string | null;
  readonly autoFinishAfterResponse?: boolean;
  readonly replacesAssistantResponseId?: string;
};

type AssistantResponseCapture = {
  readonly id: number;
  clientEventId?: string;
  providerResponseId?: string;
  readonly context: AssistantResponseContext;
  readonly replay: boolean;
  readonly transport: AssistantTransport;
  transcript: string;
  instructions?: string;
  timestamp: string | null;
  committedTranscript: string;
  cancelled: boolean;
  audioStarted: boolean;
  audioStartedOwned?: boolean;
  audioStopped?: boolean;
  audioStoppedOwned?: boolean;
  generationDone?: boolean;
  generationSucceeded?: boolean;
  closingReadyAt?: number;
  closingSettleTimer?: number;
  automaticFinishReady?: boolean;
  automaticFinishSuperseded?: boolean;
  playbackMuted?: boolean;
  narrationAcknowledged?: boolean;
  narrationReadyAt?: number;
  narrationSettleTimer?: number;
  narrationFailed?: boolean;
  narrationRecoveryQuestionId?: number | null;
  settleCancelledHostMessage?: () => void;
  bufferedOwner?: string;
  bufferedHost?: string;
  bufferedPaused?: boolean;
  bufferedLocallyDrained?: boolean;
  providerAudioEnded?: boolean;
  providerAudioStarted?: boolean;
  outputItemId?: string;
  outputAudioContentIndex?: number;
  cancelWhenCreated?: boolean;
  audioDeltaEventIds?: Set<string>;
};

const assistantCaptionIdentity = (capture?: AssistantResponseCapture | null) => (capture ? {
  responseId: capture.providerResponseId ?? `local-${capture.context.connectionAttemptId}-${capture.id}`,
  replacesResponseId: capture.context.replacesAssistantResponseId,
  responsePurpose: capture.context.responsePurpose,
  questionId: capture.context.questionId,
  assistantOnly: true,
} : {});

type ClosingSafetyWait = {
  key: string;
  deadline: number;
  expired: boolean;
  isCurrent: () => boolean;
  noticePending?: boolean;
  notice?: SpeechSynthesisUtterance;
  deadlineTimer?: number;
};

type AssistantResponseSession = Pick<VoiceFlowSessionBootstrap, 'id' | 'runtime_generation' | 'current_question'>
  & Partial<Pick<VoiceFlowSessionBootstrap, 'current_target' | 'narration_token' | 'narration_text' | 'command_action' | 'spoken_response_mode' | 'assistant_turn_directive'>>;

const canDeliverNarration = (response: Pick<AssistantResponseSession, 'command_action' | 'spoken_response_mode'>): boolean => (
  response.spoken_response_mode !== 'conversational'
  && !['rephrase', 'explain', 'examples'].includes(response.command_action ?? '')
);

const supportsBufferedNarration = (session: VoiceFlowSessionBootstrap | null): boolean => (
  session?.voice_runtime === 'realtime' && session.voice_adapter_version === 2
  && session.turn_detection?.interrupt_response === false
);

const getCompletedTurnKey = (turnSnapshot: CompletedTurnSnapshot) => (
  `${turnSnapshot.targetContentId}:${turnSnapshot.targetRevision ?? ''}:${turnSnapshot.inputAudioItemId || turnSnapshot.userTranscript}`
);

export type UseVoiceSessionParams = {
  flowName: string;
  flowRunId: number | null;
  initialBootstrap?: VoiceFlowSessionBootstrap | null;
  context?: VoiceSessionContext;
  onLearnerTranscript?: (text: string, context: { questionId: number | null; sessionId: number; runtimeGeneration: number }) => Promise<boolean>;
  learnerTranscriptMetadata?: (text: string) => Record<string, unknown>;
  retainSafetyPolling?: boolean;
};

export type UseVoiceSessionResult = {
  loading: boolean;
  error: string | null;
  inputError?: string | null;
  session: VoiceFlowSessionBootstrap | null;
  connectionState: ConnectionState;
  isReconnecting?: boolean;
  micPermission: MicPermissionState;
  isMuted: boolean;
  assistantPhase: AssistantPhase;
  completionClosingPending: boolean;
  turnProcessing: boolean;
  userTranscript: string;
  assistantTranscript: string;
  turnHistory: VoiceFlowTurn[];
  assistantDebugHistory: AssistantDebugEntry[];
  liveUserDebugEntry: DebugTranscriptEntry | null;
  liveAssistantDebugEntry: AssistantDebugEntry | null;
  recentRealtimeEvents: DebugRealtimeEvent[];
  uiMode: VoiceUiMode;
  resourcesPath: string | null;
  ctaLabel: string | null;
  currentQuestion: Question | null;
  currentTarget: VoiceFlowContentItem | null;
  progress: VoiceFlowProgress | null;
  contentSchema: VoiceFlowContentItem[];
  itemStates: VoiceFlowItemState[];
  instructionBlock: string;
  latestExtraction: Record<string, unknown> | null;
  connect: () => Promise<void>;
  reconnect: () => Promise<void>;
  refreshSession: (options?: { sendRealtimeUpdate?: boolean }) => Promise<VoiceFlowSessionBootstrap | null>;
  clearCrisisPause: () => Promise<VoiceFlowSessionBootstrap | null>;
  resetVoiceRun: () => Promise<VoiceFlowSessionBootstrap | null>;
  setDebugRealtimeModelOverride: (realtimeModel: string | null) => Promise<VoiceFlowSessionBootstrap | null>;
  setDebugTranscriptionModelOverride: (transcriptionModel: string | null) => Promise<VoiceFlowSessionBootstrap | null>;
  disconnect: () => void;
  toggleMute: () => void;
  interruptAssistant: () => void;
  replayAssistantMessage: (message: string) => void;
  clearLiveTranscript: () => void;
  narrationConfirmationPending?: boolean;
  confirmNarration?: () => Promise<void>;
  idleWarningSeconds?: number | null;
  isIdlePaused?: boolean;
  stayConnected?: () => void;
  capturePaused?: boolean;
  executeCommand?: (command: string, arguments_?: Record<string, unknown>) => Promise<VoiceFlowSessionBootstrap | null>;
  flushPendingDraft?: () => Promise<boolean>;
  disconnectAsync?: () => Promise<void>;
  speak?: (message: string) => Promise<void>;
  /** Conversational host guidance; true requires full owned, uninterrupted playback. */
  speakConversational?: (instructions: string) => Promise<boolean>;
  terminalActionReady?: 'finish' | 'leave' | null;
  automaticCompletionReady?: boolean;
  canFinishAutomatically?: () => boolean;
};

const getErrorMessage = (error: unknown, fallback: string): string => {
  const responseData = (error as { response?: { data?: { detail?: string; error?: string; message?: string } } })?.response?.data;
  return responseData?.detail ?? responseData?.error ?? responseData?.message ?? fallback;
};

const appendTranscript = (previous: string, nextValue: string) => {
  const cleaned = nextValue.trim();
  if (!cleaned) {
    return previous;
  }
  if (!previous) {
    return cleaned;
  }
  return `${previous} ${cleaned}`.trim();
};

const normalizeTranscriptSpacing = (value: string) => value
  .replace(/\s+/g, ' ')
  .replace(/\s+([,.!?;:])/g, '$1')
  .trim();

const normalizeTranscriptForComparison = (value: string) => normalizeTranscriptSpacing(value)
  .replace(/[\u2018\u2019]/g, "'")
  .replace(/[\u201C\u201D]/g, '"')
  .replace(/\s+/g, '')
  .toLowerCase();

const transcriptIncludes = (container: string, contained: string) => {
  const normalizedContainer = normalizeTranscriptForComparison(container);
  const normalizedContained = normalizeTranscriptForComparison(contained);
  return Boolean(
    normalizedContainer
    && normalizedContained
    && normalizedContainer.includes(normalizedContained),
  );
};

const mergeAssistantTranscript = (previous: string, nextValue: string) => {
  const previousText = normalizeTranscriptSpacing(previous);
  const nextText = normalizeTranscriptSpacing(nextValue);
  if (!nextText) {
    return previousText;
  }
  if (!previousText) {
    return nextText;
  }
  if (transcriptIncludes(nextText, previousText)) {
    return nextText;
  }
  if (transcriptIncludes(previousText, nextText)) {
    return previousText;
  }
  return `${previousText} ${nextText}`.trim();
};

const USER_TRANSCRIPT_DELTA_EVENT = 'conversation.item.input_audio_transcription.delta';
const USER_TRANSCRIPT_COMPLETED_EVENT = 'conversation.item.input_audio_transcription.completed';
const USER_SPEECH_STARTED_EVENT = 'input_audio_buffer.speech_started';
const USER_SPEECH_STOPPED_EVENT = 'input_audio_buffer.speech_stopped';
const REALTIME_RESPONSE_DONE_EVENT = 'response.done';
const ASSISTANT_TRANSCRIPT_DELTA_EVENTS = new Set([
  'response.output_audio_transcript.delta',
  'response.output_text.delta',
]);
const ASSISTANT_TRANSCRIPT_COMPLETED_EVENTS = new Set([
  'response.output_audio_transcript.done',
  'response.output_text.done',
]);
const OUTPUT_AUDIO_BUFFER_STARTED_EVENT = 'output_audio_buffer.started';
const OUTPUT_AUDIO_BUFFER_STOPPED_EVENT = 'output_audio_buffer.stopped';
const OUTPUT_AUDIO_BUFFER_CLEARED_EVENT = 'output_audio_buffer.cleared';
const REALTIME_CLIENT_SECRET_REFRESH_MARGIN_SECONDS = 30;
const AI_SAFETY_POLL_INTERVAL_MS = 1000;
const AI_SAFETY_POLL_TIMEOUT_MS = 10000;
const CLOSING_SAFETY_POLL_TIMEOUT_MS = 60000;
const CLOSING_SAFETY_POLL_INTERVAL_MS = 2000;
const CLOSING_SAFETY_TIMEOUT_MESSAGE = 'Finishing is taking longer than expected. Your answers are safe. Reconnect to check again.';
const MAX_ASSISTANT_RESPONSE_CONTEXTS = 128;

const connectionRecoveryContext = (session: VoiceFlowSessionBootstrap | null) => JSON.stringify([
  session?.id, session?.runtime_generation ?? 0, session?.status, session?.ui_mode,
  session?.voice_runtime, session?.can_connect, session?.voice, session?.realtime_model,
  session?.transcription_model, session?.transcription_transport,
  session?.current_question?.id, session?.current_target?.content_id, session?.current_target?.target_revision,
  session?.command_action, session?.command_receipt?.id, session?.pending_confirmation?.id,
  session?.assistant_turn_directive?.response_purpose, session?.assistant_turn_directive?.auto_finish_after_response,
]);

const getRealtimeEventTranscript = (event: Record<string, unknown>) =>
  typeof event.transcript === 'string'
    ? event.transcript
    : typeof event.delta === 'string'
      ? event.delta
      : typeof event.text === 'string'
        ? event.text
        : '';

const getRealtimeItemId = (event: Record<string, unknown>) =>
  typeof event.item_id === 'string' ? event.item_id : '';

const getRealtimeResponseId = (event: Record<string, unknown>): string => {
  if (typeof event.response_id === 'string') return event.response_id;
  const response = event.response as { id?: unknown } | undefined;
  return typeof response?.id === 'string' ? response.id : '';
};

const getRealtimeEventId = (event: Record<string, unknown>, fallbackPrefix: string) => {
  const directId = typeof event.event_id === 'string' ? event.event_id : '';
  if (directId) {
    return directId;
  }
  const response = event.response;
  if (response && typeof response === 'object') {
    const responseId = (response as { id?: unknown }).id;
    if (typeof responseId === 'string' && responseId) {
      return responseId;
    }
  }
  const itemId = getRealtimeItemId(event);
  return itemId || `${fallbackPrefix}-${Date.now()}`;
};

const getRealtimeUsagePayload = (event: Record<string, unknown>): Record<string, unknown> | null => {
  const response = event.response;
  if (response && typeof response === 'object') {
    const usage = (response as { usage?: unknown }).usage;
    if (usage && typeof usage === 'object') {
      return usage as Record<string, unknown>;
    }
  }
  const usage = event.usage;
  return usage && typeof usage === 'object' ? usage as Record<string, unknown> : null;
};

const getRealtimeErrorMessage = (event: Record<string, unknown>) => {
  if (typeof event.message === 'string' && event.message.trim()) {
    return event.message.trim();
  }

  const errorPayload = event.error;
  if (errorPayload && typeof errorPayload === 'object') {
    const nestedMessage = (errorPayload as { message?: unknown }).message;
    if (typeof nestedMessage === 'string' && nestedMessage.trim()) {
      return nestedMessage.trim();
    }
  }

  return '';
};

const getAssistantTranscriptFromResponse = (event: Record<string, unknown>) => {
  const response = event.response;
  if (!response || typeof response !== 'object') {
    return '';
  }

  const output = (response as { output?: unknown }).output;
  if (!Array.isArray(output)) {
    return '';
  }

  const transcriptSegments: string[] = [];
  output.forEach((outputItem) => {
    if (!outputItem || typeof outputItem !== 'object') {
      return;
    }
    const content = (outputItem as { content?: unknown }).content;
    if (!Array.isArray(content)) {
      return;
    }

    content.forEach((contentItem) => {
      if (!contentItem || typeof contentItem !== 'object') {
        return;
      }
      const transcript = (contentItem as { transcript?: unknown }).transcript;
      const text = (contentItem as { text?: unknown }).text;

      if (typeof transcript === 'string' && transcript.trim()) {
        transcriptSegments.push(transcript.trim());
        return;
      }
      if (typeof text === 'string' && text.trim()) {
        transcriptSegments.push(text.trim());
      }
    });
  });

  return transcriptSegments.join(' ').trim();
};

const isRealtimeClientSecretStale = (
  bootstrap: VoiceFlowSessionBootstrap | null | undefined,
): boolean => {
  if (!bootstrap?.client_secret) {
    return true;
  }

  if (typeof bootstrap.client_secret_expires_at !== 'number') {
    return false;
  }

  const nowInSeconds = Math.floor(Date.now() / 1000);
  return bootstrap.client_secret_expires_at <= nowInSeconds + REALTIME_CLIENT_SECRET_REFRESH_MARGIN_SECONDS;
};

const isNarrationConnectionIdentityCurrent = (
  candidate: VoiceFlowSessionBootstrap | null | undefined,
  expected: VoiceFlowSessionBootstrap,
  flowName: string,
  host: VoiceSessionContext,
): boolean => {
  const matchesHost = (bootstrap: VoiceFlowSessionBootstrap) => (
    (host.kind === 'flow_run'
      ? bootstrap.flow_run_id === host.id && bootstrap.quiz_attempt_id == null
      : bootstrap.quiz_attempt_id === host.id && bootstrap.flow_run_id === null)
    && (!bootstrap.context || (bootstrap.context.kind === host.kind && bootstrap.context.id === host.id))
  );
  return Boolean(candidate && matchesHost(candidate) && matchesHost(expected)
    && candidate.id === expected.id && candidate.flow_name === flowName && expected.flow_name === flowName
    && (candidate.runtime_generation ?? 0) === (expected.runtime_generation ?? 0)
    && supportsBufferedNarration(candidate) && supportsBufferedNarration(expected)
    && candidate.provider === expected.provider && candidate.realtime_model === expected.realtime_model
    && candidate.voice === expected.voice);
};

const isAssistantOnlyTurn = (turn: VoiceFlowTurn): boolean =>
  Boolean(turn.assistant_transcript.trim()) && !turn.user_transcript.trim();

const getCostEstimateTotal = (estimate: VoiceFlowSessionBootstrap['cost_estimate'] | null | undefined): number =>
  typeof estimate?.total_cost_usd_micros === 'number' ? estimate.total_cost_usd_micros : 0;

const getCostEstimateSectionTotal = (
  section: VoiceFlowCostEstimate['realtime'] | VoiceFlowCostEstimate['transcription'] | null | undefined,
): number => (
  typeof section?.cost_usd_micros === 'number' ? section.cost_usd_micros : 0
);

const mergeCostEstimateSections = <T extends VoiceFlowCostEstimate['realtime'] | VoiceFlowCostEstimate['transcription']>(
  nextSection: T | null | undefined,
  previousSection: T | null | undefined,
): T | undefined => (
  getCostEstimateSectionTotal(nextSection) >= getCostEstimateSectionTotal(previousSection)
    ? nextSection ?? previousSection ?? undefined
    : previousSection ?? undefined
);

const mergeCostEstimates = (
  nextEstimate: VoiceFlowCostEstimate | null | undefined,
  previousEstimate: VoiceFlowCostEstimate | null | undefined,
): VoiceFlowCostEstimate | undefined => {
  if (!nextEstimate && !previousEstimate) {
    return undefined;
  }
  if (!nextEstimate) {
    return previousEstimate ?? undefined;
  }
  if (!previousEstimate) {
    return nextEstimate;
  }

  const realtime = mergeCostEstimateSections(nextEstimate.realtime, previousEstimate.realtime);
  const transcription = mergeCostEstimateSections(nextEstimate.transcription, previousEstimate.transcription);
  const totalCostUsdMicros = getCostEstimateSectionTotal(realtime) + getCostEstimateSectionTotal(transcription);

  return {
    ...previousEstimate,
    ...nextEstimate,
    realtime: realtime ?? nextEstimate.realtime,
    transcription: transcription ?? nextEstimate.transcription,
    total_cost_usd_micros: Math.max(totalCostUsdMicros, getCostEstimateTotal(nextEstimate), getCostEstimateTotal(previousEstimate)),
    last_event_at: nextEstimate.last_event_at ?? previousEstimate.last_event_at,
  };
};

const mergeSessionCostEstimate = (
  nextSession: VoiceFlowSessionBootstrap,
  previousSession: VoiceFlowSessionBootstrap | null | undefined,
): VoiceFlowSessionBootstrap => {
  const previousDebug = previousSession?.debug ?? null;
  const nextDebug = nextSession.debug ?? null;
  const mergedCostEstimate = mergeCostEstimates(
    nextSession.cost_estimate ?? nextDebug?.cost_estimate,
    previousSession?.cost_estimate ?? previousDebug?.cost_estimate,
  );

  return {
    ...nextSession,
    cost_estimate: mergedCostEstimate,
    debug: nextDebug
      ? {
          ...nextDebug,
          cost_estimate: mergedCostEstimate,
        }
      : nextDebug,
  };
};

const mergeSessionFromTurnResult = (
  previousSession: VoiceFlowSessionBootstrap | null,
  turnResult: VoiceFlowTurnResult,
): VoiceFlowSessionBootstrap | null => {
  if (!previousSession) {
    return previousSession;
  }

  const previousDebug = previousSession.debug ?? null;
  const nextDebug = turnResult.debug;
  const mergedCostEstimate = mergeCostEstimates(
    nextDebug?.cost_estimate,
    previousSession.cost_estimate ?? previousDebug?.cost_estimate,
  );
  const mergedDebug: VoiceFlowDebugPayload | null = nextDebug
    ? {
      ...previousDebug,
      ...nextDebug,
      latency_metrics:
        nextDebug.latency_metrics
        ?? (isAssistantOnlyTurn(turnResult.turn) ? previousDebug?.latency_metrics : null),
      ai_safety: nextDebug.ai_safety ?? turnResult.ai_safety ?? previousDebug?.ai_safety,
      recent_turns: nextDebug.recent_turns ?? previousDebug?.recent_turns,
      cost_estimate: mergedCostEstimate,
    }
    : previousDebug;

  return {
    ...previousSession,
    voice: turnResult.voice ?? previousSession.voice,
    voice_personality: turnResult.voice_personality ?? previousSession.voice_personality,
    voice_response_length: turnResult.voice_response_length ?? previousSession.voice_response_length,
    voice_follow_up_intensity: turnResult.voice_follow_up_intensity ?? previousSession.voice_follow_up_intensity,
    status: turnResult.session_status,
    ui_mode: turnResult.ui_mode,
    roleplay_safety_status: turnResult.roleplay_safety_status ?? null,
    current_question: turnResult.current_question,
    current_target: turnResult.current_target,
    content_pending: turnResult.content_pending ?? turnResult.current_target?.content_pending ?? false,
    content_schema: turnResult.content_schema,
    item_states: turnResult.item_states,
    progress: turnResult.progress,
    instruction_block: turnResult.instruction_block,
    latest_extraction: turnResult.latest_extraction ?? null,
    ai_safety: turnResult.ai_safety ?? previousSession.ai_safety ?? null,
    recent_turns: turnResult.recent_turns ?? previousSession.recent_turns,
    cost_estimate: mergedCostEstimate,
    assistant_turn_directive: turnResult.assistant_turn_directive,
    pending_confirmation: turnResult.pending_confirmation ?? null,
    command_action: turnResult.command_action ?? null,
    spoken_message: turnResult.spoken_message ?? null,
    spoken_response_mode: turnResult.spoken_response_mode ?? 'exact',
    command_receipt: turnResult.command_receipt ?? null,
    quiz_submission_result: turnResult.quiz_submission_result ?? previousSession.quiz_submission_result ?? null,
    narration_token: canDeliverNarration(turnResult) ? turnResult.narration_token ?? turnResult.current_target?.narration_token ?? null : null,
    narration_text: canDeliverNarration(turnResult) ? turnResult.narration_text ?? turnResult.current_target?.narration_text ?? null : null,
    debug: mergedDebug,
  };
};

const canPrepareGeneratedContent = (session: VoiceFlowSessionBootstrap): boolean => Boolean(
  session.status === 'active' && getBootstrapUiMode(session) !== 'crisis'
  && (session.content_pending || session.current_target?.content_pending)
  && session.current_target && ['ai_summary', 'callback_summary'].includes(session.current_target.question_type)
  && !session.pending_confirmation && !session.roleplay_safety_status
  && !['pause', 'pause_capture', 'settings', 'rephrase', 'explain', 'examples', 'review', 'leave', 'finish']
    .includes(session.command_action ?? ''),
);

const getBootstrapRecentTurns = (bootstrap: VoiceFlowSessionBootstrap | null | undefined): VoiceFlowTurn[] =>
  bootstrap?.recent_turns ?? bootstrap?.debug?.recent_turns ?? [];

const hasPendingSignedNarration = (bootstrap: VoiceFlowSessionBootstrap | null): boolean => {
  const target = bootstrap?.current_target;
  if (!bootstrap || !supportsBufferedNarration(bootstrap) || bootstrap.status !== 'active' || !target
    || target.question_id !== bootstrap.current_question?.id || getBootstrapUiMode(bootstrap) === 'crisis'
    || bootstrap.roleplay_safety_status || bootstrap.pending_confirmation
    || bootstrap.content_pending || target.content_pending || !canDeliverNarration(bootstrap)
    || !bootstrap.assistant_turn_directive?.should_create_response
    || !['description', 'image', 'video_player', 'pdf', 'accordion', 'additional_resources', 'radar_chart',
      'ai_summary', 'callback_summary', 'multiple_reveal', 'custom_screen'].includes(target.question_type)
    || ['pause', 'pause_capture', 'capture_held', 'settings', 'save_settings', 'cancel_settings',
      'rephrase', 'explain', 'examples', 'review', 'leave', 'finish'].includes(bootstrap.command_action ?? '')
    || !(bootstrap.narration_token ?? target.narration_token)?.trim()
    || !(bootstrap.narration_text ?? target.narration_text)?.trim()) return false;
  const state = bootstrap.item_states.find((item) => item.content_id === target.content_id
    && item.question_id === target.question_id);
  return target.status !== 'complete' && state?.status !== 'complete'
    && [target.status, state?.status].some((status) => status === 'unanswered' || status === 'partial');
};

const currentTaskHasSpokenPresentation = (bootstrap: VoiceFlowSessionBootstrap | null, turns: VoiceFlowTurn[]): boolean => {
  const latest = [...turns].reverse().find((turn) => turn.user_transcript.trim() || turn.assistant_transcript.trim());
  return Boolean(latest && isAssistantOnlyTurn(latest)
    && latest.question_id === (bootstrap?.current_question?.id ?? null)
    && (latest.metadata?.target_revision ?? null) === (bootstrap?.current_target?.target_revision ?? null)
    && !['save_failure', 'transcription_failure', 'content_preparation_failure'].includes(String(latest.metadata?.response_purpose ?? '')));
};

const processedCaptureIds = (turns: VoiceFlowTurn[]): Set<string> => new Set(turns.flatMap((turn) => {
  const captureId = turn.metadata?.input_audio_item_id;
  const processing = turn.metadata?.voice_turn_processing as { status?: string } | undefined;
  // Recognition alone does not prove that this turn was processed. Legacy
  // extraction metadata is a completed receipt; failed/in-flight claims are not.
  const processed = processing ? processing.status === 'complete' : Boolean(turn.metadata?.latest_extraction);
  return typeof captureId === 'string' && captureId && processed ? [captureId, ...captureId.split(',')] : [];
}));

const getLatestAssistantOnlyTranscript = (turns: VoiceFlowTurn[]): string => {
  const latestAssistantOnlyTurn = [...turns].reverse().find(isAssistantOnlyTurn);
  return latestAssistantOnlyTurn?.assistant_transcript.trim() ?? '';
};

const isCompletionClosingDirective = (
  directive: VoiceAssistantTurnDirective | null | undefined,
) => Boolean(
  directive?.should_create_response
  && directive.response_purpose === 'completion_closing'
);

const allowsAutomaticCompletion = (active: VoiceFlowSessionBootstrap | null | undefined): boolean => Boolean(
  active?.assistant_turn_directive?.auto_finish_after_response === true
  && active.assistant_turn_directive.response_purpose === 'completion_closing'
  && (active.voice_runtime ?? 'realtime') === 'realtime' && active.voice_adapter_version === 2
  && active.flow_run_id !== null && active.context?.kind !== 'quiz_attempt' && active.quiz_attempt_id == null
  && ['active', 'completed'].includes(active.status) && !active.current_question && !active.current_target
  && active.progress.is_fully_answered === true && active.item_states.every((item) => item.status === 'complete')
  && !active.pending_confirmation && (!active.command_action || ['confirm', 'narration_complete', 'next', 'next_section',
    'continue_roleplay', 'prepare_content', 'skip_introductions', 'repeat'].includes(active.command_action))
  && active.ui_mode !== 'crisis' && active.assistant_turn_directive.mode !== 'crisis'
);

const getSpokenReplyInstructions = (payload: Pick<VoiceFlowSessionBootstrap,
  'instruction_block' | 'spoken_message' | 'spoken_response_mode' | 'assistant_turn_directive'
  | 'voice_runtime' | 'voice_adapter_version' | 'narration_token' | 'narration_text'
  | 'command_action' | 'pending_confirmation' | 'ui_mode' | 'quiz_attempt_id'>): string => {
  if (payload.spoken_response_mode === 'conversational') return payload.instruction_block;
  const authorizedNarrationTransition = payload.spoken_response_mode === 'exact'
    && (payload.voice_runtime ?? 'realtime') === 'realtime' && payload.voice_adapter_version === 2
    && payload.assistant_turn_directive?.narration_transition === true
    && payload.assistant_turn_directive.mode === 'normal' && payload.ui_mode !== 'crisis'
    && !payload.pending_confirmation && payload.quiz_attempt_id == null
    && !['repeat', 'review', 'rephrase', 'explain', 'examples'].includes(payload.command_action ?? '')
    && Boolean(payload.narration_token?.trim() && payload.narration_text?.trim() && payload.instruction_block.trim())
    && (!payload.spoken_message || payload.spoken_message.trim() === payload.narration_text?.trim());
  // The server authorizes the bridge, while the original passage/token still
  // govern delivery. Other exact messages retain their literal wrapper.
  if (authorizedNarrationTransition) return payload.instruction_block;
  if (!payload.spoken_message) return payload.instruction_block;
  return exactVoicePassageInstructions(payload.spoken_message);
};

const hasTerminalSpokenReply = (payload: { command_action?: string | null; spoken_message?: string | null; ui_mode?: string }) => (
  (payload.command_action === 'finish' || payload.command_action === 'leave')
  && Boolean(payload.spoken_message?.trim()) && payload.ui_mode !== 'crisis'
);

const shouldBufferResponse = (context: AssistantResponseContext, active: VoiceFlowSessionBootstrap | null): boolean => (
  supportsBufferedNarration(active) && Boolean(
    (context.narrationToken && context.spokenResponseMode === 'exact')
    || (context.responsePurpose === 'roleplay_host' && context.spokenResponseMode === 'conversational'
      && active?.current_target?.question_type === 'roleplay')
    || (context.autoFinishAfterResponse && allowsAutomaticCompletion(active))
  )
);

const buildAssistantDebugHistoryFromTurns = (turns: VoiceFlowTurn[]): AssistantDebugEntry[] => (
  buildAssistantTranscriptEntries(turns)
);

const getBootstrapUiMode = (
  bootstrap: VoiceFlowSessionBootstrap | null | undefined,
): VoiceUiMode => {
  if (bootstrap?.status === 'crisis_paused') {
    return 'crisis';
  }
  return bootstrap?.ui_mode ?? 'normal';
};

const getBootstrapSafetyMetadata = (
  bootstrap: VoiceFlowSessionBootstrap | null | undefined,
) => {
  const metadata = bootstrap?.debug?.session_metadata ?? {};
  const resourcesPath = bootstrap?.resources_path ?? metadata.resources_path;
  const ctaLabel = bootstrap?.cta_label ?? metadata.cta_label;

  return {
    resourcesPath: typeof resourcesPath === 'string' && resourcesPath.trim() ? resourcesPath : null,
    ctaLabel: typeof ctaLabel === 'string' && ctaLabel.trim() ? ctaLabel : null,
  };
};

export const useVoiceSession = ({
  flowName,
  flowRunId,
  initialBootstrap = null,
  context,
  onLearnerTranscript,
  learnerTranscriptMetadata,
  retainSafetyPolling = false,
}: UseVoiceSessionParams): UseVoiceSessionResult => {
  const hostContext = JSON.stringify([flowName, flowRunId, context?.kind, context?.id]);
  const activeHostContextRef = useRef(hostContext);
  activeHostContextRef.current = hostContext;
  const initialTurnHistory = getBootstrapRecentTurns(initialBootstrap);
  const [loading, setLoading] = useState<boolean>(() => !initialBootstrap);
  const [error, setError] = useState<string | null>(null);
  const [inputError, setInputError] = useState<string | null>(null);
  const [session, setSession] = useState<VoiceFlowSessionBootstrap | null>(initialBootstrap);
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [closingSafetyTimeoutKey, setClosingSafetyTimeoutKey] = useState<string | null>(null);
  const [micPermission, setMicPermission] = useState<MicPermissionState>('unknown');
  const [isMuted, setIsMuted] = useState(false);
  const [capturePaused, setCapturePaused] = useState(false);
  const [terminalActionReady, setTerminalActionReady] = useState<'finish' | 'leave' | null>(null);
  const [automaticCompletionReady, setAutomaticCompletionReady] = useState(false);
  const [reconnectRevision, setReconnectRevision] = useState(0);
  const capturePausedRef = useRef(false);
  const learnerTranscriptHandlerRef = useRef(onLearnerTranscript);
  learnerTranscriptHandlerRef.current = onLearnerTranscript;
  const learnerTranscriptMetadataRef = useRef(learnerTranscriptMetadata);
  learnerTranscriptMetadataRef.current = learnerTranscriptMetadata;
  const retainSafetyPollingRef = useRef(retainSafetyPolling);
  retainSafetyPollingRef.current = retainSafetyPolling;
  const [assistantPhase, setAssistantPhase] = useState<AssistantPhase>('idle');
  const [completionClosingPending, setCompletionClosingPending] = useState(
    () => isCompletionClosingDirective(initialBootstrap?.assistant_turn_directive),
  );
  const [turnProcessing, setTurnProcessing] = useState(false);
  const [userTranscript, setUserTranscript] = useState('');
  const [assistantTranscript, setAssistantTranscript] = useState('');
  const [turnHistory, setTurnHistory] = useState<VoiceFlowTurn[]>(initialTurnHistory);
  const [assistantDebugHistory, setAssistantDebugHistory] = useState<AssistantDebugEntry[]>(
    () => buildAssistantDebugHistoryFromTurns(initialTurnHistory),
  );
  const [liveUserDebugEntry, setLiveUserDebugEntry] = useState<DebugTranscriptEntry | null>(null);
  const [liveAssistantDebugEntry, setLiveAssistantDebugEntry] = useState<AssistantDebugEntry | null>(null);
  const [recentRealtimeEvents, setRecentRealtimeEvents] = useState<DebugRealtimeEvent[]>([]);
  const [uiMode, setUiMode] = useState<VoiceUiMode>(() => getBootstrapUiMode(initialBootstrap));
  const [resourcesPath, setResourcesPath] = useState<string | null>(
    () => getBootstrapSafetyMetadata(initialBootstrap).resourcesPath,
  );
  const [ctaLabel, setCtaLabel] = useState<string | null>(
    () => getBootstrapSafetyMetadata(initialBootstrap).ctaLabel,
  );

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const completedAudioCaptureRef = useRef<CompletedTurnAudioCapture | null>(null);
  const transcriptionControllersRef = useRef<Set<AbortController>>(new Set());
  const transcribedItemIdsRef = useRef<Set<string>>(new Set());
  const transcriptionQueueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingTranscriptionCountRef = useRef(0);
  const captureTurnRef = useRef<CompletedTurnSnapshot | null>(null);
  const speechGroupRef = useRef<{ id: string; questionId: number | null; targetContentId: string; startedAt: number; targetRevision?: string } | null>(null);
  const transcribedTurnsRef = useRef<CompletedTurnSnapshot[]>([]);
  const failedSpeechGroupsRef = useRef(new Set<string>());
  const latestFailedSpeechGenerationRef = useRef(-1);
  const flushTranscribedTurnsRef = useRef<() => void>(() => undefined);
  const streamingTurnContextsRef = useRef<Map<string, CompletedTurnSnapshot>>(new Map());
  const activeNativeSpeechItemIdRef = useRef('');
  const activeSessionRef = useRef(session);
  activeSessionRef.current = session;
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const narrationPlaybackRef = useRef<BufferedNarrationPlayback | null>(null);
  const narrationTransportRef = useRef<NarrationRealtimeTransport | null>(null);
  const narrationTransportControllerRef = useRef<AbortController | null>(null);
  const failNarrationTransportRef = useRef<(failure: Error) => void>(() => undefined);
  const narrationPlaybackControllerRef = useRef<AbortController | null>(null);
  const narrationPlaybackStartupTimerRef = useRef<number | null>(null);
  const bufferedNarrationCaptureRef = useRef<AssistantResponseCapture | null>(null);
  const narrationPlaybackFailedRef = useRef(false);
  const discardedNarrationRef = useRef<{ owner: string; responseId?: string; cleared: boolean } | null>(null);
  const narrationClearTimerRef = useRef<number | null>(null);
  const discardBufferedNarrationRef = useRef<(capture?: AssistantResponseCapture | null) => void>(() => undefined);
  const browserAudioPlayableRef = useRef(false);
  const currentUserTranscriptRef = useRef('');
  const currentAssistantTranscriptRef = useRef('');
  const currentUserTranscriptItemIdRef = useRef('');
  const currentUserDebugTimestampRef = useRef<string | null>(null);
  const currentAssistantDebugTimestampRef = useRef<string | null>(null);
  const lastAssistantCommittedTranscriptRef = useRef('');
  const lastPersistedAssistantTranscriptRef = useRef(getLatestAssistantOnlyTranscript(initialTurnHistory));
  const lastPersistedAssistantTurnKeyRef = useRef('');
  const isPersistingTurnRef = useRef(false);
  const lastPersistedTurnKeyRef = useRef('');
  const assistantPhaseRef = useRef<AssistantPhase>('idle');
  const userSpeechGenerationRef = useRef(0);
  const userSpeechActiveRef = useRef(false);
  const pendingCompletedTurnsRef = useRef<CompletedTurnSnapshot[]>([]);
  const processCompletedTurnRef = useRef<((turnSnapshot: CompletedTurnSnapshot) => Promise<void>) | null>(null);
  const assistantDebugSequenceRef = useRef(0);
  const realtimeEventSequenceRef = useRef(0);
  const connectionAttemptIdRef = useRef(0);
  const shouldCreateOpeningResponseRef = useRef(!currentTaskHasSpokenPresentation(initialBootstrap ?? null, initialTurnHistory));
  const lastRequestedQuestionRef = useRef<number | null | undefined>(undefined);
  const lastRequestedTargetRevisionRef = useRef<string | null | undefined>(undefined);
  const replayInProgressRef = useRef(false);
  const assistantOutputAudioActiveRef = useRef(false);
  const assistantOutputAudioSeenForResponseRef = useRef(false);
  const pendingDisconnectAfterResponseRef = useRef(false);
  const activePersistingTurnKeyRef = useRef('');
  const completionClosingResponseDoneRef = useRef(false);
  const completionClosingDisconnectFallbackRef = useRef<number | null>(null);
  const pendingAssistantOnlyResponseRef = useRef<PendingAssistantOnlyResponse | null>(null);
  const pendingAssistantOnlySessionUpdateRef = useRef<string | null>(null);
  const pendingConnectionNoticeRef = useRef<{ instructions: string; purpose: string; commandAction?: string | null;
    spokenResponseMode?: VoiceFlowSessionBootstrap['spoken_response_mode']; questionId?: number | null; resumeAutomaticClosing?: boolean } | null>(null);
  const assistantResponseSequenceRef = useRef(0);
  const currentAssistantResponseRef = useRef<AssistantResponseCapture | null>(null);
  const pendingAssistantResponseCapturesRef = useRef<AssistantResponseCapture[]>([]);
  const assistantResponsesByProviderIdRef = useRef(new Map<string, AssistantResponseCapture>());
  const handleRealtimeEventRef = useRef<((event: Record<string, unknown>, source?: AssistantTransport) => void) | null>(null);
  const aiSafetyPollTimeoutRef = useRef<number | null>(null);
  const aiSafetyPollStartedAtRef = useRef<number | null>(null);
  const aiSafetyPollRevisionRef = useRef(0);
  const aiSafetyPollControllerRef = useRef<AbortController | null>(null);
  const aiSafetyPollOwnershipRef = useRef<(() => boolean) | null>(null);
  const aiSafetyClosingScopeRef = useRef<ClosingSafetyWait | null>(null);
  const closingSafetyWaitRef = useRef<ClosingSafetyWait | null>(null);
  const closingSafetyIntentRevisionRef = useRef(0);
  const speechStartedAtByItemIdRef = useRef<Record<string, number>>({});
  const latestSpeechStartedAtRef = useRef<number | null>(null);
  const recordedUsageEventIdsRef = useRef<Set<string>>(new Set());
  const lastUnsavedTurnRef = useRef<CompletedTurnSnapshot | null>(null);
  const recognizedUserTurnsRef = useRef(new Map<string, { sessionId: number; turn: VoiceFlowTurn }>());
  const recognizedTurnSequenceRef = useRef(0);
  const attemptedRecoveredCapturesRef = useRef(new Set<string>());
  const activeGenerationRef = useRef<AssistantResponseCapture | null>(null);
  const activeGenerationRequestRef = useRef<QueuedAssistantResponse | null>(null);
  const queuedAssistantResponsesRef = useRef<QueuedAssistantResponse[]>([]);
  const blockedProviderResponseRef = useRef<string | null>(null);
  const flushAssistantResponsesRef = useRef<() => void>(() => undefined);
  const flushClosingSafetyNoticeRef = useRef<() => void>(() => undefined);
  const cancelRealtimeResponseRef = useRef<() => void>(() => undefined);
  const generationRecoveryTimeoutRef = useRef<number | null>(null);
  const automaticReconnectCountRef = useRef(0);
  const reconnectFailureAnnouncedRef = useRef(false);
  const reconnectTimerRef = useRef<number | null>(null);
  const connectionRecoveryRevisionRef = useRef(0);
  const recoveryContextHoldRef = useRef(false);
  const connectionFailureRef = useRef<(VoiceConnectionFailure & { attempt: number; revision: number; context: string; host: string }) | null>(null);
  const connectionNoticeRef = useRef<SpeechSynthesisUtterance | null>(null);
  const intentionalDisconnectRef = useRef(false);
  const narrationAckRef = useRef<(capture: AssistantResponseCapture) => void>(() => undefined);
  const pendingNarrationCaptureRef = useRef<AssistantResponseCapture | null>(null);
  const narrationInFlightRef = useRef<AssistantResponseCapture | null>(null);
  const pendingSpokenMessagesRef = useRef<SpokenHostMessage[]>([]);
  const activeSpokenMessageRef = useRef<SpokenHostMessage | null>(null);
  const ownsSpokenHostMessageRef = useRef<(message: SpokenHostMessage) => boolean>(() => false);
  const flushSpokenMessagesRef = useRef<() => void>(() => undefined);
  const settledPlaybackRef = useRef<(capture: AssistantResponseCapture) => void>(() => undefined);
  const preparedContentRef = useRef<{ key: string; status: 'pending' | 'failed' | 'complete' } | null>(null);
  const [contentPreparationRevision, setContentPreparationRevision] = useState(0);
  const contentPreparationIntentRef = useRef(0);
  const interruptedPresentationRef = useRef<InterruptedPresentation | null>(null);
  const presentationResumeInFlightRef = useRef<InterruptedPresentation | null>(null);
  const flushPresentationResumeRef = useRef<() => boolean>(() => false);
  const activityRecoveryNoticeRef = useRef<{ noticeContext: AssistantResponseContext; activityContext: AssistantResponseContext;
    speechGeneration: number } | null>(null);
  const pendingAutomaticClosingRef = useRef<AssistantResponseCapture | null>(null);
  const finishAutomaticClosingRef = useRef<(capture: AssistantResponseCapture) => boolean>(() => false);

  const currentQuestion = session?.current_question ?? null;
  const currentTarget = session?.current_target ?? null;
  const progress = session?.progress ?? null;
  const contentSchema = session?.content_schema ?? [];
  const itemStates = session?.item_states ?? [];
  const instructionBlock = session?.instruction_block ?? '';
  const latestExtraction = (session?.latest_extraction as Record<string, unknown> | null | undefined) ?? null;

  const stopAiSafetyPolling = useCallback(() => {
    aiSafetyPollRevisionRef.current += 1;
    if (aiSafetyPollTimeoutRef.current !== null) {
      window.clearTimeout(aiSafetyPollTimeoutRef.current);
      aiSafetyPollTimeoutRef.current = null;
    }
    aiSafetyPollStartedAtRef.current = null;
    aiSafetyPollControllerRef.current?.abort();
    aiSafetyPollControllerRef.current = null;
    aiSafetyPollOwnershipRef.current = null;
    aiSafetyClosingScopeRef.current = null;
    const wait = closingSafetyWaitRef.current;
    if (wait?.deadlineTimer !== undefined) window.clearTimeout(wait.deadlineTimer);
    if (wait) wait.deadlineTimer = undefined;
  }, []);

  const cancelConnectionNotice = useCallback(() => {
    if (!connectionNoticeRef.current) return;
    connectionNoticeRef.current = null;
    window.speechSynthesis?.cancel();
  }, []);

  const flushClosingSafetyNotice = useCallback(() => {
    const wait = closingSafetyWaitRef.current;
    if (!wait?.noticePending) return;
    if (!wait.isCurrent()) {
      wait.noticePending = false;
      return;
    }
    const hasConversationWork = () => Boolean(activeGenerationRef.current || blockedProviderResponseRef.current || assistantOutputAudioActiveRef.current
      || userSpeechActiveRef.current || captureTurnRef.current || pendingTranscriptionCountRef.current
      || streamingTurnContextsRef.current.size || isPersistingTurnRef.current || lastUnsavedTurnRef.current
      || transcribedTurnsRef.current.length || pendingCompletedTurnsRef.current.length
      || queuedAssistantResponsesRef.current.length || pendingAssistantOnlyResponseRef.current
      || pendingSpokenMessagesRef.current.length || activeSpokenMessageRef.current);
    if (hasConversationWork()) return;
    wait.noticePending = false;
    if (typeof window.speechSynthesis === 'undefined' || typeof SpeechSynthesisUtterance === 'undefined') return;
    cancelConnectionNotice();
    const notice = new SpeechSynthesisUtterance(CLOSING_SAFETY_TIMEOUT_MESSAGE);
    notice.lang = 'en-US';
    const release = () => { if (connectionNoticeRef.current === notice) connectionNoticeRef.current = null; };
    notice.onend = release;
    notice.onerror = release;
    notice.onstart = () => {
      if (connectionNoticeRef.current === notice && (closingSafetyWaitRef.current !== wait
        || !wait.isCurrent() || hasConversationWork())) cancelConnectionNotice();
    };
    wait.notice = notice;
    connectionNoticeRef.current = notice;
    window.speechSynthesis.speak(notice);
  }, [cancelConnectionNotice]);
  flushClosingSafetyNoticeRef.current = flushClosingSafetyNotice;

  const invalidateConnectionRecovery = useCallback(() => {
    connectionRecoveryRevisionRef.current += 1;
    if (aiSafetyClosingScopeRef.current) stopAiSafetyPolling();
    connectionFailureRef.current = null;
    if (reconnectTimerRef.current !== null) window.clearTimeout(reconnectTimerRef.current);
    reconnectTimerRef.current = null;
    cancelConnectionNotice();
    setIsReconnecting(false);
  }, [cancelConnectionNotice, stopAiSafetyPolling]);

  const rememberConnectionFailure = useCallback((failure: unknown, stage: VoiceConnectionStage) => {
    const described = describeVoiceConnectionFailure(failure, stage);
    connectionFailureRef.current = { ...described, attempt: connectionAttemptIdRef.current,
      revision: connectionRecoveryRevisionRef.current, context: connectionRecoveryContext(activeSessionRef.current), host: activeHostContextRef.current };
    console.warn('Voice connection failure', { ...described, connectionAttempt: connectionAttemptIdRef.current,
      sessionId: activeSessionRef.current?.id ?? null, runtimeGeneration: activeSessionRef.current?.runtime_generation ?? 0 });
    return described;
  }, []);

  const setAssistantPhaseValue = useCallback((nextPhase: AssistantPhase) => {
    assistantPhaseRef.current = nextPhase;
    setAssistantPhase(nextPhase);
  }, []);

  // Retiring a retained passage is different from a tentative VAD pause. Only
  // actual words, explicit controls, or an ownership/safety change retire it.
  const discardBufferedNarration = useCallback((capture = bufferedNarrationCaptureRef.current) => {
    if (!capture?.bufferedOwner || capture.bufferedLocallyDrained) return;
    const player = narrationPlaybackRef.current;
    const heardMs = Math.max(0, Math.floor(player?.heardMs(capture.bufferedOwner) ?? 0));
    const channel = dataChannelRef.current;
    if (!capture.cancelled && capture.transport === 'signed_ws') {
      discardedNarrationRef.current = { owner: capture.bufferedOwner, responseId: capture.providerResponseId, cleared: true };
      if (!capture.generationDone) {
        if (capture.providerResponseId) narrationTransportRef.current?.send({ type: 'response.cancel', response_id: capture.providerResponseId });
        else capture.cancelWhenCreated = true;
      }
      if (capture.outputItemId) narrationTransportRef.current?.send({ type: 'conversation.item.truncate',
        item_id: capture.outputItemId, content_index: capture.outputAudioContentIndex ?? 0, audio_end_ms: heardMs });
    } else if (!capture.cancelled && channel?.readyState === 'open') {
      const providerOutputEnded = capture.providerAudioEnded === true;
      discardedNarrationRef.current = { owner: capture.bufferedOwner, responseId: capture.providerResponseId, cleared: providerOutputEnded };
      if (!capture.generationDone) channel.send(JSON.stringify({ type: 'response.cancel' }));
      if (!providerOutputEnded) channel.send(JSON.stringify({ type: 'output_audio_buffer.clear' }));
      if (capture.outputItemId) channel.send(JSON.stringify({ type: 'conversation.item.truncate',
        item_id: capture.outputItemId, content_index: capture.outputAudioContentIndex ?? 0, audio_end_ms: heardMs }));
      if (narrationClearTimerRef.current !== null) window.clearTimeout(narrationClearTimerRef.current);
      narrationClearTimerRef.current = null;
      if (!providerOutputEnded) {
        const discarded = discardedNarrationRef.current;
        narrationClearTimerRef.current = window.setTimeout(() => {
          narrationClearTimerRef.current = null;
          if (discardedNarrationRef.current !== discarded || discarded.cleared) return;
          narrationPlaybackFailedRef.current = true;
          setError('The previous narration did not stop cleanly. Your progress is preserved. Please reconnect to continue.');
          setAssistantPhaseValue('listening');
        }, 5000);
      }
    }
    capture.cancelled = true;
    const hostMessage = activeSpokenMessageRef.current;
    if (hostMessage?.responseContext === capture.context) {
      activeSpokenMessageRef.current = null;
      hostMessage.onInterrupted?.();
    }
    capture.bufferedPaused = false;
    capture.narrationFailed = true;
    if (capture.narrationSettleTimer !== undefined) window.clearTimeout(capture.narrationSettleTimer);
    capture.narrationSettleTimer = undefined;
    player?.discard(capture.bufferedOwner);
    if (bufferedNarrationCaptureRef.current === capture) bufferedNarrationCaptureRef.current = null;
    if (pendingNarrationCaptureRef.current === capture) pendingNarrationCaptureRef.current = null;
    if (interruptedPresentationRef.current?.bufferedCapture === capture) interruptedPresentationRef.current = null;
    assistantOutputAudioActiveRef.current = false;
  }, [setAssistantPhaseValue]);
  discardBufferedNarrationRef.current = discardBufferedNarration;

  const bufferedNarrationIsCurrent = useCallback((capture: AssistantResponseCapture, next = activeSessionRef.current) => {
    const canonicalToken = next?.narration_token ?? next?.current_target?.narration_token;
    const hostMessage = activeSpokenMessageRef.current;
    const host = capture.context.responsePurpose === 'roleplay_host';
    const closing = capture.context.autoFinishAfterResponse;
    return Boolean(next && !capture.cancelled && !capture.narrationFailed
      && (capture === bufferedNarrationCaptureRef.current
        || (capture.bufferedLocallyDrained && capture === currentAssistantResponseRef.current))
      && capture.bufferedHost === activeHostContextRef.current
      && capture.context.connectionAttemptId === connectionAttemptIdRef.current
      && capture.context.sessionId === next.id && capture.context.runtimeGeneration === (next.runtime_generation ?? 0)
      && capture.context.questionId === (next.current_question?.id ?? null)
      && capture.context.targetRevision === (next.current_target?.target_revision ?? undefined)
      && next.ui_mode !== 'crisis' && next.status !== 'crisis_paused' && !next.roleplay_safety_status
      && !capturePausedRef.current
      && (closing ? allowsAutomaticCompletion(next)
        : host ? next.status === 'active' && next.current_target?.question_type === 'roleplay'
          && hostMessage?.responseContext === capture.context && ownsSpokenHostMessageRef.current(hostMessage)
        : next.status === 'active' && (!canonicalToken || capture.context.narrationToken === canonicalToken)
          && canDeliverNarration(next) && next.current_target?.question_type !== 'roleplay')
      // A host turn owns its presentation through the phase/intent checks
      // above. Its fresh context intentionally omits the learner's preceding
      // command (for example capture_held after "ready").
      && (host || !next.command_action || next.command_action === capture.context.commandAction));
  }, []);

  useEffect(() => {
    const capture = bufferedNarrationCaptureRef.current;
    if (capture && !bufferedNarrationIsCurrent(capture, session)) discardBufferedNarration(capture);
  }, [bufferedNarrationIsCurrent, discardBufferedNarration, hostContext, session]);

  useEffect(() => {
    if (session?.ai_safety?.status === 'pending') return;
    const capture = currentAssistantResponseRef.current;
    if (capture?.bufferedLocallyDrained && capture.bufferedHost === activeHostContextRef.current) {
      narrationAckRef.current(capture);
      settledPlaybackRef.current(capture);
    }
    if (interruptedPresentationRef.current?.bufferedCapture
      || (supportsBufferedNarration(activeSessionRef.current)
        && queuedAssistantResponsesRef.current.some((request) => request.context.narrationToken))) flushAssistantResponsesRef.current();
  }, [session?.ai_safety?.status]);

  const hydrateDebugState = useCallback((bootstrapSession: VoiceFlowSessionBootstrap | null) => {
    const recentTurns = getBootstrapRecentTurns(bootstrapSession);
    const savedCaptureIds = new Set(recentTurns.map((turn) => turn.metadata?.input_audio_item_id));
    const processedIds = processedCaptureIds(recentTurns);
    let recoveredProcessedCapture = false;
    if (bootstrapSession) {
      processedIds.forEach((captureId) => clearVoiceDraft(bootstrapSession.id, captureId));
      const unsaved = lastUnsavedTurnRef.current;
      if (unsaved && unsaved.inputAudioItemId.split(',').every((id) => processedIds.has(id))) {
        recoveredProcessedCapture = true;
        lastUnsavedTurnRef.current = null;
      }
    }
    recognizedUserTurnsRef.current.forEach(({ sessionId, turn }, key) => {
      if (sessionId !== bootstrapSession?.id || savedCaptureIds.has(turn.metadata?.input_audio_item_id)) {
        recognizedUserTurnsRef.current.delete(key);
      }
    });
    setTurnHistory([...recentTurns, ...Array.from(recognizedUserTurnsRef.current.values(), ({ turn }) => turn)]);
    setAssistantDebugHistory(buildAssistantDebugHistoryFromTurns(recentTurns));
    currentUserDebugTimestampRef.current = null;
    currentAssistantDebugTimestampRef.current = null;
    lastAssistantCommittedTranscriptRef.current = getLatestAssistantOnlyTranscript(recentTurns);
    lastPersistedAssistantTranscriptRef.current = getLatestAssistantOnlyTranscript(recentTurns);
    lastPersistedAssistantTurnKeyRef.current = '';
    if (currentTaskHasSpokenPresentation(bootstrapSession, recentTurns)) shouldCreateOpeningResponseRef.current = false;
    else {
      const latest = [...recentTurns].reverse().find((turn) => turn.user_transcript.trim() || turn.assistant_transcript.trim());
      if (recoveredProcessedCapture || lastRequestedQuestionRef.current !== (bootstrapSession?.current_question?.id ?? null)
        || lastRequestedTargetRevisionRef.current !== (bootstrapSession?.current_target?.target_revision ?? undefined)
        || ['save_failure', 'transcription_failure'].includes(String(latest?.metadata?.response_purpose ?? ''))) {
        shouldCreateOpeningResponseRef.current = true;
      }
    }
    setLiveUserDebugEntry(null);
    setLiveAssistantDebugEntry(null);
  }, []);

  const rememberCapturedTranscript = useCallback((snapshot: CompletedTurnSnapshot, sessionId: number) => {
    if (!snapshot.userTranscript.trim()) return;
    const captureId = snapshot.inputAudioItemId;
    const fragments = new Set(captureId.split(','));
    if (fragments.size > 1) fragments.forEach((id) => recognizedUserTurnsRef.current.delete(id));
    const saved = recognizedUserTurnsRef.current.get(captureId);
    const timestamp = snapshot.capturedAt ?? saved?.turn.created_at ?? new Date().toISOString();
    const turn: VoiceFlowTurn = {
      id: saved?.turn.id ?? -(++recognizedTurnSequenceRef.current), question_id: snapshot.questionId,
      user_transcript: snapshot.userTranscript, assistant_transcript: '', created_at: timestamp,
      metadata: { input_audio_item_id: captureId, input_audio_started_at: timestamp, capture_local_only: true },
    };
    recognizedUserTurnsRef.current.set(captureId, { sessionId, turn });
    setTurnHistory((previousTurns) => {
      const previous = previousTurns.filter((entry) => !(entry.metadata?.capture_local_only
        && fragments.size > 1 && fragments.has(String(entry.metadata?.input_audio_item_id))));
      const existing = previous.find((entry) => entry.metadata?.input_audio_item_id === captureId);
      if (existing && !existing.metadata?.capture_local_only) return previous;
      return existing ? previous.map((entry) => entry.id === existing.id ? turn : entry) : [...previous, turn];
    });
    setLiveUserDebugEntry({ id: captureId, text: snapshot.userTranscript, timestamp, isLive: true });
  }, []);

  const clearLiveTranscript = useCallback(() => {
    currentUserTranscriptRef.current = '';
    currentAssistantTranscriptRef.current = '';
    currentUserTranscriptItemIdRef.current = '';
    currentUserDebugTimestampRef.current = null;
    currentAssistantDebugTimestampRef.current = null;
    lastAssistantCommittedTranscriptRef.current = '';
    setLiveUserDebugEntry(null);
    setLiveAssistantDebugEntry(null);
    setUserTranscript('');
    setAssistantTranscript('');
  }, []);

  const clearAssistantTranscript = useCallback(() => {
    currentAssistantTranscriptRef.current = '';
    currentAssistantDebugTimestampRef.current = null;
    lastAssistantCommittedTranscriptRef.current = '';
    setLiveAssistantDebugEntry(null);
    setAssistantTranscript('');
  }, []);

  const clearCompletionClosingDisconnectFallback = useCallback(() => {
    if (completionClosingDisconnectFallbackRef.current !== null) {
      window.clearTimeout(completionClosingDisconnectFallbackRef.current);
      completionClosingDisconnectFallbackRef.current = null;
    }
  }, []);

  const pushRealtimeEvent = useCallback((event: Record<string, unknown>) => {
    const receivedAt = new Date().toISOString();
    const eventType = typeof event.type === 'string' ? event.type : 'unknown';
    const nextEventId = `realtime-event-${realtimeEventSequenceRef.current++}`;
    setRecentRealtimeEvents((previous) => [
      ...previous.slice(-39),
      {
        id: nextEventId,
        receivedAt,
        type: eventType,
        payload: event,
      },
    ]);
  }, []);

  const fetchSession = useCallback(async (expectedConnectionAttempt?: number, mayApply?: () => boolean,
    didApply?: (next: VoiceFlowSessionBootstrap) => void) => {
    if (!context && typeof flowRunId !== 'number') {
      throw new Error('Voice mode requires an active flow run or quiz attempt.');
    }

    const response = await voiceFlow.createSession(flowName, context ?? (flowRunId as number));
    if (mayApply && !mayApply()) throw new DOMException('This reconnect request was superseded.', 'AbortError');
    if (expectedConnectionAttempt !== undefined && expectedConnectionAttempt !== connectionAttemptIdRef.current) return response.data;
    activeSessionRef.current = response.data;
    didApply?.(response.data);
    setSession((previous) => mergeSessionCostEstimate(response.data, previous));
    hydrateDebugState(response.data);
    setUiMode(getBootstrapUiMode(response.data));
    const safetyMetadata = getBootstrapSafetyMetadata(response.data);
    setResourcesPath(safetyMetadata.resourcesPath);
    setCtaLabel(safetyMetadata.ctaLabel);
    setCompletionClosingPending(isCompletionClosingDirective(response.data.assistant_turn_directive));
    return response.data;
  }, [flowName, flowRunId, context, hydrateDebugState]);

  const loadSession = useCallback(async () => {
    if (!context && typeof flowRunId !== 'number') {
      setLoading(false);
      setError('Voice mode requires an active module run.');
      return;
    }

    setLoading(true);
    setError(null);
    // Effect setup is a new activation, including React StrictMode's setup /
    // cleanup / setup probe. A retired setup still fails its revision guard.
    intentionalDisconnectRef.current = false;
    recoveryContextHoldRef.current = false;
    const attempt = connectionAttemptIdRef.current;
    const revision = connectionRecoveryRevisionRef.current;
    const host = activeHostContextRef.current;
    const startingContext = connectionRecoveryContext(activeSessionRef.current);
    const ownsLoad = () => attempt === connectionAttemptIdRef.current && revision === connectionRecoveryRevisionRef.current
      && host === activeHostContextRef.current && !intentionalDisconnectRef.current;
    try {
      await fetchSession(attempt, () => ownsLoad() && startingContext === connectionRecoveryContext(activeSessionRef.current),
        () => setLoading(false));
    } catch (sessionError) {
      if (!ownsLoad() || startingContext !== connectionRecoveryContext(activeSessionRef.current)) return;
      const failure = rememberConnectionFailure(sessionError, 'session');
      setError(failure.message);
      setIsReconnecting(failure.retryable);
      setConnectionState('error');
    } finally {
      if (ownsLoad()) setLoading(false);
    }
  }, [fetchSession, flowRunId, context, rememberConnectionFailure]);

  useEffect(() => {
    if (initialBootstrap) {
      const newerContextDuringRecovery = activeSessionRef.current
        && connectionRecoveryContext(activeSessionRef.current) !== connectionRecoveryContext(initialBootstrap)
        && automaticReconnectCountRef.current > 0;
      if (newerContextDuringRecovery) {
        intentionalDisconnectRef.current = true;
        recoveryContextHoldRef.current = true;
        invalidateConnectionRecovery();
        setConnectionState('disconnected');
      } else {
        intentionalDisconnectRef.current = false;
      }
      activeSessionRef.current = initialBootstrap;
      setSession((previous) => mergeSessionCostEstimate(initialBootstrap, previous));
      hydrateDebugState(initialBootstrap);
      setUiMode(getBootstrapUiMode(initialBootstrap));
      const safetyMetadata = getBootstrapSafetyMetadata(initialBootstrap);
      setResourcesPath(safetyMetadata.resourcesPath);
      setCtaLabel(safetyMetadata.ctaLabel);
      setCompletionClosingPending(isCompletionClosingDirective(initialBootstrap.assistant_turn_directive));
      setLoading(false);
      return;
    }
    void loadSession();
  }, [hydrateDebugState, initialBootstrap, invalidateConnectionRecovery, loadSession]);

  const preserveDraft = useCallback((snapshot?: CompletedTurnSnapshot | null, options?: { finalized?: boolean; retryable?: boolean }) => {
    const active = activeSessionRef.current;
    const draft = snapshot ?? lastUnsavedTurnRef.current
      ?? transcribedTurnsRef.current[0] ?? pendingCompletedTurnsRef.current[0]
      ?? [...streamingTurnContextsRef.current.values()].reverse().find((item) => item.userTranscript.trim());
    if (!active || !draft?.userTranscript.trim()) return;
    storeVoiceDraft({
      sessionId: active.id, runtimeGeneration: draft.runtimeGeneration ?? active.runtime_generation ?? 0,
      questionId: draft.questionId, contentId: draft.targetContentId,
      targetRevision: draft.targetRevision,
      transcript: draft.userTranscript, inputAudioItemId: draft.inputAudioItemId,
      speechGroupId: draft.speechGroupId, confirmation: draft.confirmation, savedAt: Date.now(),
      finalized: options?.finalized ?? Boolean(snapshot || lastUnsavedTurnRef.current || transcribedTurnsRef.current.length || pendingCompletedTurnsRef.current.length),
      transcriptionModel: draft.transcriptionModel ?? active.transcription_model,
      capturedAt: draft.capturedAt,
      retryable: options?.retryable,
    });
  }, []);

  const disconnect = useCallback((options?: { updateState?: boolean; recoverable?: boolean; preservePendingHostMessages?: boolean;
    connectionFailure?: { error: unknown; stage: VoiceConnectionStage } }) => {
    const updateState = options?.updateState ?? true;
    discardBufferedNarrationRef.current();
    narrationTransportControllerRef.current?.abort();
    narrationTransportControllerRef.current = null;
    narrationTransportRef.current?.close();
    narrationTransportRef.current = null;
    narrationPlaybackControllerRef.current?.abort();
    narrationPlaybackControllerRef.current = null;
    if (narrationPlaybackStartupTimerRef.current !== null) window.clearTimeout(narrationPlaybackStartupTimerRef.current);
    narrationPlaybackStartupTimerRef.current = null;
    void narrationPlaybackRef.current?.close();
    narrationPlaybackRef.current = null;
    narrationPlaybackFailedRef.current = false;
    discardedNarrationRef.current = null;
    if (narrationClearTimerRef.current !== null) window.clearTimeout(narrationClearTimerRef.current);
    narrationClearTimerRef.current = null;
    preserveDraft();
    [...transcribedTurnsRef.current, ...pendingCompletedTurnsRef.current].forEach((draft) => preserveDraft(draft));
    intentionalDisconnectRef.current = !options?.recoverable;
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    connectionAttemptIdRef.current += 1;
    if (options?.recoverable) {
      rememberConnectionFailure(options.connectionFailure?.error ?? new Error('The voice connection was interrupted.'),
        options.connectionFailure?.stage ?? 'connection');
    } else {
      invalidateConnectionRecovery();
    }
    completedAudioCaptureRef.current?.close();
    completedAudioCaptureRef.current = null;
    transcriptionControllersRef.current.forEach((controller) => controller.abort());
    transcriptionControllersRef.current.clear();
    transcriptionQueueRef.current = Promise.resolve();
    pendingTranscriptionCountRef.current = 0;
    captureTurnRef.current = null;
    speechGroupRef.current = null;
    failedSpeechGroupsRef.current.clear();
    latestFailedSpeechGenerationRef.current = -1;
    transcribedTurnsRef.current = [];
    streamingTurnContextsRef.current.clear();
    activeNativeSpeechItemIdRef.current = '';
    transcribedItemIdsRef.current.clear();
    dataChannelRef.current?.close();
    dataChannelRef.current = null;

    peerConnectionRef.current?.close();
    peerConnectionRef.current = null;

    localStreamRef.current?.getTracks().forEach((track) => track.stop());
    localStreamRef.current = null;

    if (audioElementRef.current) {
      audioElementRef.current.srcObject = null;
    }
    browserAudioPlayableRef.current = false;
    if (activeSpokenMessageRef.current) {
      if (activeSpokenMessageRef.current.onInterrupted) activeSpokenMessageRef.current.onInterrupted();
      else pendingSpokenMessagesRef.current.unshift(activeSpokenMessageRef.current);
      activeSpokenMessageRef.current = null;
    }
    pendingSpokenMessagesRef.current = pendingSpokenMessagesRef.current.filter((message) => {
      if (!message.onInterrupted) return true;
      // A reconnect can queue its first host turn before connect starts. Only
      // that never-started, still-current message survives setup cleanup.
      if (options?.preservePendingHostMessages && !message.responseContext
        && ownsSpokenHostMessageRef.current(message)) return true;
      message.onInterrupted();
      return false;
    });

    currentUserDebugTimestampRef.current = null;
    currentAssistantDebugTimestampRef.current = null;
    userSpeechActiveRef.current = false;
    replayInProgressRef.current = false;
    assistantOutputAudioActiveRef.current = false;
    assistantOutputAudioSeenForResponseRef.current = false;
    pendingDisconnectAfterResponseRef.current = false;
    pendingAssistantOnlyResponseRef.current = null;
    pendingAssistantOnlySessionUpdateRef.current = null;
    pendingNarrationCaptureRef.current = null;
    narrationInFlightRef.current = null;
    interruptedPresentationRef.current = null;
    presentationResumeInFlightRef.current = null;
    activityRecoveryNoticeRef.current = null;
    const pendingClosing = pendingAutomaticClosingRef.current;
    if (pendingClosing?.closingSettleTimer !== undefined) window.clearTimeout(pendingClosing.closingSettleTimer);
    pendingAutomaticClosingRef.current = null;
    setAutomaticCompletionReady(false);
    if (currentAssistantResponseRef.current?.context.autoFinishAfterResponse) setTerminalActionReady(null);
    currentAssistantResponseRef.current?.settleCancelledHostMessage?.();
    currentAssistantResponseRef.current = null;
    activeGenerationRef.current = null;
    activeGenerationRequestRef.current = null;
    blockedProviderResponseRef.current = null;
    queuedAssistantResponsesRef.current = [];
    if (generationRecoveryTimeoutRef.current !== null) window.clearTimeout(generationRecoveryTimeoutRef.current);
    generationRecoveryTimeoutRef.current = null;
    pendingAssistantResponseCapturesRef.current = [];
    assistantResponsesByProviderIdRef.current.clear();
    activePersistingTurnKeyRef.current = '';
    isPersistingTurnRef.current = false;
    completionClosingResponseDoneRef.current = false;
    clearCompletionClosingDisconnectFallback();
    pendingCompletedTurnsRef.current = [];
    if (!retainSafetyPollingRef.current || activeSessionRef.current?.status === 'crisis_paused') stopAiSafetyPolling();

    if (updateState) {
      setTurnProcessing(false);
      setCompletionClosingPending(false);
      setLiveUserDebugEntry(null);
      setLiveAssistantDebugEntry(null);
      setAssistantPhaseValue('idle');
      setConnectionState('disconnected');
    }
  }, [clearCompletionClosingDisconnectFallback, invalidateConnectionRecovery, preserveDraft, rememberConnectionFailure, setAssistantPhaseValue, stopAiSafetyPolling]);

  failNarrationTransportRef.current = (failure) => {
    if (narrationPlaybackFailedRef.current
      && !narrationTransportControllerRef.current && !narrationPlaybackControllerRef.current) return;
    const classified = failure instanceof NarrationRealtimeTransportError
      ? failure : new NarrationRealtimeTransportError('event_processing');
    const recoverable = isRetryableNarrationTransportError(classified);
    const described = describeVoiceConnectionFailure(classified, 'connection');
    const recoveringHost = recoverable && activeSpokenMessageRef.current?.owner
      && ownsSpokenHostMessageRef.current(activeSpokenMessageRef.current)
      && !userSpeechActiveRef.current && !captureTurnRef.current && !pendingTranscriptionCountRef.current
      ? activeSpokenMessageRef.current : null;
    if (recoveringHost) activeSpokenMessageRef.current = null;
    disconnect({ recoverable: true, connectionFailure: { error: classified, stage: 'connection' } });
    if (recoveringHost) {
      recoveringHost.responseContext = undefined;
      pendingSpokenMessagesRef.current.unshift(recoveringHost);
    }
    // Transient output loss shares the existing retry budget. Malformed audio,
    // authorization and configuration failures remain closed for correction.
    narrationPlaybackFailedRef.current = true;
    setIsReconnecting(recoverable && automaticReconnectCountRef.current < 3);
    setError(described.message);
    setConnectionState('error');
  };

  const resetRecoveryWhenReady = useCallback((heardResponse = false) => {
    if (dataChannelRef.current?.readyState !== 'open'
      || (supportsBufferedNarration(activeSessionRef.current)
        && (!narrationTransportRef.current || !narrationPlaybackRef.current || narrationPlaybackFailedRef.current))) return;
    // A connection that opens and immediately loses output has not recovered.
    // Keep the shared budget until one owned response really finishes playing.
    if (heardResponse || automaticReconnectCountRef.current === 0) {
      automaticReconnectCountRef.current = 0;
      reconnectFailureAnnouncedRef.current = false;
    }
    invalidateConnectionRecovery();
  }, [invalidateConnectionRecovery]);

  useEffect(() => () => {
    stopAiSafetyPolling();
    disconnect({ updateState: false });
    pendingSpokenMessagesRef.current.splice(0).forEach((message) => message.reject(new Error('The guide connection ended.')));
  }, [disconnect, stopAiSafetyPolling]);

  useEffect(() => {
    if (!retainSafetyPolling && connectionState === 'disconnected') stopAiSafetyPolling();
  }, [connectionState, retainSafetyPolling, stopAiSafetyPolling]);

  const finishCompletionClosingResponse = useCallback(() => {
    clearCompletionClosingDisconnectFallback();
    completionClosingResponseDoneRef.current = false;
    assistantOutputAudioSeenForResponseRef.current = false;
    pendingDisconnectAfterResponseRef.current = false;
    setCompletionClosingPending(false);
    assistantOutputAudioActiveRef.current = false;
    replayInProgressRef.current = false;
    setAssistantPhaseValue('idle');
    disconnect();
  }, [clearCompletionClosingDisconnectFallback, disconnect, setAssistantPhaseValue]);

  const sendRealtimeSessionUpdate = useCallback((nextInstructionBlock: string) => {
    const channel = dataChannelRef.current;
    if (!channel || channel.readyState !== 'open') {
      return;
    }

    channel.send(
      JSON.stringify({
        type: 'session.update',
        session: {
          type: 'realtime',
          instructions: nextInstructionBlock,
        },
      }),
    );
  }, []);

  const refreshSession = useCallback(async (options?: { sendRealtimeUpdate?: boolean }) => {
    try {
      const nextSession = await fetchSession();
      if (options?.sendRealtimeUpdate && nextSession.instruction_block) {
        sendRealtimeSessionUpdate(nextSession.instruction_block);
      }
      return nextSession;
    } catch (sessionError) {
      setError(getErrorMessage(sessionError, 'Unable to refresh voice mode settings.'));
      return null;
    }
  }, [fetchSession, sendRealtimeSessionUpdate]);

  const hydrateFromBootstrap = useCallback((nextSession: VoiceFlowSessionBootstrap) => {
    const buffered = bufferedNarrationCaptureRef.current;
    if (buffered && !bufferedNarrationIsCurrent(buffered, nextSession)) discardBufferedNarration(buffered);
    if (['repeat', 'resume', 'resume_capture'].includes(nextSession.command_action ?? '')
      && preparedContentRef.current?.status === 'failed') preparedContentRef.current = null;
    activeSessionRef.current = nextSession;
    setSession((previous) => mergeSessionCostEstimate(nextSession, previous));
    hydrateDebugState(nextSession);
    setUiMode(getBootstrapUiMode(nextSession));
    const safetyMetadata = getBootstrapSafetyMetadata(nextSession);
    setResourcesPath(safetyMetadata.resourcesPath);
    setCtaLabel(safetyMetadata.ctaLabel);
    setCompletionClosingPending(isCompletionClosingDirective(nextSession.assistant_turn_directive));
  }, [bufferedNarrationIsCurrent, discardBufferedNarration, hydrateDebugState]);

  const clearCrisisPause = useCallback(async () => {
    if (!session) {
      return null;
    }
    try {
      disconnect();
      const response = await voiceFlow.clearCrisisPause(flowName, session.id);
      hydrateFromBootstrap(response.data);
      if (response.data.client_secret && response.data.status === 'active') {
        setConnectionState('idle');
      }
      return response.data;
    } catch (clearError) {
      setError(getErrorMessage(clearError, 'Unable to clear crisis pause.'));
      return null;
    }
  }, [disconnect, flowName, hydrateFromBootstrap, session]);

  const resetVoiceRun = useCallback(async () => {
    if (!session) {
      return null;
    }
    try {
      disconnect();
      const response = await voiceFlow.resetRun(flowName, session.id);
      hydrateFromBootstrap(response.data);
      setConnectionState('idle');
      return response.data;
    } catch (resetError) {
      setError(getErrorMessage(resetError, 'Unable to reset voice module run.'));
      return null;
    }
  }, [disconnect, flowName, hydrateFromBootstrap, session]);

  const setDebugRealtimeModelOverride = useCallback(async (realtimeModel: string | null) => {
    if (!session) {
      return null;
    }
    try {
      disconnect();
      const response = await voiceFlow.setRealtimeModelOverride(flowName, session.id, realtimeModel);
      hydrateFromBootstrap(response.data);
      if (response.data.client_secret && response.data.status === 'active') {
        setConnectionState('idle');
      }
      return response.data;
    } catch (modelError) {
      setError(getErrorMessage(modelError, 'Unable to update voice realtime model.'));
      return null;
    }
  }, [disconnect, flowName, hydrateFromBootstrap, session]);

  const setDebugTranscriptionModelOverride = useCallback(async (transcriptionModel: string | null) => {
    if (!session) {
      return null;
    }
    try {
      disconnect();
      const response = await voiceFlow.setTranscriptionModelOverride(flowName, session.id, transcriptionModel);
      hydrateFromBootstrap(response.data);
      if (response.data.client_secret && response.data.status === 'active') {
        setConnectionState('idle');
      }
      return response.data;
    } catch (modelError) {
      setError(getErrorMessage(modelError, 'Unable to update voice transcription model.'));
      return null;
    }
  }, [disconnect, flowName, hydrateFromBootstrap, session]);

  const makeAssistantResponseContext = useCallback((
    responseSession: AssistantResponseSession,
    responsePurpose?: string,
  ): AssistantResponseContext => ({
    sessionId: responseSession.id,
    questionId: responsePurpose === 'completion_closing' ? null : responseSession.current_question?.id ?? null,
    targetRevision: responsePurpose === 'completion_closing' ? undefined
      : responseSession.current_target?.target_revision
        ?? (responseSession.current_question?.id === activeSessionRef.current?.current_question?.id
          ? activeSessionRef.current?.current_target?.target_revision ?? undefined : undefined),
    runtimeGeneration: responseSession.runtime_generation ?? 0,
    connectionAttemptId: connectionAttemptIdRef.current,
    responsePurpose,
    spokenResponseMode: responseSession.spoken_response_mode === 'conversational' ? 'conversational' : 'exact',
    narrationToken: responsePurpose !== 'question_help' && canDeliverNarration(responseSession) ? responseSession.narration_token ?? responseSession.current_target?.narration_token : null,
    narrationText: responsePurpose !== 'question_help' && canDeliverNarration(responseSession) ? responseSession.narration_text ?? responseSession.current_target?.narration_text : null,
    narrationContentId: responseSession.current_target?.content_id,
    passiveNarration: responseSession.current_target?.is_passive === true,
    commandAction: responsePurpose === 'question_help' ? null : responseSession.command_action,
    autoFinishAfterResponse: responsePurpose === 'completion_closing'
      && responseSession.assistant_turn_directive?.auto_finish_after_response === true,
  }), []);

  const isAssistantResponseCurrentConnection = useCallback((context: AssistantResponseContext) => (
    context.connectionAttemptId === connectionAttemptIdRef.current
    && context.sessionId === activeSessionRef.current?.id
    && context.runtimeGeneration === (activeSessionRef.current?.runtime_generation ?? 0)
  ), []);

  const captureAssistantResponse = useCallback((context: AssistantResponseContext, replay = false, transport: AssistantTransport = 'rtc') => {
    const capture: AssistantResponseCapture = {
      id: assistantResponseSequenceRef.current++, context, replay, transport,
      transcript: '', timestamp: null, committedTranscript: '', cancelled: false, audioStarted: false,
    };
    currentAssistantResponseRef.current = capture;
    pendingAssistantResponseCapturesRef.current.push(capture);
    return capture;
  }, []);

  const hasPendingNarrationReceipt = useCallback(() => {
    const capture = pendingNarrationCaptureRef.current;
    if (capture) {
      const active = activeSessionRef.current;
      const canonicalToken = active?.narration_token ?? active?.current_target?.narration_token;
      if (!active || !isAssistantResponseCurrentConnection(capture.context)
        || capture.context.questionId !== active.current_question?.id
        || (canonicalToken && capture.context.narrationToken !== canonicalToken)
        || !canDeliverNarration(active) || capture.cancelled || capture.narrationFailed
        || (active.command_action === 'repeat' && capture.context.commandAction !== 'repeat')
        || active.ui_mode === 'crisis' || active.status === 'crisis_paused') {
        pendingNarrationCaptureRef.current = null;
      }
    }
    return Boolean((pendingNarrationCaptureRef.current && !capturePausedRef.current) || narrationInFlightRef.current);
  }, [isAssistantResponseCurrentConnection]);

  const completedDisplayReceiptForSpeech = useCallback(() => {
    const capture = pendingNarrationCaptureRef.current ?? narrationInFlightRef.current;
    return capture?.context.passiveNarration && !capture.cancelled && !capture.playbackMuted
      && capture.audioStarted && capture.audioStopped && capture.generationDone
      && isAssistantResponseCurrentConnection(capture.context) ? capture : null;
  }, [isAssistantResponseCurrentConnection]);

  const armResponseRecovery = useCallback(() => {
    if (!queuedAssistantResponsesRef.current.length || generationRecoveryTimeoutRef.current !== null) return;
    const capture = currentAssistantResponseRef.current;
    const attempt = connectionAttemptIdRef.current;
    generationRecoveryTimeoutRef.current = window.setTimeout(() => {
      generationRecoveryTimeoutRef.current = null;
      if (attempt !== connectionAttemptIdRef.current || currentAssistantResponseRef.current !== capture
        || (!activeGenerationRef.current && !assistantOutputAudioActiveRef.current)) return;
      setError('The guide did not finish its previous response. Reconnecting with your words preserved.');
      disconnect({ recoverable: true });
      setConnectionState('disconnected');
    }, 15000);
  }, [disconnect]);

  const flushAssistantResponses = useCallback(() => {
    flushClosingSafetyNoticeRef.current();
    const channel = dataChannelRef.current;
    if (!channel || channel.readyState !== 'open') return;
    // Install the single audible route before generating any remote audio.
    if (supportsBufferedNarration(activeSessionRef.current)
      && (!narrationPlaybackRef.current || narrationPlaybackFailedRef.current)) return;
    const queuedNarration = queuedAssistantResponsesRef.current[0];
    if (queuedNarration && !queuedNarration.replay && shouldBufferResponse(queuedNarration.context, activeSessionRef.current)
      && !narrationTransportRef.current) return;
    if (supportsBufferedNarration(activeSessionRef.current) && activeSessionRef.current?.ai_safety?.status === 'pending'
      && queuedAssistantResponsesRef.current[0]?.context.narrationToken) return;
    if (discardedNarrationRef.current && !discardedNarrationRef.current.cleared) return;
    if (pendingAutomaticClosingRef.current && finishAutomaticClosingRef.current(pendingAutomaticClosingRef.current)) return;
    if (pendingNarrationCaptureRef.current) narrationAckRef.current(pendingNarrationCaptureRef.current);
    const heardHost = currentAssistantResponseRef.current;
    if (heardHost?.bufferedLocallyDrained && activeSpokenMessageRef.current?.responseContext === heardHost.context) {
      settledPlaybackRef.current(heardHost);
    }
    if (hasPendingNarrationReceipt()) return;
    if (flushPresentationResumeRef.current()) return;
    if (isPersistingTurnRef.current || pendingTranscriptionCountRef.current > 0 || transcribedTurnsRef.current.length > 0) return;
    if (activeGenerationRef.current || blockedProviderResponseRef.current || assistantOutputAudioActiveRef.current || userSpeechActiveRef.current) {
      if (!blockedProviderResponseRef.current) armResponseRecovery();
      return;
    }
    let request = queuedAssistantResponsesRef.current.shift();
    while (request) {
      const hostMessage = activeSpokenMessageRef.current;
      const conversationalHost = !request.replay && request.context.responsePurpose === 'roleplay_host'
        && request.context.spokenResponseMode === 'conversational';
      const invalidHost = conversationalHost && (!hostMessage?.owner
        || hostMessage.responseContext !== request.context || !ownsSpokenHostMessageRef.current(hostMessage));
      if (isAssistantResponseCurrentConnection(request.context)
        && (request.context.responsePurpose === 'completion_closing'
          || (request.context.questionId === (activeSessionRef.current?.current_question?.id ?? null)
            && request.context.targetRevision === (activeSessionRef.current?.current_target?.target_revision ?? undefined)))
        && !invalidHost) break;
      if (conversationalHost && hostMessage?.responseContext === request.context) {
        activeSpokenMessageRef.current = null;
        hostMessage.onInterrupted?.();
      }
      request = queuedAssistantResponsesRef.current.shift();
    }
    if (!request) return;
    if (request.resetUser) clearLiveTranscript();
    else clearAssistantTranscript();
    replayInProgressRef.current = Boolean(request.replay);
    shouldCreateOpeningResponseRef.current = false;
    pendingDisconnectAfterResponseRef.current = Boolean(request.disconnectAfterResponse);
    completionClosingResponseDoneRef.current = false;
    assistantOutputAudioSeenForResponseRef.current = false;
    clearCompletionClosingDisconnectFallback();
    setCompletionClosingPending(request.context.responsePurpose === 'completion_closing');
    setAssistantPhaseValue('responding');
    if (!request.replay) sendRealtimeSessionUpdate(request.instructions);
    // Reserve generation before sending. Provider callbacks can arrive before
    // captions/playback, and cancellation is not a terminal acknowledgement.
    const signedNarration = !request.replay && shouldBufferResponse(request.context, activeSessionRef.current);
    activeGenerationRequestRef.current = request;
    activeGenerationRef.current = captureAssistantResponse(request.context, Boolean(request.replay), signedNarration ? 'signed_ws' : 'rtc');
    activeGenerationRef.current.instructions = request.instructions;
    if (signedNarration) {
      const capture = activeGenerationRef.current;
      capture.bufferedOwner = `narration:${capture.context.connectionAttemptId}:${capture.id}`;
      capture.bufferedHost = activeHostContextRef.current;
      bufferedNarrationCaptureRef.current = capture;
      // Generation may finish before local playback starts (for example while
      // muted). The unread queue still owns output until drain or retirement.
      assistantOutputAudioActiveRef.current = true;
      narrationPlaybackRef.current?.start(capture.bufferedOwner, { source: 'chunks' });
    }
    if (!request.replay && !['save_failure', 'transcription_failure', 'content_preparation_failure', 'roleplay_host', 'completion_closing']
      .includes(request.context.responsePurpose ?? '')) {
      lastRequestedQuestionRef.current = request.context.questionId;
      lastRequestedTargetRevisionRef.current = request.context.targetRevision;
    }
    const eventId = newLiveId();
    activeGenerationRef.current.clientEventId = eventId;
    const responseEvent = { type: 'response.create', event_id: eventId,
      response: { instructions: request.instructions, input: [] } };
    if (signedNarration) {
      if (!narrationTransportRef.current?.send(responseEvent)) failNarrationTransportRef.current(new NarrationRealtimeTransportError('send_failed'));
    } else channel.send(JSON.stringify(responseEvent));
  }, [armResponseRecovery, captureAssistantResponse, clearAssistantTranscript, clearCompletionClosingDisconnectFallback, hasPendingNarrationReceipt,
    clearLiveTranscript, isAssistantResponseCurrentConnection, sendRealtimeSessionUpdate, setAssistantPhaseValue]);
  flushAssistantResponsesRef.current = flushAssistantResponses;

  const sendRealtimeResponse = useCallback((
    nextInstructionBlock: string,
    options?: {
      resetUser?: boolean;
      responsePurpose?: string;
      disconnectAfterResponse?: boolean;
      responseSession?: AssistantResponseSession;
      responseContext?: AssistantResponseContext;
      replay?: boolean;
    },
  ): boolean => {
    const channel = dataChannelRef.current;
    if (!channel || channel.readyState !== 'open') {
      return false;
    }
    const responseSession = options?.responseSession ?? activeSessionRef.current;
    if (!responseSession) return false;
    if (queuedAssistantResponsesRef.current.length >= MAX_ASSISTANT_RESPONSE_CONTEXTS) return false;
    const responseContext = options?.responseContext
      ?? makeAssistantResponseContext(responseSession, options?.responsePurpose);
    if (!isAssistantResponseCurrentConnection(responseContext)) return false;

    // A new request owns the conversation. It cannot inherit a previously
    // delivered closing's authorization, even when the target is still null.
    if (pendingAutomaticClosingRef.current) pendingAutomaticClosingRef.current.automaticFinishSuperseded = true;
    pendingAutomaticClosingRef.current = null;
    setAutomaticCompletionReady(false);
    setTerminalActionReady(null);

    queuedAssistantResponsesRef.current.push({ instructions: nextInstructionBlock, context: responseContext,
      replay: options?.replay, resetUser: options?.resetUser, disconnectAfterResponse: options?.disconnectAfterResponse });
    flushAssistantResponsesRef.current();
    // True means accepted into the serialized queue, not provider playback.
    return true;
  }, [isAssistantResponseCurrentConnection, makeAssistantResponseContext]);

  const flushPendingAssistantOnlyResponse = useCallback((): boolean => {
    if (pendingNarrationCaptureRef.current) narrationAckRef.current(pendingNarrationCaptureRef.current);
    const pendingResponse = pendingAssistantOnlyResponseRef.current;
    if (
      !pendingResponse
      || assistantOutputAudioActiveRef.current
      || userSpeechActiveRef.current
      || captureTurnRef.current
      || isPersistingTurnRef.current
      || pendingTranscriptionCountRef.current > 0
      || transcribedTurnsRef.current.length > 0
      || pendingCompletedTurnsRef.current.length > 0
      || hasPendingNarrationReceipt()
    ) {
      return false;
    }

    pendingAssistantOnlyResponseRef.current = null;
    if (!isAssistantResponseCurrentConnection(pendingResponse.responseContext)) return false;
    const active = activeSessionRef.current;
    if (pendingResponse.responsePurpose === 'transcription_failure'
      && (!active || capturePausedRef.current || active.ui_mode === 'crisis' || active.status === 'crisis_paused'
        || (active.current_question?.id ?? null) !== pendingResponse.responseContext.questionId
        || (active.runtime_generation ?? 0) !== pendingResponse.responseContext.runtimeGeneration)) return false;
    const responseStarted = sendRealtimeResponse(pendingResponse.instructionBlock, {
      responsePurpose: pendingResponse.responsePurpose,
      disconnectAfterResponse: pendingResponse.disconnectAfterResponse,
      responseContext: pendingResponse.responseContext,
    });
    if (!responseStarted) {
      pendingAssistantOnlyResponseRef.current = pendingResponse;
      return false;
    }
    return true;
  }, [hasPendingNarrationReceipt, isAssistantResponseCurrentConnection, sendRealtimeResponse]);

  const reconnectForSpokenVoiceChange = useCallback(async (
    next: VoiceFlowSessionBootstrap, previousVoice?: string,
  ) => {
    if (next.command_action !== 'save_settings' || !next.voice || next.voice === previousVoice) return false;
    // Realtime cannot change its output voice reliably after audio has begun.
    // Keep the canonical host and drafts, then read the save receipt on the
    // newly configured connection instead of claiming a voice changed in place.
    pendingConnectionNoticeRef.current = { instructions: getSpokenReplyInstructions(next),
      purpose: 'settings', commandAction: next.command_action, spokenResponseMode: next.spoken_response_mode };
    disconnect({ updateState: false, recoverable: true });
    const attempt = connectionAttemptIdRef.current;
    setConnectionState('connecting');
    try {
      await fetchSession(attempt);
      if (attempt === connectionAttemptIdRef.current) setConnectionState('idle');
    } catch (failure) {
      if (attempt === connectionAttemptIdRef.current) {
        setError(getErrorMessage(failure, 'Your voice settings were saved. Reconnecting to use the selected voice.'));
        setConnectionState('error');
      }
    }
    return true;
  }, [disconnect, fetchSession]);

  const executeCommand = useCallback(async (command: string, arguments_?: Record<string, unknown>) => {
    if (command !== 'narration_complete' && command !== 'prepare_content') discardBufferedNarrationRef.current();
    const active = activeSessionRef.current;
    if (!active) return null;
    if (!['prepare_content', 'narration_complete'].includes(command)) contentPreparationIntentRef.current += 1;
    const preparationIntent = contentPreparationIntentRef.current;
    const preparationSpeechGeneration = userSpeechGenerationRef.current;
    invalidateConnectionRecovery();
    const attempt = connectionAttemptIdRef.current;
    setTerminalActionReady(null);
    setAutomaticCompletionReady(false);
    if (pendingAutomaticClosingRef.current) pendingAutomaticClosingRef.current.automaticFinishSuperseded = true;
    pendingAutomaticClosingRef.current = null;
    if (currentAssistantResponseRef.current?.context.autoFinishAfterResponse) currentAssistantResponseRef.current.automaticFinishSuperseded = true;
    try {
      const { data } = await voiceFlow.command(flowName, active.id, {
        command, question_id: active.current_question?.id ?? null,
        connection_generation: active.runtime_generation ?? 0,
        ...(active.pending_confirmation ? {
          confirmation_id: active.pending_confirmation.id,
          revision: active.pending_confirmation.revision,
        } : {}),
        ...(arguments_ ? { arguments: arguments_ } : {}),
        client_command_id: newLiveId(),
      });
      if (attempt !== connectionAttemptIdRef.current) return null;
      const newerCanonical = activeSessionRef.current;
      if (command === 'prepare_content' && (!newerCanonical || newerCanonical.id !== active.id
        || (newerCanonical.runtime_generation ?? 0) !== (active.runtime_generation ?? 0)
        || newerCanonical.current_question?.id !== active.current_question?.id
        || contentPreparationIntentRef.current !== preparationIntent
        || userSpeechGenerationRef.current !== preparationSpeechGeneration || userSpeechActiveRef.current
        || isPersistingTurnRef.current || pendingTranscriptionCountRef.current
        || pendingCompletedTurnsRef.current.length || transcribedTurnsRef.current.length
        || !canPrepareGeneratedContent(newerCanonical) || capturePausedRef.current)) return null;
      if (command === 'narration_complete' && (!newerCanonical || newerCanonical.id !== active.id
        || (newerCanonical.runtime_generation ?? 0) !== (active.runtime_generation ?? 0))) return null;
      if (command === 'narration_complete' && newerCanonical && newerCanonical !== active
        && newerCanonical.command_action !== active.command_action
        && ['pause', 'pause_capture', 'leave', 'finish', 'settings', 'save_settings', 'cancel_settings',
          'repeat', 'rephrase', 'explain', 'examples']
          .includes(newerCanonical.command_action ?? '')) {
        // A control finalized after the receipt already carries its canonical
        // progress. Its menu, settings, pause or closing owns the next speech.
        return newerCanonical;
      }
      const next = { ...active, ...data, spoken_response_mode: data.spoken_response_mode ?? 'exact',
        client_secret: data.client_secret ?? active.client_secret };
      if (command === 'narration_complete' && activeSessionRef.current?.id === active.id
        && (activeSessionRef.current.runtime_generation ?? 0) === (active.runtime_generation ?? 0)) {
        // The receipt snapshot may arrive after a later learner turn was saved.
        // Keep those server-confirmed words visible without changing ownership.
        const incoming = getBootstrapRecentTurns(next);
        const incomingIds = new Set(incoming.map((turn) => turn.id));
        const laterLearnerTurns = getBootstrapRecentTurns(activeSessionRef.current).filter((turn) =>
          turn.user_transcript.trim() && !incomingIds.has(turn.id));
        if (laterLearnerTurns.length) next.recent_turns = [...incoming, ...laterLearnerTurns];
      }
      hydrateFromBootstrap(next);
      if (command !== 'prepare_content') cancelRealtimeResponseRef.current();
      const terminalReply = hasTerminalSpokenReply(next);
      if (terminalReply) setCompletionClosingPending(true);
      if (await reconnectForSpokenVoiceChange(next, active.voice)) return next;
      if (next.command_action === 'pause' || next.command_action === 'pause_capture') {
        capturePausedRef.current = true;
        setCapturePaused(true);
      } else if (next.command_action === 'resume' || next.command_action === 'resume_capture') {
        capturePausedRef.current = false;
        setCapturePaused(false);
      }
      const narrationRecoveryQueued = command === 'narration_complete'
        && narrationInFlightRef.current?.narrationRecoveryQuestionId !== undefined
        && narrationInFlightRef.current.narrationRecoveryQuestionId === (next.current_question?.id ?? null);
      if (narrationRecoveryQueued || (command === 'narration_complete' && capturePausedRef.current)) return next;
      if (terminalReply || (next.assistant_turn_directive?.should_create_response && next.instruction_block)) {
        sendRealtimeResponse(getSpokenReplyInstructions(next), {
          responseSession: terminalReply ? { ...next, narration_token: null, narration_text: null, current_target: null } : next,
          responsePurpose: terminalReply ? 'completion_closing' : next.assistant_turn_directive?.response_purpose,
          disconnectAfterResponse: terminalReply ? false : next.assistant_turn_directive?.disconnect_after_response,
        });
      } else if (next.spoken_message) {
        sendRealtimeResponse(getSpokenReplyInstructions(next), {
          responseSession: { ...next, narration_token: null, narration_text: null, current_target: null },
        });
      }
      return next;
    } catch (commandError) {
      if (command !== 'prepare_content' && attempt === connectionAttemptIdRef.current) setError(getErrorMessage(commandError, 'Unable to carry out that voice command. Please try again.'));
      return null;
    }
  }, [flowName, hydrateFromBootstrap, invalidateConnectionRecovery, reconnectForSpokenVoiceChange, sendRealtimeResponse]);

  flushPresentationResumeRef.current = () => {
    const inFlight = presentationResumeInFlightRef.current;
    if (inFlight) {
      const current = activeSessionRef.current;
      if (!current || !isAssistantResponseCurrentConnection(inFlight.context)
        || (current.current_question?.id ?? null) !== inFlight.context.questionId
        || (current.command_action && current.command_action !== (inFlight.taskCommandAction ?? inFlight.context.commandAction))
        || (current.status !== 'active' && !(inFlight.context.autoFinishAfterResponse && allowsAutomaticCompletion(current)))
        || current.ui_mode === 'crisis' || capturePausedRef.current) {
        presentationResumeInFlightRef.current = null;
      } else return true;
    }
    const interrupted = interruptedPresentationRef.current;
    if (!interrupted?.noSpeechSeen) return false;
    if (interrupted.bufferedCapture) {
      const capture = interrupted.bufferedCapture;
      if (!bufferedNarrationIsCurrent(capture) || narrationPlaybackFailedRef.current) {
        discardBufferedNarrationRef.current(capture);
        interruptedPresentationRef.current = null;
        return false;
      }
      // Provider generation/stream ingestion can continue while paused. Only
      // pending learner work delays resuming the same unread PCM queue.
      if (userSpeechActiveRef.current || captureTurnRef.current || pendingTranscriptionCountRef.current
        || isPersistingTurnRef.current || transcribedTurnsRef.current.length || pendingCompletedTurnsRef.current.length
        || activeSessionRef.current?.ai_safety?.status === 'pending') return true;
      const hostMessage = activeSpokenMessageRef.current;
      if (hostMessage?.owner && hostMessage.responseContext === capture.context) {
        // Only settled empty captures may rebase advisory VAD ownership. Actual
        // words retire this buffer before reaching this branch.
        hostMessage.owner.speechGeneration = userSpeechGenerationRef.current;
      }
      interruptedPresentationRef.current = null;
      if (capture.bufferedLocallyDrained) {
        assistantOutputAudioActiveRef.current = false;
        narrationAckRef.current(capture);
        settledPlaybackRef.current(capture);
        return true;
      }
      if (!capture.bufferedOwner || !narrationPlaybackRef.current?.resume(capture.bufferedOwner)) {
        narrationPlaybackFailedRef.current = true;
        capture.narrationFailed = true;
        discardBufferedNarrationRef.current(capture);
        setError('The remaining narration could not resume. Your progress is preserved. Please reconnect to continue.');
        return true;
      }
      capture.bufferedPaused = false;
      assistantOutputAudioActiveRef.current = !capture.audioStopped;
      setAssistantPhaseValue('responding');
      return true;
    }
    const active = activeSessionRef.current;
    if (!active || !isAssistantResponseCurrentConnection(interrupted.context)
      || interrupted.context.questionId !== (active.current_question?.id ?? null)
      || (active.command_action && active.command_action !== (interrupted.taskCommandAction ?? interrupted.context.commandAction))
      || (active.status !== 'active' && !(interrupted.context.autoFinishAfterResponse && allowsAutomaticCompletion(active))) || active.ui_mode === 'crisis'
      || (capturePausedRef.current && !(interrupted.request && ['pause', 'pause_capture'].includes(interrupted.context.commandAction ?? '')))
      || (active.current_target?.question_type === 'roleplay' && learnerTranscriptHandlerRef.current)
      || (interrupted.failureGeneration !== undefined
        && interrupted.failureGeneration !== latestFailedSpeechGenerationRef.current)) {
      interruptedPresentationRef.current = null;
      return false;
    }
    if (userSpeechActiveRef.current || captureTurnRef.current || pendingTranscriptionCountRef.current
      || isPersistingTurnRef.current || transcribedTurnsRef.current.length || pendingCompletedTurnsRef.current.length
      || activeGenerationRef.current || assistantOutputAudioActiveRef.current || blockedProviderResponseRef.current) return false;
    if (pendingAssistantOnlyResponseRef.current || queuedAssistantResponsesRef.current.some((request) =>
      isAssistantResponseCurrentConnection(request.context)
      && request.context.questionId === (active.current_question?.id ?? null))) {
      interruptedPresentationRef.current = null;
      return false;
    }
    interruptedPresentationRef.current = null;
    if (interrupted.request) {
      const request = interrupted.request;
      queuedAssistantResponsesRef.current.push(interrupted.assistantResponseId && request.context.autoFinishAfterResponse
        ? { ...request, context: { ...request.context, replacesAssistantResponseId: interrupted.assistantResponseId } }
        : request);
      return false;
    }
    if (active.current_target?.content_pending || (!context && typeof flowRunId !== 'number')) return false;
    presentationResumeInFlightRef.current = interrupted;
    let resumeTimeout: number | undefined;
    void Promise.race([
      voiceFlow.getSession(flowName, context ?? (flowRunId as number), { includeClientSecret: false }),
      new Promise<never>((_, reject) => { resumeTimeout = window.setTimeout(() => reject(new Error('Canonical activity is unavailable.')), 3000); }),
    ]).then(({ data }) => {
      const current = activeSessionRef.current;
      if (presentationResumeInFlightRef.current !== interrupted || !current
        || !isAssistantResponseCurrentConnection(interrupted.context)
        || data.id !== current.id || (data.runtime_generation ?? 0) !== (current.runtime_generation ?? 0)
        || (data.current_question?.id ?? null) !== interrupted.context.questionId
        || (current.current_question?.id ?? null) !== interrupted.context.questionId
        || (current.command_action && current.command_action !== (interrupted.taskCommandAction ?? interrupted.context.commandAction))
        || (data.command_action && data.command_action !== (interrupted.taskCommandAction ?? interrupted.context.commandAction))
        || userSpeechGenerationRef.current !== interrupted.speechGeneration
        || capturePausedRef.current || (data.status !== 'active' && !(interrupted.context.autoFinishAfterResponse && allowsAutomaticCompletion(data)))
        || data.ui_mode === 'crisis'
        || data.current_target?.content_pending || !data.instruction_block
        || data.assistant_turn_directive?.should_create_response !== true
        || (data.current_target?.question_type === 'roleplay' && learnerTranscriptHandlerRef.current)) return;
      const canonical = { ...data, client_secret: data.client_secret ?? current.client_secret };
      hydrateFromBootstrap(canonical);
      sendRealtimeResponse(getSpokenReplyInstructions(canonical), {
        responseSession: canonical, responsePurpose: canonical.assistant_turn_directive?.response_purpose,
        ...(interrupted.assistantResponseId && interrupted.context.autoFinishAfterResponse ? {
          responseContext: { ...makeAssistantResponseContext(canonical, canonical.assistant_turn_directive?.response_purpose),
            replacesAssistantResponseId: interrupted.assistantResponseId },
        } : {}),
      });
    }).catch(() => { /* Empty audio does not justify an error caption or invented task. */ }).finally(() => {
      if (resumeTimeout !== undefined) window.clearTimeout(resumeTimeout);
      if (presentationResumeInFlightRef.current !== interrupted) return;
      presentationResumeInFlightRef.current = null;
      flushPendingAssistantOnlyResponse();
      flushAssistantResponsesRef.current();
    });
    return true;
  };

  narrationAckRef.current = (capture) => {
    const clearSettleTimer = () => {
      if (capture.narrationSettleTimer !== undefined) window.clearTimeout(capture.narrationSettleTimer);
      capture.narrationSettleTimer = undefined;
    };
    if (!capture.context.narrationToken || capture.narrationAcknowledged || capture.narrationFailed || !capture.generationDone
      || !capture.audioStarted || !capture.audioStopped || capture.playbackMuted || capture.cancelled
      || (capture.bufferedOwner && capture.generationSucceeded !== true)
      || capture.replay || !browserAudioPlayableRef.current
      || !isAssistantResponseCurrentConnection(capture.context)
      || capture.context.questionId !== activeSessionRef.current?.current_question?.id || capturePausedRef.current) {
      clearSettleTimer();
      return;
    }
    if (capture.bufferedOwner && activeSessionRef.current?.ai_safety?.status === 'pending') return;
    const active = activeSessionRef.current;
    const canonicalToken = active?.narration_token ?? active?.current_target?.narration_token;
    if (!active || (canonicalToken && capture.context.narrationToken !== canonicalToken)
      || !canDeliverNarration(active)
      || (active.command_action === 'repeat' && capture.context.commandAction !== 'repeat')
      || active.ui_mode === 'crisis' || active.status === 'crisis_paused') {
      clearSettleTimer();
      return;
    }
    pendingNarrationCaptureRef.current = capture;
    if (!hasPendingNarrationReceipt()) {
      clearSettleTimer();
      return;
    }
    if (capture.narrationReadyAt === undefined) capture.narrationReadyAt = Date.now() + 250;
    const remainingSettleDelay = capture.narrationReadyAt - Date.now();
    if (remainingSettleDelay > 0) {
      // Timer callbacks can run just before their wall-clock deadline. Keep one
      // owned timer until the passage has actually settled, rather than losing
      // its completion receipt until another learner/provider event arrives.
      if (capture.narrationSettleTimer === undefined) {
        capture.narrationSettleTimer = window.setTimeout(() => {
          capture.narrationSettleTimer = undefined;
          narrationAckRef.current(capture);
        }, remainingSettleDelay);
      }
      return;
    }
    clearSettleTimer();
    if (narrationInFlightRef.current
      || userSpeechActiveRef.current || captureTurnRef.current || pendingTranscriptionCountRef.current > 0
      || isPersistingTurnRef.current || transcribedTurnsRef.current.length > 0 || pendingCompletedTurnsRef.current.length > 0) return;
    pendingNarrationCaptureRef.current = null;
    narrationInFlightRef.current = capture;
    capture.narrationAcknowledged = true;
    void executeCommand('narration_complete', {
      narration_token: capture.context.narrationToken,
      assistant_response_id: capture.providerResponseId ?? `local-${capture.context.connectionAttemptId}-${capture.id}`,
      assistant_transcript: capture.transcript,
      played: true,
    }).then((result) => {
      if (!result) {
        capture.narrationAcknowledged = false;
        capture.narrationFailed = true;
      }
    }).finally(() => {
      if (narrationInFlightRef.current !== capture) return;
      narrationInFlightRef.current = null;
      flushPendingAssistantOnlyResponse();
      flushAssistantResponsesRef.current();
    });
  };

  const flushPendingAssistantOnlySessionUpdate = useCallback((): boolean => {
    const pendingInstructionBlock = pendingAssistantOnlySessionUpdateRef.current;
    if (
      !pendingInstructionBlock
      || assistantOutputAudioActiveRef.current
      || userSpeechActiveRef.current
    ) {
      return false;
    }

    pendingAssistantOnlySessionUpdateRef.current = null;
    sendRealtimeSessionUpdate(pendingInstructionBlock);
    return true;
  }, [sendRealtimeSessionUpdate]);

  const cancelRealtimeResponse = useCallback(() => {
    const buffered = bufferedNarrationCaptureRef.current;
    if (buffered) {
      discardBufferedNarrationRef.current(buffered);
      return;
    }
    const channel = dataChannelRef.current;
    if (!channel || channel.readyState !== 'open' || (!activeGenerationRef.current && !assistantOutputAudioActiveRef.current)) {
      return;
    }
    if (activeGenerationRef.current?.cancelled) return;
    // A late generation terminal or a learner's backchannel cannot undo audio
    // that already finished playing. Earlier interruptions still cancel it.
    if (currentAssistantResponseRef.current?.context.narrationToken
      && currentAssistantResponseRef.current.audioStarted && currentAssistantResponseRef.current.audioStopped
      && !currentAssistantResponseRef.current.cancelled) return;
    if (currentAssistantResponseRef.current) currentAssistantResponseRef.current.cancelled = true;
    if (currentAssistantResponseRef.current?.context.responsePurpose === 'roleplay_host' && activeSpokenMessageRef.current) {
      const interrupted = activeSpokenMessageRef.current;
      activeSpokenMessageRef.current = null;
      if (interrupted.owner) queuedAssistantResponsesRef.current = queuedAssistantResponsesRef.current
        .filter((request) => request.context !== interrupted.responseContext);
      const capture = currentAssistantResponseRef.current;
      const settle = interrupted.onInterrupted ?? interrupted.resolve;
      if (capture.generationDone) settle();
      else capture.settleCancelledHostMessage = settle;
    }

    channel.send(
      JSON.stringify({
        type: 'response.cancel',
      }),
    );
    if (assistantOutputAudioActiveRef.current) channel.send(JSON.stringify({ type: 'output_audio_buffer.clear' }));
  }, []);
  cancelRealtimeResponseRef.current = cancelRealtimeResponse;

  const interruptAssistant = useCallback(() => {
    closingSafetyIntentRevisionRef.current += 1;
    if (aiSafetyClosingScopeRef.current) stopAiSafetyPolling();
    cancelConnectionNotice();
    if (currentAssistantResponseRef.current?.context.autoFinishAfterResponse) currentAssistantResponseRef.current.automaticFinishSuperseded = true;
    pendingAutomaticClosingRef.current = null;
    setAutomaticCompletionReady(false);
    setTerminalActionReady(null);
    cancelRealtimeResponse();
    if (assistantPhaseRef.current === 'responding') {
      replayInProgressRef.current = false;
      setAssistantPhaseValue('listening');
    }
  }, [cancelConnectionNotice, cancelRealtimeResponse, setAssistantPhaseValue, stopAiSafetyPolling]);

  const scheduleAiSafetyPolling = useCallback((turnId?: number | null) => {
    if (!context && typeof flowRunId !== 'number') {
      return;
    }

    stopAiSafetyPolling();
    aiSafetyPollStartedAtRef.current = Date.now();
    const pollRevision = aiSafetyPollRevisionRef.current;
    const sessionId = activeSessionRef.current?.id;
    const runtimeGeneration = activeSessionRef.current?.runtime_generation ?? 0;
    const attempt = connectionAttemptIdRef.current;
    const host = activeHostContextRef.current;
    const controlRevision = connectionRecoveryRevisionRef.current;
    const intentRevision = closingSafetyIntentRevisionRef.current;
    let closingScope: ClosingSafetyWait | null = null;
    const ownsPoll = () => pollRevision === aiSafetyPollRevisionRef.current && host === activeHostContextRef.current
      && activeSessionRef.current?.id === sessionId
      && (activeSessionRef.current?.runtime_generation ?? 0) === runtimeGeneration
      && (!turnId || !activeSessionRef.current?.ai_safety?.turn_id || activeSessionRef.current.ai_safety.turn_id === turnId);
    aiSafetyPollOwnershipRef.current = ownsPoll;
    const closingKey = () => {
      const active = activeSessionRef.current;
      if (attempt !== connectionAttemptIdRef.current || host !== activeHostContextRef.current
        || controlRevision !== connectionRecoveryRevisionRef.current || intentRevision !== closingSafetyIntentRevisionRef.current
        || intentionalDisconnectRef.current || dataChannelRef.current?.readyState !== 'open'
        || !active || active.id !== sessionId || (active.runtime_generation ?? 0) !== runtimeGeneration
        || !allowsAutomaticCompletion(active) || active.ai_safety?.status !== 'pending'
        || (turnId && active.ai_safety.turn_id && active.ai_safety.turn_id !== turnId)) return null;
      return JSON.stringify([host, attempt, sessionId, runtimeGeneration, turnId ?? active.ai_safety.turn_id,
        connectionRecoveryContext(active)]);
    };
    const expireClosingWait = (wait: ClosingSafetyWait) => {
      if (pollRevision !== aiSafetyPollRevisionRef.current || closingSafetyWaitRef.current !== wait
        || wait.expired || !wait.isCurrent()) return;
      const remaining = wait.deadline - Date.now();
      if (remaining > 0) {
        if (wait.deadlineTimer !== undefined) window.clearTimeout(wait.deadlineTimer);
        wait.deadlineTimer = window.setTimeout(() => expireClosingWait(wait), remaining);
        return;
      }
      stopAiSafetyPolling();
      wait.expired = true;
      wait.noticePending = true;
      setClosingSafetyTimeoutKey(wait.key);
      flushClosingSafetyNoticeRef.current();
    };
    const ownedClosingWait = () => {
      // A closing episode may retire, but it cannot adopt a newer control or
      // fall back to token-producing ordinary polling between status reads.
      if (closingScope) return closingScope.isCurrent() ? closingScope : null;
      const key = closingKey();
      if (!key) return null;
      if (closingSafetyWaitRef.current?.key !== key || !closingSafetyWaitRef.current.isCurrent()) {
        if (closingSafetyWaitRef.current?.deadlineTimer !== undefined) window.clearTimeout(closingSafetyWaitRef.current.deadlineTimer);
        if (closingSafetyWaitRef.current?.notice === connectionNoticeRef.current) cancelConnectionNotice();
        closingSafetyWaitRef.current = {
          key, deadline: (aiSafetyPollStartedAtRef.current ?? Date.now()) + CLOSING_SAFETY_POLL_TIMEOUT_MS,
          expired: false, isCurrent: () => closingKey() === key,
        };
      }
      const wait = closingSafetyWaitRef.current;
      if (!wait) return null;
      closingScope = wait;
      aiSafetyClosingScopeRef.current = wait;
      if (!wait.expired && wait.deadlineTimer === undefined) {
        wait.deadlineTimer = window.setTimeout(() => expireClosingWait(wait), Math.max(0, wait.deadline - Date.now()));
      }
      return closingSafetyWaitRef.current;
    };
    ownedClosingWait();

    const poll = async () => {
      if (aiSafetyPollStartedAtRef.current === null || !ownsPoll() || (closingScope && !closingScope.isCurrent())) {
        if (pollRevision === aiSafetyPollRevisionRef.current) stopAiSafetyPolling();
        return;
      }
      const closingWait = ownedClosingWait();
      if (closingWait?.expired || (closingWait ? Date.now() >= closingWait.deadline
        : Date.now() - aiSafetyPollStartedAtRef.current > AI_SAFETY_POLL_TIMEOUT_MS)) {
        if (closingWait && !closingWait.expired) expireClosingWait(closingWait);
        else stopAiSafetyPolling();
        return;
      }

      const controller = closingWait ? new AbortController() : null;
      if (controller) aiSafetyPollControllerRef.current = controller;
      try {
        // The guide releases its microphone to the roleplay actor while its
        // final classifier may still be pending. Keep that existing bounded
        // check alive without issuing another provider token or connection.
        const response = closingWait || retainSafetyPollingRef.current
          ? await voiceFlow.getSession(flowName, context ?? (flowRunId as number), {
            includeClientSecret: false, ...(controller ? { signal: controller.signal } : {}),
          })
          : await voiceFlow.createSession(flowName, context ?? (flowRunId as number));
        if (!ownsPoll() || (closingWait && (closingScope !== closingWait || !closingWait.isCurrent()))) {
          if (pollRevision === aiSafetyPollRevisionRef.current) stopAiSafetyPolling();
          return;
        }
        const nextSession = response.data;
        if (nextSession.id !== sessionId || (nextSession.runtime_generation ?? 0) !== runtimeGeneration) {
          stopAiSafetyPolling();
          return;
        }
        const safetyStatus = nextSession.ai_safety?.status;
        const safetyTurnId = nextSession.ai_safety?.turn_id ?? null;
        const relevantTurn = !turnId || !safetyTurnId || safetyTurnId === turnId;

        if (nextSession.status === 'crisis_paused' || nextSession.ui_mode === 'crisis') {
          cancelRealtimeResponse();
          disconnect();
          hydrateFromBootstrap(nextSession);
          stopAiSafetyPolling();
          return;
        }

        if (
          relevantTurn
          && safetyStatus
          && safetyStatus !== 'pending'
        ) {
          if (closingSafetyWaitRef.current?.notice === connectionNoticeRef.current) cancelConnectionNotice();
          const safetyMetadata = getBootstrapSafetyMetadata(nextSession);
          setSession((previous) => {
            if (!previous) {
              return nextSession;
            }
            const mergedCostEstimate = mergeCostEstimates(
              nextSession.cost_estimate ?? nextSession.debug?.cost_estimate,
              previous.cost_estimate ?? previous.debug?.cost_estimate,
            );
            return {
              ...previous,
              cost_estimate: mergedCostEstimate,
              ai_safety: nextSession.ai_safety ?? previous.ai_safety ?? null,
              debug: nextSession.debug
                ? {
                    ...(previous.debug ?? { enabled: false, session_metadata: {} }),
                    ...nextSession.debug,
                    cost_estimate: mergedCostEstimate,
                  }
                : previous.debug,
            };
          });
          setUiMode(getBootstrapUiMode(nextSession));
          setResourcesPath(safetyMetadata.resourcesPath);
          setCtaLabel(safetyMetadata.ctaLabel);
          stopAiSafetyPolling();
          return;
        }

        const interval = closingWait && Date.now() - aiSafetyPollStartedAtRef.current >= AI_SAFETY_POLL_TIMEOUT_MS
          ? CLOSING_SAFETY_POLL_INTERVAL_MS : AI_SAFETY_POLL_INTERVAL_MS;
        aiSafetyPollTimeoutRef.current = window.setTimeout(poll, interval);
      } catch (pollError) {
        if (pollRevision !== aiSafetyPollRevisionRef.current) return;
        if (closingScope && !closingScope.isCurrent()) {
          stopAiSafetyPolling();
          return;
        }
        const interval = closingWait && Date.now() - (aiSafetyPollStartedAtRef.current ?? Date.now()) >= AI_SAFETY_POLL_TIMEOUT_MS
          ? CLOSING_SAFETY_POLL_INTERVAL_MS : AI_SAFETY_POLL_INTERVAL_MS;
        aiSafetyPollTimeoutRef.current = window.setTimeout(poll, interval);
      } finally {
        if (controller && aiSafetyPollControllerRef.current === controller) aiSafetyPollControllerRef.current = null;
      }
    };

    aiSafetyPollTimeoutRef.current = window.setTimeout(poll, AI_SAFETY_POLL_INTERVAL_MS);
  }, [cancelConnectionNotice, cancelRealtimeResponse, context, disconnect, flowName, flowRunId, hydrateFromBootstrap, stopAiSafetyPolling]);

  const replayAssistantMessage = useCallback((message: string) => {
    const cleaned = message.trim();
    const channel = dataChannelRef.current;
    if (
      !cleaned
      || !channel
      || channel.readyState !== 'open'
      || assistantPhaseRef.current === 'responding'
      || pendingAssistantResponseCapturesRef.current.length >= MAX_ASSISTANT_RESPONSE_CONTEXTS
    ) {
      return;
    }

    sendRealtimeResponse([
            'Repeat the following guide message aloud exactly as written.',
            'Do not paraphrase it. Do not add an introduction, apology, explanation, or follow-up question.',
            '',
            cleaned,
          ].join('\n'), { replay: true });
  }, [sendRealtimeResponse]);

  const persistAssistantOnlyTurn = useCallback((
    transcript: string,
    timestamp: string,
    capture: AssistantResponseCapture,
  ) => {
    const cleaned = transcript.trim();
    const context = capture.context;
    if (!cleaned || capture.replay || !isAssistantResponseCurrentConnection(context)) {
      return;
    }

    const turnKey = `${context.connectionAttemptId}:${capture.id}:${cleaned}`;
    if (turnKey === lastPersistedAssistantTurnKeyRef.current) {
      return;
    }

    lastPersistedAssistantTurnKeyRef.current = turnKey;
    if (currentAssistantResponseRef.current === capture) lastPersistedAssistantTranscriptRef.current = cleaned;
    const responsePurpose = context.responsePurpose;

    void voiceFlow.persistTurn(flowName, context.sessionId, {
      runtime_generation: context.runtimeGeneration,
      question_id: context.questionId,
      assistant_transcript: cleaned,
      metadata: {
        assistant_only: true,
        assistant_response_id: capture.providerResponseId ?? `local-${context.connectionAttemptId}-${capture.id}`,
        assistant_audio_started: capture.audioStarted,
        recorded_at: timestamp,
        source: 'realtime',
        ...(capture.transport === 'signed_ws' ? { assistant_output_transport: 'signed_ws' } : {}),
        spoken_response_mode: context.spokenResponseMode,
        ...(context.targetRevision ? { target_revision: context.targetRevision } : {}),
        ...(responsePurpose ? { response_purpose: responsePurpose } : {}),
        ...(context.replacesAssistantResponseId ? { replaces_assistant_response_id: context.replacesAssistantResponseId } : {}),
      },
    }).then((response) => {
      if (!isAssistantResponseCurrentConnection(context)) return;
      const payload = response.data;
      setTurnHistory((previousTurns) => {
        if (previousTurns.some((turn) => turn.id === payload.turn.id)) {
          return previousTurns;
        }
        return [...previousTurns, payload.turn];
      });
      // Conversational roleplay speech records dialogue only. Its delayed
      // assistant-only save cannot replace newer canonical safety/control state.
      if (context.responsePurpose === 'roleplay_host' && context.spokenResponseMode === 'conversational') return;
      // A retained passage already owns its provider response, captions, token
      // and playback receipt. Saving its caption must not echo another request
      // to speak the same canonical passage or replace newer learner controls.
      if (capture.bufferedOwner) return;
      // An older response may finish saving after a new target has started.
      // Keep its history without replacing the new response's session/instructions.
      if (
        currentAssistantResponseRef.current !== capture
        || (activeSessionRef.current?.current_question?.id ?? null) !== context.questionId
        || (activeSessionRef.current?.current_target?.target_revision ?? undefined) !== context.targetRevision
      ) return;
      const previousInstruction = activeSessionRef.current?.instruction_block;
      activeSessionRef.current = mergeSessionFromTurnResult(activeSessionRef.current, payload);
      setSession((previousSession) => mergeSessionFromTurnResult(previousSession, payload));
      if (
        payload.assistant_turn_directive?.should_create_response
        && payload.instruction_block
      ) {
        pendingAssistantOnlyResponseRef.current = {
          instructionBlock: getSpokenReplyInstructions(payload),
          responsePurpose: payload.assistant_turn_directive.response_purpose,
          disconnectAfterResponse: payload.assistant_turn_directive.disconnect_after_response,
          responseContext: makeAssistantResponseContext({
            id: context.sessionId,
            runtime_generation: context.runtimeGeneration,
            current_question: payload.current_question,
            current_target: payload.current_target,
            narration_token: payload.narration_token,
            narration_text: payload.narration_text,
            command_action: payload.command_action,
            spoken_response_mode: payload.spoken_response_mode,
            assistant_turn_directive: payload.assistant_turn_directive,
          }, payload.assistant_turn_directive.response_purpose),
        };
        flushPendingAssistantOnlyResponse();
      } else if (payload.instruction_block && payload.instruction_block !== previousInstruction) {
        pendingAssistantOnlySessionUpdateRef.current = payload.instruction_block;
        flushPendingAssistantOnlySessionUpdate();
      }
    }).catch((persistError) => {
      if (!isAssistantResponseCurrentConnection(context)) return;
      if (lastPersistedAssistantTurnKeyRef.current === turnKey && lastPersistedAssistantTranscriptRef.current === cleaned) {
        lastPersistedAssistantTranscriptRef.current = '';
      }
      if (lastPersistedAssistantTurnKeyRef.current === turnKey) {
        lastPersistedAssistantTurnKeyRef.current = '';
      }
      console.warn('Unable to persist assistant voice turn', persistError);
    });
  }, [
    flowName,
    flushPendingAssistantOnlyResponse,
    flushPendingAssistantOnlySessionUpdate,
    isAssistantResponseCurrentConnection,
    makeAssistantResponseContext,
  ]);

  const commitAssistantDebugTranscript = useCallback((
    transcript: string,
    timestamp?: string | null,
    capture = currentAssistantResponseRef.current,
  ) => {
    if (!capture || capture.replay || !isAssistantResponseCurrentConnection(capture.context)) return;
    const cleaned = transcript.trim();
    const isCurrentResponse = capture === currentAssistantResponseRef.current;
    if (!cleaned) {
      if (isCurrentResponse) setLiveAssistantDebugEntry(null);
      return;
    }
    if (capture.committedTranscript === cleaned) {
      if (isCurrentResponse) {
        currentAssistantDebugTimestampRef.current = null;
        setLiveAssistantDebugEntry(null);
      }
      return;
    }

    const resolvedTimestamp = timestamp || capture.timestamp || new Date().toISOString();
    capture.transcript = cleaned;
    capture.committedTranscript = cleaned;
    if (isCurrentResponse) {
      lastAssistantCommittedTranscriptRef.current = cleaned;
      currentAssistantDebugTimestampRef.current = null;
      setLiveAssistantDebugEntry(null);
    }
    setAssistantDebugHistory((previous) => [
      ...previous,
      {
        id: `assistant-debug-${assistantDebugSequenceRef.current++}`,
        text: cleaned,
        timestamp: resolvedTimestamp,
        source: 'realtime',
        ...assistantCaptionIdentity(capture),
      },
    ]);
    persistAssistantOnlyTurn(cleaned, resolvedTimestamp, capture);
  }, [isAssistantResponseCurrentConnection, persistAssistantOnlyTurn]);

  const processCompletedTurn = useCallback(async (turnSnapshot: CompletedTurnSnapshot) => {
    if (!session?.id) {
      return;
    }
    if (turnSnapshot.connectionAttemptId !== undefined && turnSnapshot.connectionAttemptId !== connectionAttemptIdRef.current) return;
    const turnConnectionAttempt = connectionAttemptIdRef.current;

    const turnKey = getCompletedTurnKey(turnSnapshot);

    if (isPersistingTurnRef.current) {
      const isDuplicateInFlightTurn = activePersistingTurnKeyRef.current === turnKey;
      const isDuplicateQueuedTurn = pendingCompletedTurnsRef.current.some(
        (pendingTurn) => getCompletedTurnKey(pendingTurn) === turnKey,
      );
      if (isDuplicateInFlightTurn || isDuplicateQueuedTurn) {
        return;
      }
      pendingCompletedTurnsRef.current = [...pendingCompletedTurnsRef.current, turnSnapshot];
      return;
    }

    if (!turnSnapshot.userTranscript.trim()) {
      return;
    }

    if (turnKey === lastPersistedTurnKeyRef.current) {
      return;
    }

    activePersistingTurnKeyRef.current = turnKey;
    isPersistingTurnRef.current = true;
    setTurnProcessing(true);
    setTerminalActionReady(null);
    lastUnsavedTurnRef.current = turnSnapshot;
    preserveDraft(turnSnapshot);
    try {
      const isRoleplayCapture = session.content_schema.some((item) => item.question_id === turnSnapshot.questionId
        && item.question_type === 'roleplay');
      const metadata = {
        ...(turnSnapshot.targetRevision ? { target_revision: turnSnapshot.targetRevision } : {}),
        ...(turnSnapshot.inputAudioItemId ? { input_audio_item_id: turnSnapshot.inputAudioItemId } : {}),
        ...(turnSnapshot.assistantResponseId ? { assistant_response_id: turnSnapshot.assistantResponseId } : {}),
        ...(turnSnapshot.capturedAt ? { input_audio_started_at: turnSnapshot.capturedAt } : {}),
        ...(turnSnapshot.confirmation ? { command_context: {
          confirmation_id: turnSnapshot.confirmation.id,
          revision: turnSnapshot.confirmation.revision,
          runtime_generation: turnSnapshot.confirmation.runtime_generation,
        } } : {}),
        ...(isRoleplayCapture && learnerTranscriptHandlerRef.current ? {
          roleplay_host: true,
          ...learnerTranscriptMetadataRef.current?.(turnSnapshot.userTranscript),
        } : {}),
      };
      const response = await voiceFlow.persistTurn(flowName, session.id, {
        runtime_generation: session.runtime_generation ?? 0,
        question_id: turnSnapshot.questionId,
        ...(turnSnapshot.speechGroupId ? { speech_group_id: turnSnapshot.speechGroupId } : {}),
        user_transcript: turnSnapshot.userTranscript,
        assistant_transcript:
          (turnSnapshot.assistantResponseId
            ? Boolean(assistantResponsesByProviderIdRef.current.get(turnSnapshot.assistantResponseId)?.committedTranscript)
            : normalizeTranscriptSpacing(turnSnapshot.assistantTranscript) === normalizeTranscriptSpacing(lastPersistedAssistantTranscriptRef.current))
            ? ''
            : turnSnapshot.assistantTranscript,
        metadata,
      });
      const payload = response.data;
      if (turnConnectionAttempt !== connectionAttemptIdRef.current) return;
      // A successfully processed answer or command owns the next spoken action.
      // An ASR failure from concurrent audio must not replace that action.
      pendingAssistantOnlyResponseRef.current = null;
      queuedAssistantResponsesRef.current = queuedAssistantResponsesRef.current.filter((request) =>
        !['save_failure', 'transcription_failure'].includes(request.context.responsePurpose ?? ''));
      setError(null);
      if (currentAssistantResponseRef.current?.context.responsePurpose === 'transcription_failure'
        && currentAssistantResponseRef.current.context.questionId === turnSnapshot.questionId) {
        cancelRealtimeResponse();
      }
      const terminalReply = hasTerminalSpokenReply(payload);
      // A terminal command disables new connections on the server. Its receipt
      // still needs to play on this live connection before shutdown/navigation.
      // Set the hold before any await can render the completed session.
      if (terminalReply) setCompletionClosingPending(true);
      lastPersistedTurnKeyRef.current = turnKey;
      clearVoiceDraft(session.id, turnSnapshot.inputAudioItemId);
      const acceptedUpdates = payload.latest_extraction?.accepted_updates;
      if (Array.isArray(acceptedUpdates) && acceptedUpdates.some((update) => update?.status === 'complete'
        && (update.question_id === turnSnapshot.questionId || update.content_id === turnSnapshot.targetContentId))) {
        // A successful repeated answer supersedes only incomplete captures for
        // this question. Other captured answers and finalized retries survive.
        // A later failed capture can still contain words the learner has not
        // replaced. Retain them until a subsequent complete answer supersedes it.
        if (userSpeechGenerationRef.current <= turnSnapshot.speechGeneration) {
          clearIncompleteVoiceDrafts(session.id, turnSnapshot.questionId);
        }
      }
      if (lastUnsavedTurnRef.current === turnSnapshot) lastUnsavedTurnRef.current = null;
      recognizedUserTurnsRef.current.delete(turnSnapshot.inputAudioItemId);
      setTurnHistory((previousTurns) => [...previousTurns.filter((turn) => turn.id !== payload.turn.id
        && !(turn.metadata?.capture_local_only && turn.metadata?.input_audio_item_id === turnSnapshot.inputAudioItemId)),
        { ...payload.turn, metadata: { ...payload.turn.metadata, input_audio_item_id: turnSnapshot.inputAudioItemId,
          ...(turnSnapshot.capturedAt ? { input_audio_started_at: turnSnapshot.capturedAt } : {}) } }]);
      currentUserDebugTimestampRef.current = null;
      setLiveUserDebugEntry(null);
      setUiMode(payload.ui_mode);
      setResourcesPath(payload.resources_path ?? null);
      setCtaLabel(payload.cta_label ?? null);
      contentPreparationIntentRef.current += 1;
      if (['repeat', 'resume', 'resume_capture'].includes(payload.command_action ?? '')
        && preparedContentRef.current?.status === 'failed') preparedContentRef.current = null;
      setSession((previousSession) => mergeSessionFromTurnResult(previousSession, payload));
      const nextSession = mergeSessionFromTurnResult(session, payload);
      if (nextSession) activeSessionRef.current = nextSession;
      if (nextSession && await reconnectForSpokenVoiceChange(nextSession, session.voice)) return;
      if (payload.command_action === 'pause' || payload.command_action === 'pause_capture') {
        capturePausedRef.current = true;
        setCapturePaused(true);
      } else if (payload.command_action === 'resume' || payload.command_action === 'resume_capture') {
        capturePausedRef.current = false;
        setCapturePaused(false);
      }
      if (capturePausedRef.current && payload.command_action === 'capture_held'
        && payload.assistant_turn_directive?.should_create_response === false
        && (activeSessionRef.current?.current_question?.id ?? null) === turnSnapshot.questionId
        && (activeSessionRef.current?.runtime_generation ?? 0) === (turnSnapshot.runtimeGeneration ?? session.runtime_generation ?? 0)
        && speechGroupRef.current?.id === turnSnapshot.speechGroupId) {
        // Explicit pause retains recognized words but deliberately produces no
        // guide playback. Its finalized capture therefore supplies the boundary
        // that playback normally supplies; queued captures keep their own IDs.
        speechGroupRef.current = null;
      }
      if (isCompletionClosingDirective(payload.assistant_turn_directive)) {
        setCompletionClosingPending(true);
      }
      if (payload.ai_safety?.status === 'pending') {
        scheduleAiSafetyPolling(payload.ai_safety.turn_id ?? payload.turn.id);
      }

      const roleplayOwnedAction = !payload.command_action
        || ['capture_held', 'begin_roleplay', 'end_roleplay', 'retry_roleplay', 'repeat'].includes(payload.command_action);
      if (isRoleplayCapture && roleplayOwnedAction && learnerTranscriptHandlerRef.current && payload.ui_mode !== 'crisis') {
        // Release the settled guide turn before its callback drains the guide.
        isPersistingTurnRef.current = false;
        activePersistingTurnKeyRef.current = '';
        setTurnProcessing(false);
        const handled = await learnerTranscriptHandlerRef.current(turnSnapshot.userTranscript, {
          questionId: turnSnapshot.questionId, sessionId: session.id,
          runtimeGeneration: session.runtime_generation ?? 0,
        });
        if (handled) return;
      }

      const hasNewerUserSpeech = userSpeechGenerationRef.current !== turnSnapshot.speechGeneration
        && userSpeechGenerationRef.current !== latestFailedSpeechGenerationRef.current;
      const hasQueuedTurn = pendingCompletedTurnsRef.current.length > 0
        || transcribedTurnsRef.current.length > 0 || pendingTranscriptionCountRef.current > 0;
      if (!terminalReply && (!payload.command_action || payload.command_action === 'capture_held') && hasPendingNarrationReceipt()) {
        // This utterance belongs to the just-heard display. Keep it there, then
        // commit its settled narration before speaking the next task.
        setAssistantPhaseValue('listening');
      } else if (
        (terminalReply || (payload.assistant_turn_directive?.should_create_response && payload.instruction_block))
        && !hasNewerUserSpeech
        && !hasQueuedTurn
        && !userSpeechActiveRef.current
      ) {
        currentAssistantTranscriptRef.current = '';
        setAssistantTranscript('');
        const responseStarted = sendRealtimeResponse(getSpokenReplyInstructions(payload), {
          responsePurpose: terminalReply ? 'completion_closing' : payload.assistant_turn_directive?.response_purpose,
          disconnectAfterResponse: terminalReply ? false : payload.assistant_turn_directive?.disconnect_after_response,
          responseSession: {
            id: session.id,
            runtime_generation: session.runtime_generation,
            current_question: payload.current_question,
            current_target: terminalReply ? null : payload.current_target,
            narration_token: terminalReply ? null : payload.narration_token,
            narration_text: terminalReply ? null : payload.narration_text,
            command_action: payload.command_action,
            spoken_response_mode: payload.spoken_response_mode,
            assistant_turn_directive: payload.assistant_turn_directive,
          },
        });
        if (!responseStarted && isCompletionClosingDirective(payload.assistant_turn_directive)) {
          setCompletionClosingPending(true);
          setAssistantPhaseValue('idle');
          disconnect({ updateState: false });
          const recoveryAttempt = connectionAttemptIdRef.current;
          setTurnProcessing(false);
          try {
            const retrySession = await fetchSession(recoveryAttempt);
            if (recoveryAttempt !== connectionAttemptIdRef.current) return;
            if (retrySession?.client_secret && isCompletionClosingDirective(retrySession.assistant_turn_directive)) {
              setConnectionState('idle');
            } else {
              setCompletionClosingPending(false);
              setConnectionState('disconnected');
            }
          } catch (recoveryError) {
            if (recoveryAttempt === connectionAttemptIdRef.current) {
              setError(getErrorMessage(recoveryError, 'Unable to resume the closing response. Please reconnect.'));
              setCompletionClosingPending(false);
              setConnectionState('error');
            }
          }
        }
      } else {
        if (payload.assistant_turn_directive?.should_create_response && payload.instruction_block) {
          // Keep the authoritative action while another capture finishes. If
          // that capture fails, its retry notice cannot replace this action.
          pendingAssistantOnlyResponseRef.current = {
            instructionBlock: getSpokenReplyInstructions(payload),
            responsePurpose: payload.assistant_turn_directive.response_purpose,
            disconnectAfterResponse: payload.assistant_turn_directive.disconnect_after_response,
            responseContext: makeAssistantResponseContext({
              id: session.id, runtime_generation: session.runtime_generation,
              current_question: payload.current_question, current_target: payload.current_target,
              narration_token: payload.narration_token, narration_text: payload.narration_text,
              command_action: payload.command_action, spoken_response_mode: payload.spoken_response_mode,
              assistant_turn_directive: payload.assistant_turn_directive,
            }, payload.assistant_turn_directive.response_purpose),
          };
        }
        setAssistantPhaseValue(userSpeechActiveRef.current || hasQueuedTurn ? 'listening' : 'idle');
        if (payload.session_status !== 'active' && connectionState === 'connected'
          && !terminalReply
          && (payload.session_status !== 'completed' || (!hasQueuedTurn && !hasNewerUserSpeech && !userSpeechActiveRef.current))) {
          disconnect();
        }
      }
    } catch (turnError) {
      if (turnConnectionAttempt === connectionAttemptIdRef.current) {
        const status = (turnError as { response?: { status?: number } })?.response?.status;
        const failureData = (turnError as { response?: { data?: { retryable?: boolean; error_code?: string; captured_turn?: VoiceFlowTurn } } })?.response?.data;
        const retryable = failureData?.retryable ?? !(status && [400, 401, 403].includes(status));
        const completedNarrationOverlap = status === 409 && failureData?.error_code === 'voice_narration_capture_stale'
          && typeof failureData.captured_turn?.metadata?.speech_group_narration_ack_turn_id === 'number'
          && failureData.captured_turn.question_id === turnSnapshot.questionId;
        preserveDraft(turnSnapshot, { retryable });
        if (failureData?.captured_turn) {
          const captured = failureData.captured_turn;
          recognizedUserTurnsRef.current.delete(turnSnapshot.inputAudioItemId);
          setTurnHistory((previous) => [...previous.filter((turn) => turn.id !== captured.id
            && !(turn.metadata?.capture_local_only && turn.metadata?.input_audio_item_id === turnSnapshot.inputAudioItemId)), captured]);
        }
        if (!retryable) {
          if (lastUnsavedTurnRef.current === turnSnapshot) lastUnsavedTurnRef.current = null;
        }
        setLiveUserDebugEntry((entry) => entry && turnSnapshot.inputAudioItemId.split(',').includes(entry.id) ? null : entry);
        let canonical: VoiceFlowSessionBootstrap | null = null;
        let reconciliationTimeout: number | undefined;
        if ((retryable || completedNarrationOverlap) && (context || typeof flowRunId === 'number')) {
          try {
            const response = await Promise.race([
              voiceFlow.getSession(flowName, context ?? (flowRunId as number), { includeClientSecret: false }),
              new Promise<never>((_, reject) => {
                reconciliationTimeout = window.setTimeout(() => reject(new Error('Canonical voice state is unavailable.')), 3000);
              }),
            ]);
            if (turnConnectionAttempt !== connectionAttemptIdRef.current) return;
            if (response.data.id === session.id && (response.data.runtime_generation ?? 0) === (session.runtime_generation ?? 0)) {
              canonical = { ...response.data, client_secret: response.data.client_secret ?? session.client_secret };
              hydrateFromBootstrap(canonical);
              const processed = processedCaptureIds(getBootstrapRecentTurns(canonical));
              if (turnSnapshot.inputAudioItemId.split(',').every((id) => processed.has(id))) {
                lastPersistedTurnKeyRef.current = turnKey;
                if (lastUnsavedTurnRef.current === turnSnapshot) lastUnsavedTurnRef.current = null;
                clearVoiceDraft(session.id, turnSnapshot.inputAudioItemId);
                setError(null);
                const confirmedTurn = getBootstrapRecentTurns(canonical).find((turn) => turn.metadata?.input_audio_item_id === turnSnapshot.inputAudioItemId);
                const confirmedAction = (confirmedTurn?.metadata?.latest_extraction as { command_action?: string } | undefined)?.command_action
                  ?? canonical.command_action;
                if (capturePausedRef.current && confirmedAction === 'capture_held'
                  && canonical.assistant_turn_directive?.should_create_response === false) {
                  if (speechGroupRef.current?.id === turnSnapshot.speechGroupId) speechGroupRef.current = null;
                  return;
                }
                if (canonical.instruction_block && (canonical.current_target || isCompletionClosingDirective(canonical.assistant_turn_directive))
                  && !canonical.current_target?.content_pending
                  && !(canonical.current_target?.question_type === 'roleplay' && learnerTranscriptHandlerRef.current)) {
                  sendRealtimeResponse(getSpokenReplyInstructions(canonical), { responseSession: canonical,
                    responsePurpose: canonical.assistant_turn_directive?.response_purpose,
                    disconnectAfterResponse: canonical.assistant_turn_directive?.disconnect_after_response });
                }
                return;
              }
            }
          } catch { /* Retain captured words; a failed read does not prove the save failed. */ }
          finally { if (reconciliationTimeout !== undefined) window.clearTimeout(reconciliationTimeout); }
        }
        if (completedNarrationOverlap) {
          setError(null);
          setInputError('The guide had already moved to the next question. Your words are still available.');
          if (narrationInFlightRef.current?.context.questionId === turnSnapshot.questionId) {
            narrationInFlightRef.current.narrationRecoveryQuestionId = (canonical ?? activeSessionRef.current)?.current_question?.id ?? null;
          }
          queuedAssistantResponsesRef.current = queuedAssistantResponsesRef.current.filter((request) =>
            request.context.questionId !== (canonical ?? activeSessionRef.current)?.current_question?.id);
        } else setError(getErrorMessage(turnError, 'Unable to analyze the latest turn.'));
        const recoverySession = canonical ?? activeSessionRef.current ?? session;
        const instructions = !retryable || canonical ? voiceSaveRecoveryInstructions(recoverySession)
          : 'The learner\'s words were heard and remain available, but the latest server state is unavailable. Briefly explain the connection issue in ordinary language and invite reconnection. Do not repeat a possibly outdated question, claim the action failed or succeeded, or claim an answer was saved.';
        const recoveryNoticeSession = { ...recoverySession, spoken_response_mode: 'conversational' as const, command_action: null,
          narration_token: null, narration_text: null, current_target: null };
        const recoveryNoticeContext = makeAssistantResponseContext(recoveryNoticeSession, 'save_failure');
        sendRealtimeResponse(instructions, {
          responseSession: recoveryNoticeSession, responseContext: recoveryNoticeContext,
          responsePurpose: 'save_failure',
        });
        if (voiceRecoveryIsActivity(recoverySession)) {
          activityRecoveryNoticeRef.current = {
            noticeContext: recoveryNoticeContext,
            activityContext: makeAssistantResponseContext(recoverySession), speechGeneration: userSpeechGenerationRef.current,
          };
        }
      }
    } finally {
      if (turnConnectionAttempt === connectionAttemptIdRef.current) {
        isPersistingTurnRef.current = false;
        if (activePersistingTurnKeyRef.current === turnKey) activePersistingTurnKeyRef.current = '';
        const [nextTurn, ...remainingTurns] = pendingCompletedTurnsRef.current;
        pendingCompletedTurnsRef.current = remainingTurns;
        if (nextTurn) void processCompletedTurnRef.current?.(nextTurn);
        else {
          flushTranscribedTurnsRef.current();
          // Newly captured work has its own queue. Failed stored captures are
          // retried only by an explicit flush or one bounded reconnect recovery;
          // otherwise two rejected captures can alternate forever here.
          if (!isPersistingTurnRef.current && !pendingTranscriptionCountRef.current && !transcribedTurnsRef.current.length) setTurnProcessing(false);
          flushPendingAssistantOnlyResponse();
          flushAssistantResponsesRef.current();
        }
      }
    }
  }, [
    connectionState,
    cancelRealtimeResponse,
    disconnect,
    fetchSession,
    flowName,
    flowRunId,
    context,
    hydrateFromBootstrap,
    hasPendingNarrationReceipt,
    preserveDraft,
    flushPendingAssistantOnlyResponse,
    makeAssistantResponseContext,
    sendRealtimeResponse,
    session,
    setAssistantPhaseValue,
    scheduleAiSafetyPolling,
    reconnectForSpokenVoiceChange,
  ]);

  useEffect(() => {
    processCompletedTurnRef.current = processCompletedTurn;
  }, [processCompletedTurn]);

  const recordUsageEvent = useCallback((
    data: {
      event_type: 'response.done' | 'conversation.item.input_audio_transcription.completed';
      event_id: string;
      model: string;
      usage?: Record<string, unknown> | null;
      duration_seconds?: number | null;
    },
  ) => {
    if (!session?.id || !data.event_id || recordedUsageEventIdsRef.current.has(data.event_id)) {
      return;
    }
    recordedUsageEventIdsRef.current.add(data.event_id);
    void voiceFlow.recordUsageEvent(flowName, session.id, { ...data, runtime_generation: session.runtime_generation ?? 0 })
      .then((response) => {
        const costEstimate = response.data.cost_estimate;
        if (!costEstimate) {
          return;
        }
        setSession((previous) => previous
          ? (() => {
            const mergedCostEstimate = mergeCostEstimates(
              costEstimate,
              previous.cost_estimate ?? previous.debug?.cost_estimate,
            );
            return {
              ...previous,
              cost_estimate: mergedCostEstimate,
              debug: previous.debug
                ? {
                    ...previous.debug,
                    cost_estimate: mergedCostEstimate,
                  }
                : previous.debug,
            };
          })()
          : previous);
      })
      .catch((usageError) => {
        recordedUsageEventIdsRef.current.delete(data.event_id);
        console.warn('Unable to record voice usage event', usageError);
      });
  }, [flowName, session?.id, session?.runtime_generation]);

  const failAudioTranscription = useCallback((failure: unknown, failedCapture?: CompletedTurnSnapshot, keepConnection = true) => {
    const active = activeSessionRef.current;
    const automaticClosing = allowsAutomaticCompletion(active);
    let failedSpeechGeneration = failedCapture?.speechGeneration ?? userSpeechGenerationRef.current;
    const groupId = failedCapture?.speechGroupId ?? speechGroupRef.current?.id;
    if (groupId) {
      failedSpeechGroupsRef.current.add(groupId);
      // A missing audio fragment cannot authorize the other fragments as a
      // complete answer. Keep their known words as an incomplete local draft.
      transcribedTurnsRef.current.filter((turn) => turn.speechGroupId === groupId)
        .forEach((turn) => {
          failedSpeechGeneration = Math.max(failedSpeechGeneration, turn.speechGeneration);
          preserveDraft(turn, { finalized: false });
        });
      transcribedTurnsRef.current = transcribedTurnsRef.current.filter((turn) => turn.speechGroupId !== groupId);
      streamingTurnContextsRef.current.forEach((turn, id) => {
        if (turn.speechGroupId !== groupId) return;
        failedSpeechGeneration = Math.max(failedSpeechGeneration, turn.speechGeneration);
        preserveDraft(turn, { finalized: false });
        if (active && turn.userTranscript.trim()) rememberCapturedTranscript(turn, active.id);
        // A failed earlier fragment cannot end a newer capture that is still
        // speaking/transcribing. Its result stays incomplete in this group.
        if (failedCapture && id !== failedCapture.inputAudioItemId) return;
        streamingTurnContextsRef.current.delete(id);
        transcribedItemIdsRef.current.add(id);
        pendingTranscriptionCountRef.current = Math.max(0, pendingTranscriptionCountRef.current - 1);
      });
      const survivingCapture = failedCapture && (
        (captureTurnRef.current?.speechGroupId === groupId
          && captureTurnRef.current.inputAudioItemId !== failedCapture.inputAudioItemId)
        || [...streamingTurnContextsRef.current.values()].some((turn) => turn.speechGroupId === groupId)
      );
      // Further VAD fragments belong to this incomplete answer until recovery
      // playback creates a new boundary; they cannot bypass the missing audio.
      if (speechGroupRef.current?.id === groupId && !survivingCapture) speechGroupRef.current = null;
    }
    if (!failedCapture || captureTurnRef.current?.inputAudioItemId === failedCapture.inputAudioItemId) captureTurnRef.current = null;
    if (automaticClosing && failedCapture && !failedCapture.speechGroupId) {
      const native = streamingTurnContextsRef.current.get(failedCapture.inputAudioItemId);
      if (native === failedCapture) {
        streamingTurnContextsRef.current.delete(failedCapture.inputAudioItemId);
        pendingTranscriptionCountRef.current = Math.max(0, pendingTranscriptionCountRef.current - 1);
        transcribedItemIdsRef.current.add(failedCapture.inputAudioItemId);
        if (native.userTranscript.trim() && active) {
          preserveDraft(native, { finalized: false });
          rememberCapturedTranscript(native, active.id);
        }
      }
    }
    if (!failedCapture || activeNativeSpeechItemIdRef.current === failedCapture.inputAudioItemId) {
      activeNativeSpeechItemIdRef.current = '';
      userSpeechActiveRef.current = false;
    }
    setTurnProcessing(isPersistingTurnRef.current || pendingTranscriptionCountRef.current > 0);
    // A late failure still retires its own incomplete capture, but it cannot
    // rewind another question or a replacement runtime's guide presentation.
    if (failedCapture && (!active
      || (failedCapture.questionId !== (active.current_question?.id ?? null))
      || failedCapture.targetRevision !== (active.current_target?.target_revision ?? undefined)
      || (failedCapture.runtimeGeneration !== undefined
        && failedCapture.runtimeGeneration !== (active.runtime_generation ?? 0)))) return;
    latestFailedSpeechGenerationRef.current = Math.max(latestFailedSpeechGenerationRef.current, failedSpeechGeneration);
    const message = getErrorMessage(failure, failure instanceof Error ? failure.message : 'Unable to process this audio. No answer was saved. Please speak again.');
    const healthyConnection = keepConnection && dataChannelRef.current?.readyState === 'open';
    if (healthyConnection) setInputError(message);
    else setError(message);
    if (!active || active.status === 'crisis_paused' || active.ui_mode === 'crisis') return;
    if (healthyConnection && capturePausedRef.current) return;
    const activity = voiceRecoveryIsActivity(active) || automaticClosing;
    const knownClosingWords = automaticClosing && Boolean(failedCapture?.userTranscript.trim());
    const prompt = automaticClosing ? JSON.stringify({ activity_type: 'module_closing' })
      : activity ? voiceRecoveryActivitySource(active) : voiceRecoveryQuestionSource(active);
    const instructions = knownClosingWords ? [
      'Only part of the learner’s latest words was heard, so their intent is unknown. Briefly acknowledge that and ask them to repeat what they were saying, in your own natural words.',
      'Do not invent or re-ask a module question, ask for a finish command, claim completion, or interpret the incomplete words as a command.',
      `Use the saved personality ${active.voice_personality ?? 'warm_practical'} and response length ${active.voice_response_length ?? 'balanced'}; keep recovery concise.`,
    ].join('\n') : conversationalVoiceRecoveryInstructions(prompt, activity, {
      personality: active.voice_personality, responseLength: active.voice_response_length,
    });
    const noticeSession = { ...active, spoken_response_mode: 'conversational' as const,
      current_target: null, narration_token: null, narration_text: null, command_action: null };
    if (healthyConnection) {
      cancelRealtimeResponseRef.current();
      if (!pendingAssistantOnlyResponseRef.current || pendingAssistantOnlyResponseRef.current.responsePurpose === 'transcription_failure') {
        const responseContext = makeAssistantResponseContext(noticeSession, 'transcription_failure');
        pendingAssistantOnlyResponseRef.current = { instructionBlock: instructions,
          responsePurpose: 'transcription_failure', responseContext };
        if (activity && !knownClosingWords) activityRecoveryNoticeRef.current = { noticeContext: responseContext,
          activityContext: makeAssistantResponseContext(active, automaticClosing ? active.assistant_turn_directive?.response_purpose : undefined),
          speechGeneration: userSpeechGenerationRef.current };
      }
      flushPendingAssistantOnlyResponse();
      return;
    }
    pendingConnectionNoticeRef.current = { instructions, purpose: 'transcription_failure', spokenResponseMode: 'conversational',
      questionId: active.current_question?.id ?? null, resumeAutomaticClosing: automaticClosing && !knownClosingWords };
    disconnect({ recoverable: true });
    setConnectionState('error');
  }, [disconnect, flushPendingAssistantOnlyResponse, makeAssistantResponseContext, preserveDraft, rememberCapturedTranscript]);

  const retireNoSpeechCapture = useCallback((snapshot: CompletedTurnSnapshot | undefined) => {
    if (!snapshot || snapshot.connectionAttemptId !== connectionAttemptIdRef.current) return;
    const active = activeSessionRef.current;
    if (!active) return;
    const itemId = snapshot.inputAudioItemId;
    const native = streamingTurnContextsRef.current.get(itemId);
    if (native) {
      streamingTurnContextsRef.current.delete(itemId);
      pendingTranscriptionCountRef.current = Math.max(0, pendingTranscriptionCountRef.current - 1);
    }
    transcribedItemIdsRef.current.add(itemId);
    if (transcribedItemIdsRef.current.size > 256) {
      const oldest = transcribedItemIdsRef.current.values().next().value;
      if (oldest) transcribedItemIdsRef.current.delete(oldest);
    }
    if (!snapshot.userTranscript.trim()) clearVoiceDraft(active.id, itemId);
    if (activeNativeSpeechItemIdRef.current === itemId) {
      activeNativeSpeechItemIdRef.current = '';
      userSpeechActiveRef.current = false;
    }
    setTurnProcessing(isPersistingTurnRef.current || pendingTranscriptionCountRef.current > 0);
    if ((snapshot.runtimeGeneration ?? 0) !== (active.runtime_generation ?? 0)) return;
    const interrupted = interruptedPresentationRef.current;
    if (interrupted && snapshot.speechGeneration >= interrupted.speechGeneration) interrupted.noSpeechSeen = true;
  }, []);

  const retainRecognizedSpeechPriority = useCallback((snapshot: CompletedTurnSnapshot) => {
    discardBufferedNarrationRef.current();
    closingSafetyIntentRevisionRef.current += 1;
    if (aiSafetyClosingScopeRef.current) stopAiSafetyPolling();
    cancelConnectionNotice();
    if (currentAssistantResponseRef.current?.context.autoFinishAfterResponse) currentAssistantResponseRef.current.automaticFinishSuperseded = true;
    pendingAutomaticClosingRef.current = null;
    setAutomaticCompletionReady(false);
    setTerminalActionReady(null);
    if (interruptedPresentationRef.current && snapshot.speechGeneration >= interruptedPresentationRef.current.speechGeneration) {
      interruptedPresentationRef.current = null;
    }
    if (presentationResumeInFlightRef.current && snapshot.speechGeneration >= presentationResumeInFlightRef.current.speechGeneration) {
      presentationResumeInFlightRef.current = null;
    }
    if (!snapshot.speechGroupId || !failedSpeechGroupsRef.current.has(snapshot.speechGroupId)) {
      activityRecoveryNoticeRef.current = null;
    }
  }, [cancelConnectionNotice, stopAiSafetyPolling]);

  // A VAD pause is not necessarily the end of the learner's answer. Keep
  // transcription independent of evaluation, and join fragments captured
  // before the assistant starts its next spoken response.
  const flushTranscribedTurns = useCallback(() => {
    if (userSpeechActiveRef.current || pendingTranscriptionCountRef.current || isPersistingTurnRef.current) return;
    transcribedTurnsRef.current.sort((left, right) => left.speechGeneration - right.speechGeneration);
    const first = transcribedTurnsRef.current[0];
    if (!first) return;
    const group = transcribedTurnsRef.current.filter((turn) => turn.speechGroupId === first.speechGroupId);
    transcribedTurnsRef.current = transcribedTurnsRef.current.filter((turn) => turn.speechGroupId !== first.speechGroupId);
    const transcript = group.map((turn) => turn.userTranscript.trim()).join(' ');
    currentUserTranscriptRef.current = transcript;
    setUserTranscript(transcript);
    const combined = { ...first, userTranscript: transcript,
      inputAudioItemId: group.map((turn) => turn.inputAudioItemId).join(','),
      speechGeneration: group[group.length - 1].speechGeneration };
    if (activeSessionRef.current) {
      rememberCapturedTranscript(combined, activeSessionRef.current.id);
      if (group.length > 1) {
        preserveDraft(combined);
        group.forEach((fragment) => clearVoiceDraft(activeSessionRef.current!.id, fragment.inputAudioItemId));
      }
    }
    void processCompletedTurnRef.current?.(combined);
  }, [preserveDraft, rememberCapturedTranscript]);
  flushTranscribedTurnsRef.current = flushTranscribedTurns;

  const captureNativeTurn = useCallback((itemId: string) => {
    const existing = streamingTurnContextsRef.current.get(itemId);
    if (existing) return existing;
    if (pendingTranscriptionCountRef.current + transcribedTurnsRef.current.length >= 8) {
      failAudioTranscription(new Error('Transcription is still catching up. Please reconnect and repeat your latest answer.'), undefined, false);
      return null;
    }
    const active = activeSessionRef.current;
    const displayReceipt = completedDisplayReceiptForSpeech();
    if (!speechGroupRef.current || capturePausedRef.current || displayReceipt
      || Date.now() - speechGroupRef.current.startedAt > 120_000) {
      // End-of-display backchannels and explicit commands keep separate groups
      // while the receipt settles, each owned by the display just heard.
      speechGroupRef.current = {
        id: newLiveId(), questionId: displayReceipt?.context.questionId ?? active?.current_question?.id ?? null,
        targetContentId: displayReceipt?.context.narrationContentId ?? active?.current_target?.content_id ?? 'unknown',
        targetRevision: displayReceipt ? displayReceipt.context.targetRevision : active?.current_target?.target_revision ?? undefined,
        startedAt: Date.now(),
      };
    }
    const group = speechGroupRef.current;
    const snapshot: CompletedTurnSnapshot = {
      questionId: group.questionId, targetContentId: group.targetContentId,
      targetRevision: group.targetRevision,
      ...(group.questionId !== null ? { speechGroupId: group.id } : {}),
      userTranscript: '', assistantTranscript: currentAssistantTranscriptRef.current.trim(),
      assistantResponseId: currentAssistantResponseRef.current?.providerResponseId,
      inputAudioItemId: itemId, speechGeneration: userSpeechGenerationRef.current,
      connectionAttemptId: connectionAttemptIdRef.current,
      confirmation: !displayReceipt && active?.pending_confirmation ? { ...active.pending_confirmation } : null,
      runtimeGeneration: active?.runtime_generation ?? 0,
      transcriptionModel: active?.transcription_model,
      capturedAt: new Date(speechStartedAtByItemIdRef.current[itemId] ?? Date.now()).toISOString(),
    };
    streamingTurnContextsRef.current.set(itemId, snapshot);
    // Each native item remains pending from its first evidence until its final
    // transcript. A VAD pause must not let another fragment bypass this queue.
    pendingTranscriptionCountRef.current += 1;
    return snapshot;
  }, [completedDisplayReceiptForSpeech, failAudioTranscription]);

  const beginCompletedTurnAudio = useCallback((itemId: string) => {
    const active = activeSessionRef.current;
    if (!active?.id || active.transcription_transport !== 'completed_turn_upload') return;
    if (captureTurnRef.current || transcribedItemIdsRef.current.has(itemId)) return;
    try {
      if (!completedAudioCaptureRef.current) throw new Error('Audio capture is unavailable. Please reconnect.');
      completedAudioCaptureRef.current.begin();
      const displayReceipt = completedDisplayReceiptForSpeech();
      if (!speechGroupRef.current || capturePausedRef.current || displayReceipt
        || Date.now() - speechGroupRef.current.startedAt > 120_000) {
        speechGroupRef.current = {
          id: newLiveId(), questionId: displayReceipt?.context.questionId ?? active.current_question?.id ?? null,
          targetContentId: displayReceipt?.context.narrationContentId ?? active.current_target?.content_id ?? 'unknown',
          targetRevision: displayReceipt ? displayReceipt.context.targetRevision : active.current_target?.target_revision ?? undefined,
          startedAt: Date.now(),
        };
      }
      const group = speechGroupRef.current;
      captureTurnRef.current = {
        questionId: group.questionId,
        targetContentId: group.targetContentId,
        targetRevision: group.targetRevision,
        ...(group.questionId !== null ? { speechGroupId: group.id } : {}),
        userTranscript: '', assistantTranscript: currentAssistantTranscriptRef.current.trim(),
      assistantResponseId: currentAssistantResponseRef.current?.providerResponseId,
        inputAudioItemId: itemId || newLiveId(), speechGeneration: userSpeechGenerationRef.current,
        connectionAttemptId: connectionAttemptIdRef.current,
        confirmation: !displayReceipt && active.pending_confirmation ? { ...active.pending_confirmation } : null,
        runtimeGeneration: active.runtime_generation ?? 0,
        transcriptionModel: active.transcription_model,
        capturedAt: new Date(speechStartedAtByItemIdRef.current[itemId] ?? Date.now()).toISOString(),
      };
    } catch (failure) { failAudioTranscription(failure, captureTurnRef.current ?? undefined, false); }
  }, [completedDisplayReceiptForSpeech, failAudioTranscription]);

  const finishCompletedTurnAudio = useCallback((itemId: string) => {
    const active = activeSessionRef.current;
    const snapshot = captureTurnRef.current;
    if (!active?.id || active.transcription_transport !== 'completed_turn_upload') return;
    if (itemId && transcribedItemIdsRef.current.has(itemId)) return;
    try {
      if (!snapshot || !completedAudioCaptureRef.current) {
        throw new Error('The beginning of this audio turn was not captured. No answer was saved. Please reconnect.');
      }
      if (itemId && snapshot.inputAudioItemId !== itemId) throw new Error('The audio turn changed unexpectedly. Please reconnect.');
      if (pendingTranscriptionCountRef.current + transcribedTurnsRef.current.length >= 8) throw new Error('Transcription is still catching up. Please reconnect and repeat your latest answer.');
      const audio = completedAudioCaptureRef.current.finish();
      captureTurnRef.current = null;
      transcribedItemIdsRef.current.add(snapshot.inputAudioItemId);
      // The set belongs to one connection; keep deduplication memory bounded.
      if (transcribedItemIdsRef.current.size > 256) {
        const oldest = transcribedItemIdsRef.current.values().next().value;
        if (oldest) transcribedItemIdsRef.current.delete(oldest);
      }
      const attempt = connectionAttemptIdRef.current;
      const requestId = newLiveId();
      const generation = active.runtime_generation ?? 0;
      const questionId = snapshot.questionId;
      pendingTranscriptionCountRef.current += 1;
      setTurnProcessing(true);
      transcriptionQueueRef.current = transcriptionQueueRef.current.then(async () => {
        if (attempt !== connectionAttemptIdRef.current) return;
        const controller = new AbortController();
        transcriptionControllersRef.current.add(controller);
        try {
          const response = await voiceFlow.transcribeTurn(flowName, active.id, {
            audio, request_id: requestId, runtime_generation: generation, question_id: questionId,
            ...(questionId !== null ? { speech_group_id: snapshot.speechGroupId } : {}),
            target_revision: snapshot.targetRevision,
          }, controller.signal);
          if (attempt !== connectionAttemptIdRef.current || controller.signal.aborted) return;
          const result = response.data;
          if (
            result.request_id !== requestId || result.runtime_generation !== generation
            || result.question_id !== questionId || typeof result.transcript !== 'string'
            || result.speech_group_id !== snapshot.speechGroupId
            || (result.target_revision ?? undefined) !== snapshot.targetRevision
          ) throw new Error('The transcription did not match the current audio turn. No answer was saved. Please reconnect.');
          setSession((previous) => previous ? { ...previous, cost_estimate: mergeCostEstimates(result.cost_estimate, previous.cost_estimate) } : previous);
          if (result.outcome === 'no_speech' && !result.transcript.trim()) {
            retireNoSpeechCapture(snapshot);
            return;
          }
          if (!result.transcript.trim()) throw new Error('The transcription did not match the current audio turn. No answer was saved. Please reconnect.');
          currentUserTranscriptRef.current = result.transcript;
          setUserTranscript(result.transcript);
          const transcribed = { ...snapshot, userTranscript: result.transcript };
          retainRecognizedSpeechPriority(transcribed);
          rememberCapturedTranscript(transcribed, active.id);
          preserveDraft(transcribed);
          if (snapshot.speechGroupId && failedSpeechGroupsRef.current.has(snapshot.speechGroupId)) {
            preserveDraft(transcribed, { finalized: false });
          } else transcribedTurnsRef.current.push(transcribed);
        } catch (failure) {
          if (attempt === connectionAttemptIdRef.current && !controller.signal.aborted) failAudioTranscription(failure, snapshot);
        } finally {
          transcriptionControllersRef.current.delete(controller);
          if (attempt === connectionAttemptIdRef.current) {
            pendingTranscriptionCountRef.current = Math.max(0, pendingTranscriptionCountRef.current - 1);
            flushTranscribedTurnsRef.current();
            flushPendingAssistantOnlyResponse();
            flushAssistantResponsesRef.current();
            if (!pendingTranscriptionCountRef.current && !isPersistingTurnRef.current) setTurnProcessing(false);
          }
        }
      });
    } catch (failure) { failAudioTranscription(failure, snapshot ?? undefined, !/reconnect|turn limit/i.test(failure instanceof Error ? failure.message : '')); }
  }, [failAudioTranscription, flowName, flushPendingAssistantOnlyResponse, preserveDraft, rememberCapturedTranscript,
    retireNoSpeechCapture, retainRecognizedSpeechPriority]);

  const handleRealtimeEvent = useCallback((event: Record<string, unknown>, source: AssistantTransport = 'rtc') => {
    const eventType = typeof event.type === 'string' ? event.type : '';
    if (source === 'signed_ws' && (eventType.startsWith('input_audio_buffer.')
      || eventType.startsWith('conversation.item.input_audio_transcription.'))) return;
    // PCM belongs to the playback queue only. Never retain audio/base64 in the
    // debug event history, including malformed, stale or foreign-lane chunks.
    if (eventType === 'response.output_audio.delta' || eventType === 'response.audio.delta') {
      const responseId = getRealtimeResponseId(event);
      const capture = responseId ? assistantResponsesByProviderIdRef.current.get(responseId) : undefined;
      if (source !== 'signed_ws' || !responseId || capture?.transport !== source
        || capture !== currentAssistantResponseRef.current || !capture.bufferedOwner
        || capture.providerAudioEnded || !bufferedNarrationIsCurrent(capture)) return;
      if ((capture.outputItemId && event.item_id !== capture.outputItemId)
        || (capture.outputAudioContentIndex !== undefined && event.content_index !== capture.outputAudioContentIndex)) return;
      if (typeof event.event_id === 'string') {
        capture.audioDeltaEventIds ??= new Set();
        if (capture.audioDeltaEventIds.has(event.event_id)) return;
        capture.audioDeltaEventIds.add(event.event_id);
      }
      try {
        if (typeof event.delta !== 'string') throw new Error('Voice narration audio was invalid. Please reconnect.');
        if (typeof event.item_id === 'string') capture.outputItemId = event.item_id;
        if (typeof event.content_index === 'number' && Number.isInteger(event.content_index) && event.content_index >= 0) {
          capture.outputAudioContentIndex = event.content_index;
        }
        const pcm = decodeNarrationPcm16(event.delta);
        if (!capture.providerAudioStarted) {
          capture.providerAudioStarted = true;
          narrationPlaybackRef.current?.providerStarted(capture.bufferedOwner);
        }
        if (!narrationPlaybackRef.current?.append(capture.bufferedOwner, pcm)) {
          throw new Error('Voice narration audio could not be buffered. Please reconnect.');
        }
      } catch (failure) {
        failNarrationTransportRef.current(failure instanceof Error ? failure : new Error('Voice narration audio could not be read. Please reconnect.'));
      }
      return;
    }
    pushRealtimeEvent(event);

    const transcriptValue = getRealtimeEventTranscript(event);
    const realtimeItemId = getRealtimeItemId(event);
    if (eventType === REALTIME_RESPONSE_DONE_EVENT || eventType === 'response.completed') {
      const responseId = getRealtimeResponseId(event);
      const usageCapture = responseId ? assistantResponsesByProviderIdRef.current.get(responseId) : currentAssistantResponseRef.current;
      const usage = getRealtimeUsagePayload(event);
      const ownsUsage = usageCapture?.transport === source && (source === 'rtc' || Boolean(responseId));
      if (usage && (ownsUsage || (!usageCapture && source === 'rtc'))) recordUsageEvent({
        event_type: REALTIME_RESPONSE_DONE_EVENT, event_id: getRealtimeEventId(event, 'response'),
        model: session?.realtime_model ?? 'gpt-realtime-2.1', usage,
      });
    }
    const responseId = getRealtimeResponseId(event);
    const isAssistantEvent = eventType.startsWith('response.') || eventType.startsWith('output_audio_buffer.');
    const generationTerminal = eventType === REALTIME_RESPONSE_DONE_EVENT || eventType === 'response.completed';
    if (source === 'signed_ws' && isAssistantEvent && !responseId) return;
    if (source === 'rtc' && eventType === OUTPUT_AUDIO_BUFFER_CLEARED_EVENT && discardedNarrationRef.current
      && responseId && responseId === discardedNarrationRef.current.responseId) {
      discardedNarrationRef.current.cleared = true;
      if (narrationClearTimerRef.current !== null) window.clearTimeout(narrationClearTimerRef.current);
      narrationClearTimerRef.current = null;
      window.setTimeout(() => flushAssistantResponsesRef.current(), 0);
    }
    if (generationTerminal && responseId && blockedProviderResponseRef.current === responseId) {
      blockedProviderResponseRef.current = null;
      if (generationRecoveryTimeoutRef.current !== null) window.clearTimeout(generationRecoveryTimeoutRef.current);
      generationRecoveryTimeoutRef.current = null;
      window.setTimeout(() => flushAssistantResponsesRef.current(), 0);
      return;
    }
    let responseCapture = responseId ? assistantResponsesByProviderIdRef.current.get(responseId) : undefined;
    if (isAssistantEvent && responseId && !responseCapture) {
      // Only the provider's creation acknowledgement can bind a reserved
      // request. An unknown old terminal/caption event cannot claim or release
      // a newer request that is still waiting for its own acknowledgement.
      if (eventType !== 'response.created') return;
      const pendingIndex = pendingAssistantResponseCapturesRef.current.findIndex((capture) => capture.transport === source);
      if (pendingIndex < 0) return;
      [responseCapture] = pendingAssistantResponseCapturesRef.current.splice(pendingIndex, 1);
      // Never attach an unknown late response to the current question.
      if (!responseCapture) return;
      const responseContexts = assistantResponsesByProviderIdRef.current;
      responseCapture.providerResponseId = responseId;
      responseContexts.set(responseId, responseCapture);
      if (responseContexts.size > MAX_ASSISTANT_RESPONSE_CONTEXTS) {
        const oldestResponseId = responseContexts.keys().next().value;
        if (oldestResponseId) responseContexts.delete(oldestResponseId);
      }
    }
    responseCapture ??= currentAssistantResponseRef.current ?? undefined;
    if (isAssistantEvent && responseCapture?.transport !== source) return;
    if (eventType === 'response.created' && responseCapture?.cancelWhenCreated && responseCapture.providerResponseId) {
      narrationTransportRef.current?.send({ type: 'response.cancel', response_id: responseCapture.providerResponseId });
      responseCapture.cancelWhenCreated = false;
    }
    if (responseCapture?.bufferedOwner && eventType === 'response.output_item.added') {
      const item = event.item as { id?: unknown; type?: unknown; role?: unknown } | undefined;
      if (item?.type === 'message' && item.role === 'assistant' && typeof item.id === 'string') responseCapture.outputItemId = item.id;
    }
    if (responseCapture?.bufferedOwner && eventType === 'response.content_part.added') {
      const part = event.part as { type?: unknown } | undefined;
      if ((part?.type === 'audio' || part?.type === 'output_audio') && typeof event.content_index === 'number') {
        responseCapture.outputAudioContentIndex = event.content_index;
        if (typeof event.item_id === 'string') responseCapture.outputItemId = event.item_id;
      }
    }
    if (generationTerminal && responseCapture && isAssistantResponseCurrentConnection(responseCapture.context)) {
      responseCapture.generationDone = true;
      if (responseCapture.bufferedOwner && !responseCapture.outputItemId) {
        const output = (event.response as { output?: { id?: unknown; content?: { type?: unknown }[] }[] } | undefined)?.output;
        const audioItem = output?.find((item) => typeof item.id === 'string'
          && item.content?.some((part) => part.type === 'audio' || part.type === 'output_audio'));
        if (audioItem && typeof audioItem.id === 'string') {
          responseCapture.outputItemId = audioItem.id;
          responseCapture.outputAudioContentIndex = audioItem.content?.findIndex((part) => part.type === 'audio' || part.type === 'output_audio');
        }
      }
      if (responseId && responseId === responseCapture.providerResponseId && responseCapture.generationSucceeded === undefined) {
        const responseStatus = (event.response as { status?: unknown } | undefined)?.status;
        responseCapture.generationSucceeded = responseStatus === 'completed'
          || (eventType === 'response.completed' && responseStatus === undefined);
      }
      if (responseCapture.bufferedOwner && !responseCapture.cancelled && responseCapture.generationSucceeded === false) {
        const details = (event.response as { status_details?: { error?: unknown } } | undefined)?.status_details;
        failNarrationTransportRef.current(describeNarrationProviderError(details?.error));
        return;
      }
      responseCapture.settleCancelledHostMessage?.();
      responseCapture.settleCancelledHostMessage = undefined;
      if (activeGenerationRef.current === responseCapture) {
        if (generationRecoveryTimeoutRef.current !== null) window.clearTimeout(generationRecoveryTimeoutRef.current);
        generationRecoveryTimeoutRef.current = null;
        activeGenerationRef.current = null;
        activeGenerationRequestRef.current = null;
        pendingAssistantResponseCapturesRef.current = pendingAssistantResponseCapturesRef.current.filter((capture) => capture !== responseCapture);
        window.setTimeout(() => flushAssistantResponsesRef.current(), 0);
      }
      const hostMessage = activeSpokenMessageRef.current;
      if (responseCapture.context.responsePurpose === 'roleplay_host' && hostMessage?.owner
        && hostMessage.responseContext === responseCapture.context && responseCapture.generationSucceeded === false) {
        activeSpokenMessageRef.current = null;
        if ((event.response as { status?: unknown } | undefined)?.status === 'cancelled') hostMessage.onInterrupted?.();
        else hostMessage.reject(new Error('The guide could not finish speaking.'));
        window.setTimeout(() => flushSpokenMessagesRef.current(), 0);
      }
    }
    if (eventType === 'response.created') return;
    if (isAssistantEvent && responseCapture) {
      if (!isAssistantResponseCurrentConnection(responseCapture.context)) return;
      if (responseCapture.cancelled) {
        if ((eventType === OUTPUT_AUDIO_BUFFER_STOPPED_EVENT || eventType === OUTPUT_AUDIO_BUFFER_CLEARED_EVENT)
          && responseCapture === currentAssistantResponseRef.current) {
          assistantOutputAudioActiveRef.current = false;
          window.setTimeout(() => flushAssistantResponsesRef.current(), 0);
        }
        return;
      }
      if (responseCapture !== currentAssistantResponseRef.current) {
        // Delayed provider events still belong to the request that created them.
        // They must not replace the new question's transcript or playback state.
        if (!responseCapture.replay) {
          responseCapture.timestamp ??= new Date().toISOString();
          if (ASSISTANT_TRANSCRIPT_DELTA_EVENTS.has(eventType)) {
            responseCapture.transcript = appendTranscript(responseCapture.transcript, transcriptValue);
          } else if (ASSISTANT_TRANSCRIPT_COMPLETED_EVENTS.has(eventType)) {
            responseCapture.transcript = mergeAssistantTranscript(responseCapture.transcript, transcriptValue);
          } else if (eventType === REALTIME_RESPONSE_DONE_EVENT || eventType === 'response.completed') {
            responseCapture.transcript = mergeAssistantTranscript(responseCapture.transcript, getAssistantTranscriptFromResponse(event));
            commitAssistantDebugTranscript(responseCapture.transcript, responseCapture.timestamp, responseCapture);
          }
        }
        return;
      }
    }

    if (source === 'signed_ws' && (eventType === 'response.output_audio.done' || eventType === 'response.audio.done')) {
      if (!responseId || responseCapture?.transport !== source || responseId !== responseCapture.providerResponseId
        || !responseCapture.bufferedOwner || responseCapture.providerAudioEnded) return;
      if ((responseCapture.outputItemId && event.item_id !== responseCapture.outputItemId)
        || (responseCapture.outputAudioContentIndex !== undefined && event.content_index !== responseCapture.outputAudioContentIndex)) return;
      responseCapture.providerAudioEnded = true;
      narrationPlaybackRef.current?.providerEnded(responseCapture.bufferedOwner);
      return;
    }

    if (eventType === USER_SPEECH_STARTED_EVENT) {
      if (realtimeItemId && (transcribedItemIdsRef.current.has(realtimeItemId)
        || streamingTurnContextsRef.current.has(realtimeItemId))) return;
      cancelConnectionNotice();
      setError(null);
      setInputError(null);
      if (pendingAutomaticClosingRef.current) pendingAutomaticClosingRef.current.automaticFinishReady = false;
      setAutomaticCompletionReady(false);
      setTerminalActionReady(null);
      userSpeechActiveRef.current = true;
      const startedAt = Date.now();
      latestSpeechStartedAtRef.current = startedAt;
      if (realtimeItemId) {
        speechStartedAtByItemIdRef.current[realtimeItemId] = startedAt;
      }
      replayInProgressRef.current = false;
      const continuingFailedGroup = speechGroupRef.current && !capturePausedRef.current
        && Date.now() - speechGroupRef.current.startedAt <= 120_000
        && failedSpeechGroupsRef.current.has(speechGroupRef.current.id);
      const active = activeSessionRef.current;
      const queued = [...queuedAssistantResponsesRef.current].reverse().find((request) =>
        request.context.responsePurpose !== 'roleplay_host'
        && (request.context.responsePurpose !== 'completion_closing' || request.context.autoFinishAfterResponse)
        && isAssistantResponseCurrentConnection(request.context)
        && request.context.questionId === (active?.current_question?.id ?? null));
      const pendingNotice = pendingAssistantOnlyResponseRef.current?.responsePurpose === 'transcription_failure'
        ? pendingAssistantOnlyResponseRef.current : null;
      const current = currentAssistantResponseRef.current;
      const currentFailure = current && ['transcription_failure', 'save_failure'].includes(current.context.responsePurpose ?? '')
        && !current.audioStopped ? current.instructions ? { instructions: current.instructions, context: current.context }
          : activeGenerationRequestRef.current : null;
      const interruptedNarration = (current?.context.narrationToken || current?.bufferedOwner) && !current.replay
        && !current.audioStopped ? current : null;
      const bufferedNarration = interruptedNarration?.bufferedOwner
        && bufferedNarrationIsCurrent(interruptedNarration) ? interruptedNarration : null;
      const interruptedClosing = current?.context.autoFinishAfterResponse && !current.audioStopped && !current.replay
        ? current.instructions ? { instructions: current.instructions, context: current.context } : activeGenerationRequestRef.current : null;
      if (queued || pendingNotice || currentFailure || interruptedNarration || interruptedClosing || presentationResumeInFlightRef.current) {
        const request = queued ?? (pendingNotice ? { instructions: pendingNotice.instructionBlock,
          context: pendingNotice.responseContext, disconnectAfterResponse: pendingNotice.disconnectAfterResponse }
          : currentFailure ?? interruptedClosing ?? undefined);
        const responseContext = request?.context ?? interruptedNarration?.context ?? presentationResumeInFlightRef.current?.context;
        if (responseContext) interruptedPresentationRef.current = { context: responseContext, request,
          speechGeneration: userSpeechGenerationRef.current + 1, noSpeechSeen: false,
          ...(interruptedClosing && current?.providerResponseId ? { assistantResponseId: current.providerResponseId } : {}),
          ...(bufferedNarration ? { bufferedCapture: bufferedNarration } : {}),
          taskCommandAction: active?.command_action,
          ...(['transcription_failure', 'save_failure'].includes(responseContext.responsePurpose ?? '')
            ? { failureGeneration: latestFailedSpeechGenerationRef.current } : {}) };
        presentationResumeInFlightRef.current = null;
      }
      if (!continuingFailedGroup || pendingAssistantOnlyResponseRef.current?.responsePurpose !== 'transcription_failure') {
        pendingAssistantOnlyResponseRef.current = null;
      }
      queuedAssistantResponsesRef.current = queuedAssistantResponsesRef.current.filter((request) => request.context.responsePurpose === 'roleplay_host');
      userSpeechGenerationRef.current += 1;
      beginCompletedTurnAudio(realtimeItemId);
      if (activeSessionRef.current?.transcription_transport !== 'completed_turn_upload') {
        activeNativeSpeechItemIdRef.current = realtimeItemId;
        if (!captureNativeTurn(realtimeItemId)) return;
      }
      if (
        realtimeItemId
        && currentUserTranscriptItemIdRef.current
        && currentUserTranscriptItemIdRef.current !== realtimeItemId
      ) {
        currentUserTranscriptRef.current = '';
        currentUserDebugTimestampRef.current = null;
        setLiveUserDebugEntry(null);
      }
      if (realtimeItemId) {
        currentUserTranscriptItemIdRef.current = realtimeItemId;
      }
      if (bufferedNarration?.bufferedOwner) {
        if (narrationPlaybackRef.current?.pause(bufferedNarration.bufferedOwner)) {
          bufferedNarration.bufferedPaused = true;
        } else {
          discardBufferedNarrationRef.current(bufferedNarration);
          narrationPlaybackFailedRef.current = true;
          setError('Voice narration could not pause safely. Your progress is preserved. Please reconnect to continue.');
        }
      } else {
        cancelRealtimeResponse();
        assistantOutputAudioActiveRef.current = false;
      }
      setAssistantPhaseValue('listening');
      return;
    }

    if (eventType === USER_SPEECH_STOPPED_EVENT) {
      if (activeSessionRef.current?.transcription_transport !== 'completed_turn_upload') {
        if (transcribedItemIdsRef.current.has(realtimeItemId)) return;
        if (realtimeItemId && activeNativeSpeechItemIdRef.current
          && realtimeItemId !== activeNativeSpeechItemIdRef.current) return;
        activeNativeSpeechItemIdRef.current = '';
        if (streamingTurnContextsRef.current.size) setTurnProcessing(true);
      }
      userSpeechActiveRef.current = false;
      if (realtimeItemId) {
        currentUserTranscriptItemIdRef.current = realtimeItemId;
      }
      setAssistantPhaseValue('listening');
      finishCompletedTurnAudio(realtimeItemId);
      flushPendingAssistantOnlyResponse();
      flushAssistantResponsesRef.current();
      return;
    }

    if (
      activeSessionRef.current?.transcription_transport === 'completed_turn_upload'
      && (eventType === USER_TRANSCRIPT_DELTA_EVENT || eventType === USER_TRANSCRIPT_COMPLETED_EVENT)
    ) return; // Only the selected completed-audio transcriber may supply an answer.

    if (eventType === 'conversation.item.input_audio_transcription.failed'
      && activeSessionRef.current?.transcription_transport !== 'completed_turn_upload') {
      if (!transcribedItemIdsRef.current.has(realtimeItemId)) {
        failAudioTranscription(new Error('Unable to process this audio. No answer was saved. Please speak again.'), streamingTurnContextsRef.current.get(realtimeItemId));
      }
      return;
    }

    if (eventType === USER_TRANSCRIPT_DELTA_EVENT) {
      if (transcribedItemIdsRef.current.has(realtimeItemId)) return;
      setError(null);
      setInputError(null);
      if (!streamingTurnContextsRef.current.has(realtimeItemId)) {
        // Fallback for providers that omit VAD events. Deltas for a captured
        // item are transcription progress, never evidence of new speech.
        userSpeechGenerationRef.current += 1;
      }
      const capturedContext = captureNativeTurn(realtimeItemId);
      if (!capturedContext) return;
      if (
        realtimeItemId
        && currentUserTranscriptItemIdRef.current
        && currentUserTranscriptItemIdRef.current !== realtimeItemId
      ) {
        currentUserTranscriptRef.current = '';
        currentUserDebugTimestampRef.current = null;
        setLiveUserDebugEntry(null);
      }
      if (realtimeItemId) {
        currentUserTranscriptItemIdRef.current = realtimeItemId;
      }
      if (!currentUserDebugTimestampRef.current) {
        currentUserDebugTimestampRef.current = new Date().toISOString();
      }
      capturedContext.userTranscript = appendTranscript(capturedContext.userTranscript, transcriptValue);
      currentUserTranscriptRef.current = capturedContext.userTranscript;
      setUserTranscript(currentUserTranscriptRef.current);
      setLiveUserDebugEntry({
        id: realtimeItemId || `live-user-${currentUserDebugTimestampRef.current}`,
        text: currentUserTranscriptRef.current,
        timestamp: currentUserDebugTimestampRef.current,
        isLive: true,
      });
      setAssistantPhaseValue('listening');
      return;
    }

    if (eventType === USER_TRANSCRIPT_COMPLETED_EVENT) {
      if (realtimeItemId && transcribedItemIdsRef.current.has(realtimeItemId)) return;
      if (!transcriptValue.trim()) {
        const captured = streamingTurnContextsRef.current.get(realtimeItemId);
        if (typeof event.transcript !== 'string' || captured?.userTranscript.trim()) {
          failAudioTranscription(new Error('Unable to process this audio. Please say your answer again.'), captured);
          return;
        }
        const speechStartedAt = speechStartedAtByItemIdRef.current[realtimeItemId];
        recordUsageEvent({ event_type: USER_TRANSCRIPT_COMPLETED_EVENT,
          event_id: getRealtimeEventId(event, 'transcription'),
          model: captured?.transcriptionModel ?? session?.transcription_model ?? 'gpt-4o-mini-transcribe',
          usage: getRealtimeUsagePayload(event),
          duration_seconds: speechStartedAt ? Math.max(0, (Date.now() - speechStartedAt) / 1000) : null });
        delete speechStartedAtByItemIdRef.current[realtimeItemId];
        if (!activeNativeSpeechItemIdRef.current || activeNativeSpeechItemIdRef.current === realtimeItemId) latestSpeechStartedAtRef.current = null;
        retireNoSpeechCapture(captured);
        flushTranscribedTurnsRef.current();
        flushPendingAssistantOnlyResponse();
        flushAssistantResponsesRef.current();
        return;
      }
      const capturedContext = captureNativeTurn(realtimeItemId);
      if (!capturedContext) return;
      // Some providers omit VAD events. Final speech still supersedes an
      // unfinished guide generation, but its cancel acknowledgement is awaited.
      if (activeGenerationRef.current && !activeGenerationRef.current.cancelled) cancelRealtimeResponse();
      if (realtimeItemId) {
        transcribedItemIdsRef.current.add(realtimeItemId);
        if (transcribedItemIdsRef.current.size > 256) {
          const oldest = transcribedItemIdsRef.current.values().next().value;
          if (oldest) transcribedItemIdsRef.current.delete(oldest);
        }
      }
      setError(null);
      setInputError(null);
      if (activeNativeSpeechItemIdRef.current === realtimeItemId) {
        activeNativeSpeechItemIdRef.current = '';
        userSpeechActiveRef.current = false;
      }
      if (realtimeItemId) {
        currentUserTranscriptItemIdRef.current = realtimeItemId;
      }
      const completedAt = Date.now();
      const speechStartedAt = (
        realtimeItemId
          ? speechStartedAtByItemIdRef.current[realtimeItemId]
          : undefined
      ) ?? latestSpeechStartedAtRef.current;
      const durationSeconds = speechStartedAt
        ? Math.max(0, (completedAt - speechStartedAt) / 1000)
        : null;
      const usagePayload = getRealtimeUsagePayload(event);
      recordUsageEvent({
        event_type: USER_TRANSCRIPT_COMPLETED_EVENT,
        event_id: getRealtimeEventId(event, 'transcription'),
        model: session?.transcription_model ?? session?.effective_transcription_model ?? 'gpt-4o-mini-transcribe',
        usage: usagePayload,
        duration_seconds: durationSeconds,
      });
      if (realtimeItemId) {
        delete speechStartedAtByItemIdRef.current[realtimeItemId];
      }
      latestSpeechStartedAtRef.current = null;
      if (!currentUserDebugTimestampRef.current) {
        currentUserDebugTimestampRef.current = new Date().toISOString();
      }
      currentUserTranscriptRef.current = transcriptValue.trim();
      setUserTranscript(currentUserTranscriptRef.current);
      rememberCapturedTranscript({ ...capturedContext, userTranscript: currentUserTranscriptRef.current }, session?.id ?? 0);
      setLiveUserDebugEntry({
        id: realtimeItemId || `live-user-${currentUserDebugTimestampRef.current}`,
        text: currentUserTranscriptRef.current,
        timestamp: currentUserDebugTimestampRef.current,
        isLive: true,
      });
      setAssistantPhaseValue('listening');
      streamingTurnContextsRef.current.delete(realtimeItemId);
      pendingTranscriptionCountRef.current = Math.max(0, pendingTranscriptionCountRef.current - 1);
      const transcribed = { ...capturedContext, userTranscript: currentUserTranscriptRef.current };
      retainRecognizedSpeechPriority(transcribed);
      if (capturedContext.speechGroupId && failedSpeechGroupsRef.current.has(capturedContext.speechGroupId)) {
        preserveDraft(transcribed, { finalized: false });
      } else transcribedTurnsRef.current.push(transcribed);
      flushTranscribedTurnsRef.current();
      flushPendingAssistantOnlyResponse();
      flushAssistantResponsesRef.current();
      if (!pendingTranscriptionCountRef.current && !isPersistingTurnRef.current) setTurnProcessing(false);
      return;
    }

    if (eventType === OUTPUT_AUDIO_BUFFER_STARTED_EVENT) {
      if (responseCapture?.bufferedOwner) {
        if (!responseId || responseId !== responseCapture.providerResponseId) return;
        narrationPlaybackRef.current?.providerStarted(responseCapture.bufferedOwner);
        assistantOutputAudioActiveRef.current = true;
        return;
      }
      const discarded = discardedNarrationRef.current;
      if (discarded?.cleared && responseId && responseId === responseCapture?.providerResponseId) {
        narrationPlaybackRef.current?.releasePassthrough(discarded.owner);
        discardedNarrationRef.current = null;
      }
      if (responseCapture) {
        responseCapture.audioStarted = true;
        responseCapture.audioStartedOwned = Boolean(responseId && responseId === responseCapture.providerResponseId);
        responseCapture.playbackMuted = Boolean(audioElementRef.current?.muted || audioElementRef.current?.volume === 0);
      }
      // Playback ends this group for future speech even if earlier work is busy.
      // Captured/queued turns already own their original group and question.
      // A replay does not introduce the next authoritative question.
      if (!responseCapture?.replay) {
        speechGroupRef.current = null;
      }
      setError(null);
      setInputError(null);
      assistantOutputAudioActiveRef.current = true;
      assistantOutputAudioSeenForResponseRef.current = true;
      if (pendingDisconnectAfterResponseRef.current) {
        clearCompletionClosingDisconnectFallback();
      }
      setAssistantPhaseValue('responding');
      return;
    }

    if (eventType === OUTPUT_AUDIO_BUFFER_CLEARED_EVENT && responseCapture) {
      const message = activeSpokenMessageRef.current;
      if (message?.owner && message.responseContext === responseCapture.context) {
        responseCapture.cancelled = true;
        activeSpokenMessageRef.current = null;
        message.onInterrupted?.();
      }
    }

    if (eventType === OUTPUT_AUDIO_BUFFER_STOPPED_EVENT || eventType === OUTPUT_AUDIO_BUFFER_CLEARED_EVENT) {
      if (responseCapture?.bufferedOwner && event.local_narration_drain !== responseCapture.bufferedOwner) {
        if (!responseId || responseId !== responseCapture.providerResponseId) return;
        if (eventType === OUTPUT_AUDIO_BUFFER_STOPPED_EVENT) {
          responseCapture.providerAudioEnded = true;
          narrationPlaybackRef.current?.providerEnded(responseCapture.bufferedOwner);
        } else discardBufferedNarrationRef.current(responseCapture);
        return;
      }
      if (responseCapture) {
        if (eventType === OUTPUT_AUDIO_BUFFER_STOPPED_EVENT) {
          responseCapture.audioStopped = true;
          responseCapture.audioStoppedOwned = Boolean(responseId && responseId === responseCapture.providerResponseId);
          if (responseCapture.context.autoFinishAfterResponse) responseCapture.closingReadyAt ??= Date.now() + 250;
        }
        else responseCapture.cancelled = true;
      }
      setError(null);
      const shouldCommitCompletedAssistantAudio = (
        eventType === OUTPUT_AUDIO_BUFFER_STOPPED_EVENT
        && !replayInProgressRef.current
        && currentAssistantTranscriptRef.current.trim()
        && (!responseCapture?.bufferedOwner || responseCapture.generationDone)
      );
      if (shouldCommitCompletedAssistantAudio) {
        if (!currentAssistantDebugTimestampRef.current) {
          currentAssistantDebugTimestampRef.current = new Date().toISOString();
        }
        commitAssistantDebugTranscript(
          currentAssistantTranscriptRef.current,
          currentAssistantDebugTimestampRef.current,
        );
      }
      assistantOutputAudioActiveRef.current = false;
      if (responseCapture && eventType === OUTPUT_AUDIO_BUFFER_STOPPED_EVENT) {
        const capture = responseCapture;
        narrationAckRef.current(capture);
        if (capture.context.autoFinishAfterResponse) finishAutomaticClosingRef.current(capture);
        window.setTimeout(() => settledPlaybackRef.current(capture), 250);
      }
      window.setTimeout(() => flushAssistantResponsesRef.current(), 0);
      replayInProgressRef.current = false;
      currentAssistantTranscriptRef.current = '';
      currentAssistantDebugTimestampRef.current = null;
      setLiveAssistantDebugEntry(null);
      if (pendingDisconnectAfterResponseRef.current) {
        if (completionClosingResponseDoneRef.current) {
          finishCompletionClosingResponse();
          return;
        }
        setAssistantPhaseValue('responding');
        return;
      }
      if (flushPendingAssistantOnlyResponse()) {
        return;
      }
      flushPendingAssistantOnlySessionUpdate();
      setAssistantPhaseValue('listening');
      return;
    }

    if (ASSISTANT_TRANSCRIPT_DELTA_EVENTS.has(eventType)) {
      setError(null);
      if (replayInProgressRef.current) {
        setAssistantPhaseValue('responding');
        return;
      }
      if (!currentAssistantDebugTimestampRef.current) {
        currentAssistantDebugTimestampRef.current = new Date().toISOString();
      }
      currentAssistantTranscriptRef.current = appendTranscript(
        currentAssistantTranscriptRef.current,
        transcriptValue,
      );
      if (responseCapture) {
        responseCapture.transcript = currentAssistantTranscriptRef.current;
        responseCapture.timestamp = currentAssistantDebugTimestampRef.current;
      }
      setAssistantTranscript(currentAssistantTranscriptRef.current);
      setLiveAssistantDebugEntry({
        id: `live-assistant-${currentAssistantDebugTimestampRef.current}`,
        text: currentAssistantTranscriptRef.current,
        timestamp: currentAssistantDebugTimestampRef.current,
        source: 'realtime',
        ...assistantCaptionIdentity(responseCapture),
        isLive: true,
      });
      setAssistantPhaseValue('responding');
      return;
    }

    if (ASSISTANT_TRANSCRIPT_COMPLETED_EVENTS.has(eventType)) {
      setError(null);
      if (replayInProgressRef.current) {
        currentAssistantTranscriptRef.current = '';
        currentAssistantDebugTimestampRef.current = null;
        setLiveAssistantDebugEntry(null);
        setAssistantPhaseValue('responding');
        return;
      }
      if (!currentAssistantDebugTimestampRef.current) {
        currentAssistantDebugTimestampRef.current = new Date().toISOString();
      }
      currentAssistantTranscriptRef.current = mergeAssistantTranscript(
        currentAssistantTranscriptRef.current,
        transcriptValue,
      );
      if (responseCapture) {
        responseCapture.transcript = currentAssistantTranscriptRef.current;
        responseCapture.timestamp = currentAssistantDebugTimestampRef.current;
      }
      setAssistantTranscript(currentAssistantTranscriptRef.current);
      if (currentAssistantTranscriptRef.current) {
        setLiveAssistantDebugEntry({
          id: `live-assistant-${currentAssistantDebugTimestampRef.current}`,
          text: currentAssistantTranscriptRef.current,
          timestamp: currentAssistantDebugTimestampRef.current,
          source: 'realtime',
          ...assistantCaptionIdentity(responseCapture),
          isLive: true,
        });
      } else {
        setLiveAssistantDebugEntry(null);
      }
      setAssistantPhaseValue('responding');
      return;
    }

    if (eventType === REALTIME_RESPONSE_DONE_EVENT || eventType === 'response.completed') {
      if (responseCapture) {
        responseCapture.generationDone = true;
        narrationAckRef.current(responseCapture);
        if (responseCapture.context.autoFinishAfterResponse) finishAutomaticClosingRef.current(responseCapture);
        window.setTimeout(() => settledPlaybackRef.current(responseCapture as AssistantResponseCapture), 250);
      }
      setError(null);
      if (replayInProgressRef.current) {
        currentAssistantTranscriptRef.current = '';
        currentAssistantDebugTimestampRef.current = null;
        setLiveAssistantDebugEntry(null);
        if (assistantOutputAudioActiveRef.current) {
          setAssistantPhaseValue('responding');
        } else {
          replayInProgressRef.current = false;
          setAssistantPhaseValue('listening');
        }
        return;
      }
      const responseTranscript = getAssistantTranscriptFromResponse(event);
      const finalizedTranscript = mergeAssistantTranscript(
        currentAssistantTranscriptRef.current,
        responseTranscript,
      );
      if (finalizedTranscript) {
        if (!currentAssistantDebugTimestampRef.current) {
          currentAssistantDebugTimestampRef.current = new Date().toISOString();
        }
        currentAssistantTranscriptRef.current = finalizedTranscript;
        setAssistantTranscript(finalizedTranscript);
        commitAssistantDebugTranscript(
          finalizedTranscript,
          currentAssistantDebugTimestampRef.current,
          responseCapture,
        );
      }
      if (pendingDisconnectAfterResponseRef.current) {
        completionClosingResponseDoneRef.current = true;
        if (assistantOutputAudioActiveRef.current) {
          setAssistantPhaseValue('responding');
          return;
        }
        if (assistantOutputAudioSeenForResponseRef.current) {
          finishCompletionClosingResponse();
          return;
        }
        clearCompletionClosingDisconnectFallback();
        completionClosingDisconnectFallbackRef.current = window.setTimeout(() => {
          if (
            pendingDisconnectAfterResponseRef.current
            && completionClosingResponseDoneRef.current
            && !assistantOutputAudioActiveRef.current
          ) {
            finishCompletionClosingResponse();
          }
        }, 1500);
        setAssistantPhaseValue('responding');
        return;
      }
      setAssistantPhaseValue(assistantOutputAudioActiveRef.current ? 'responding' : 'listening');
      return;
    }

    if (eventType === 'error') {
      const errorPayload = event.error;
      // Cancellation can arrive after generation ends. Keep its diagnostic event
      // without disturbing evaluation, newer playback, or an existing error.
      if (errorPayload && typeof errorPayload === 'object'
        && (errorPayload as { code?: unknown }).code === 'response_cancel_not_active') return;
      const errorCapture = activeGenerationRef.current ?? currentAssistantResponseRef.current;
      if (errorCapture && errorCapture.transport !== source) return;
      if (errorPayload && typeof errorPayload === 'object'
        && ['response_already_active', 'conversation_already_has_active_response'].includes(String((errorPayload as { code?: unknown }).code))) {
        const rejected = activeGenerationRef.current;
        const rejectedEventId = (errorPayload as { event_id?: string }).event_id;
        if (rejectedEventId && rejectedEventId !== rejected?.clientEventId) return;
        const request = activeGenerationRequestRef.current;
        const message = getRealtimeErrorMessage(event);
        const providerId = message.match(/\bresp_[\w-]+\b/)?.[0];
        if (rejected && request && (request.overlapRecoveryCount ?? 0) < 1) {
          rejected.cancelled = true;
          pendingAssistantResponseCapturesRef.current = pendingAssistantResponseCapturesRef.current.filter((capture) => capture !== rejected);
          activeGenerationRef.current = null;
          activeGenerationRequestRef.current = null;
          queuedAssistantResponsesRef.current.unshift({ ...request, overlapRecoveryCount: 1 });
          // A rejected create is not a transport failure. Wait for the provider's
          // actual active response to terminate before resending once.
          blockedProviderResponseRef.current = providerId ?? 'unidentified-active-response';
          setError('The guide is finishing its previous response. Your words are preserved.');
          if (generationRecoveryTimeoutRef.current !== null) window.clearTimeout(generationRecoveryTimeoutRef.current);
          generationRecoveryTimeoutRef.current = window.setTimeout(() => {
            generationRecoveryTimeoutRef.current = null;
            if (!blockedProviderResponseRef.current) return;
            setError('The guide did not finish its previous response. Reconnecting with your words preserved.');
            disconnect({ recoverable: true });
            setConnectionState('disconnected');
          }, 10000);
          return;
        }
      }
      const message = getRealtimeErrorMessage(event) || 'Voice assistant reported an error.';
      const rejectedEventId = errorPayload && typeof errorPayload === 'object'
        ? (errorPayload as { event_id?: string }).event_id : undefined;
      if (activeGenerationRef.current && rejectedEventId === activeGenerationRef.current.clientEventId) {
        activeGenerationRef.current.cancelled = true;
        pendingAssistantResponseCapturesRef.current = pendingAssistantResponseCapturesRef.current.filter((capture) => capture !== activeGenerationRef.current);
        activeGenerationRef.current = null;
        activeGenerationRequestRef.current = null;
        if (generationRecoveryTimeoutRef.current !== null) window.clearTimeout(generationRecoveryTimeoutRef.current);
        generationRecoveryTimeoutRef.current = null;
        window.setTimeout(() => flushAssistantResponsesRef.current(), 0);
      }
      pendingDisconnectAfterResponseRef.current = false;
      completionClosingResponseDoneRef.current = false;
      assistantOutputAudioSeenForResponseRef.current = false;
      clearCompletionClosingDisconnectFallback();
      setCompletionClosingPending(false);
      replayInProgressRef.current = false;
      assistantOutputAudioActiveRef.current = false;
      setError(message);
      setAssistantPhaseValue('idle');
    }
  }, [
    beginCompletedTurnAudio,
    bufferedNarrationIsCurrent,
    cancelConnectionNotice,
    captureNativeTurn,
    cancelRealtimeResponse,
    clearCompletionClosingDisconnectFallback,
    commitAssistantDebugTranscript,
    finishCompletionClosingResponse,
    finishCompletedTurnAudio,
    failAudioTranscription,
    disconnect,
    rememberCapturedTranscript,
    flushPendingAssistantOnlyResponse,
    flushPendingAssistantOnlySessionUpdate,
    isAssistantResponseCurrentConnection,
    preserveDraft,
    retireNoSpeechCapture,
    retainRecognizedSpeechPriority,
    pushRealtimeEvent,
    recordUsageEvent,
    session?.effective_transcription_model,
    session?.realtime_model,
    session?.transcription_model,
    session?.id,
    setAssistantPhaseValue,
  ]);
  handleRealtimeEventRef.current = handleRealtimeEvent;

  const connect = useCallback(async () => {
    recoveryContextHoldRef.current = false;
    if (!session?.rtc_endpoint) {
      intentionalDisconnectRef.current = false;
      const failure = rememberConnectionFailure(new Error('The voice connection endpoint is unavailable.'), 'session');
      setError(failure.message);
      setConnectionState('error');
      return;
    }
    if (typeof RTCPeerConnection === 'undefined') {
      intentionalDisconnectRef.current = false;
      const failure = rememberConnectionFailure(new Error('Voice mode is not supported in this browser.'), 'browser');
      setConnectionState('error');
      setError(failure.message);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      intentionalDisconnectRef.current = false;
      const failure = rememberConnectionFailure(new Error('Microphone access is unavailable in this browser.'), 'browser');
      setConnectionState('error');
      setError(failure.message);
      return;
    }

    const recovering = automaticReconnectCountRef.current > 0;
    const connectionHost = activeHostContextRef.current;
    disconnect({ updateState: false, preservePendingHostMessages: true });
    intentionalDisconnectRef.current = false;
    const connectionAttemptId = connectionAttemptIdRef.current + 1;
    connectionAttemptIdRef.current = connectionAttemptId;
    clearLiveTranscript();
    lastPersistedTurnKeyRef.current = '';
    userSpeechGenerationRef.current = 0;
    pendingSpokenMessagesRef.current.forEach((message) => {
      if (message.owner && !message.responseContext) message.owner.speechGeneration = 0;
    });
    userSpeechActiveRef.current = false;
    pendingCompletedTurnsRef.current = [];
    setConnectionState('connecting');
    setIsReconnecting(recovering);
    window.speechSynthesis?.cancel();
    setAssistantPhaseValue('idle');
    setError(null);
    setInputError(null);
    setTurnProcessing(false);

    let setupStage: VoiceConnectionStage = 'session';
    try {
      let activeSession = activeSessionRef.current ?? session;
      if (isRealtimeClientSecretStale(activeSession)) {
        activeSession = await fetchSession(connectionAttemptId);
      }
      if (connectionAttemptIdRef.current !== connectionAttemptId) {
        return;
      }
      if (!activeSession?.client_secret || !activeSession?.rtc_endpoint) {
        throw new Error('Voice mode is not ready to connect.');
      }
      const shouldCreateCompletionClosingResponse = isCompletionClosingDirective(
        activeSession.assistant_turn_directive,
      );
      const canRunActiveTurn = activeSession.status === 'active' && Boolean(activeSession.current_target);
      if (!canRunActiveTurn && !shouldCreateCompletionClosingResponse) {
        setAssistantPhaseValue('idle');
        setConnectionState('idle');
        return;
      }

      setupStage = 'browser';
      const peerConnection = new RTCPeerConnection();
      peerConnectionRef.current = peerConnection;

      const audioElement = document.createElement('audio');
      audioElement.autoplay = true;
      audioElementRef.current = audioElement;
      audioElement.addEventListener('volumechange', () => {
        if (audioElementRef.current === audioElement) narrationPlaybackRef.current?.setVolume(audioElement.volume, audioElement.muted);
        const closing = currentAssistantResponseRef.current;
        if (audioElementRef.current === audioElement && closing?.context.autoFinishAfterResponse
          && closing.audioStarted && !closing.audioStopped && (audioElement.muted || audioElement.volume === 0)) {
          closing.playbackMuted = true;
        }
      });
      if (supportsBufferedNarration(activeSession)) {
        const outputController = new AbortController();
        narrationTransportControllerRef.current = outputController;
        const ownsOutput = () => connectionAttemptIdRef.current === connectionAttemptId
          && activeHostContextRef.current === connectionHost && !outputController.signal.aborted;
        const narrationSession = activeSession;
        const narrationHost = context ?? { kind: 'flow_run' as const, id: flowRunId as number };
        void (async () => {
          // Provider credentials can be single-use across transport types. A
          // separate GET mints output-only credentials without hydrating its
          // controls, target, safety state, captions, or pending learner work.
          const credentialTimeout = window.setTimeout(() => {
            if (ownsOutput()) failNarrationTransportRef.current(new Error('Voice narration could not prepare its connection. Your progress is preserved. Please reconnect.'));
            else outputController.abort();
          }, 5000);
          const clearCredentialTimeout = () => window.clearTimeout(credentialTimeout);
          outputController.signal.addEventListener('abort', clearCredentialTimeout, { once: true });
          let credentialSession: VoiceFlowSessionBootstrap;
          try {
            const credentialResponse = await voiceFlow.getNarrationConnection(flowName, context ?? (flowRunId as number), {
              signal: outputController.signal,
            });
            credentialSession = credentialResponse.data;
          } finally {
            clearCredentialTimeout();
            outputController.signal.removeEventListener('abort', clearCredentialTimeout);
          }
          if (!ownsOutput()) return null;
          const outputSecret = credentialSession.client_secret?.trim();
          if (!isNarrationConnectionIdentityCurrent(credentialSession, narrationSession, flowName, narrationHost)
            || !isNarrationConnectionIdentityCurrent(activeSessionRef.current, narrationSession, flowName, narrationHost)
            || !outputSecret || outputSecret === narrationSession.client_secret?.trim()
            || isRealtimeClientSecretStale(credentialSession)) {
            throw new Error('Voice narration connection details changed. Your progress is preserved. Please reconnect.');
          }
          return createNarrationRealtimeTransport({
            clientSecret: outputSecret,
            model: narrationSession.realtime_model,
            voice: narrationSession.voice,
            signal: outputController.signal,
            onEvent: (event) => { if (ownsOutput()) handleRealtimeEventRef.current?.(event, 'signed_ws'); },
            onError: (failure) => { if (ownsOutput()) failNarrationTransportRef.current(failure); },
          });
        })().then((transport) => {
          if (!transport) return;
          if (!ownsOutput()) { transport.close(); return; }
          if (!isNarrationConnectionIdentityCurrent(activeSessionRef.current, narrationSession, flowName, narrationHost)) {
            transport.close();
            failNarrationTransportRef.current(new Error('Voice narration connection details changed. Your progress is preserved. Please reconnect.'));
            return;
          }
          narrationTransportRef.current = transport;
          resetRecoveryWhenReady();
          flushAssistantResponsesRef.current();
        }).catch(() => {
          if (ownsOutput()) failNarrationTransportRef.current(new Error('Voice narration could not connect. Your progress is preserved. Please reconnect.'));
        });
      }
      peerConnection.ontrack = (peerEvent) => {
        if (connectionAttemptIdRef.current !== connectionAttemptId || activeHostContextRef.current !== connectionHost
          || audioElementRef.current !== audioElement) return;
        if (supportsBufferedNarration(activeSession)) {
          if (narrationPlaybackControllerRef.current) return;
          const playbackController = new AbortController();
          narrationPlaybackControllerRef.current = playbackController;
          const ownsPlayback = () => connectionAttemptIdRef.current === connectionAttemptId
            && activeHostContextRef.current === connectionHost
            && audioElementRef.current === audioElement && !playbackController.signal.aborted;
          const failPlayback = (failure: Error) => {
            if (!ownsPlayback()) return;
            failNarrationTransportRef.current(failure);
          };
          void createBufferedNarrationPlayback(peerEvent.streams[0], {
            signal: playbackController.signal,
            sampleRate: 24000,
            onStarted: (owner) => {
              const capture = bufferedNarrationCaptureRef.current;
              if (!ownsPlayback() || !capture || capture.bufferedOwner !== owner || !bufferedNarrationIsCurrent(capture)) return;
              capture.audioStarted = true;
              capture.audioStartedOwned = true;
              browserAudioPlayableRef.current = true;
              assistantOutputAudioActiveRef.current = true;
              assistantOutputAudioSeenForResponseRef.current = true;
              speechGroupRef.current = null;
              if (!capture.bufferedPaused) setAssistantPhaseValue('responding');
            },
            onDrained: (owner) => {
              const capture = bufferedNarrationCaptureRef.current;
              if (!ownsPlayback() || !capture || capture.bufferedOwner !== owner
                || !capture.providerAudioEnded || !bufferedNarrationIsCurrent(capture)) return;
              // The processor may finish before a VAD task enqueues its pause,
              // with this port message delivered afterward. Its owned drain is
              // evidence of full playback; learner work still fences the receipt.
              capture.bufferedPaused = false;
              capture.bufferedLocallyDrained = true;
              handleRealtimeEventRef.current?.({ type: OUTPUT_AUDIO_BUFFER_STOPPED_EVENT,
                response_id: capture.providerResponseId, local_narration_drain: owner }, capture.transport);
              if (bufferedNarrationCaptureRef.current === capture) bufferedNarrationCaptureRef.current = null;
            },
            onError: failPlayback,
          }).then((player) => {
            if (!ownsPlayback()) { void player.close(); return; }
            if (narrationPlaybackStartupTimerRef.current !== null) window.clearTimeout(narrationPlaybackStartupTimerRef.current);
            narrationPlaybackStartupTimerRef.current = null;
            narrationPlaybackRef.current = player;
            resetRecoveryWhenReady();
            player.setVolume(audioElement.volume, audioElement.muted);
            browserAudioPlayableRef.current = player.isRunning();
            flushAssistantResponsesRef.current();
          }).catch(failPlayback);
          return;
        }
        audioElement.srcObject = peerEvent.streams[0];
        void audioElement.play()?.then(() => {
          if (connectionAttemptIdRef.current === connectionAttemptId && audioElementRef.current === audioElement) browserAudioPlayableRef.current = true;
        }).catch(() => {
          if (connectionAttemptIdRef.current !== connectionAttemptId || audioElementRef.current !== audioElement) return;
          browserAudioPlayableRef.current = false;
          setError('Audio playback was blocked. Enable audio playback to continue listening.');
        });
      };

      setupStage = 'microphone';
      const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (connectionAttemptIdRef.current !== connectionAttemptId) {
        mediaStream.getTracks().forEach((track) => track.stop());
        return;
      }
      localStreamRef.current = mediaStream;
      setMicPermission('granted');
      if (activeSession.transcription_transport === 'completed_turn_upload') {
        setupStage = 'capture';
        const captureController = new AbortController();
        transcriptionControllersRef.current.add(captureController);
        try {
          const capture = await createCompletedTurnAudioCapture(mediaStream,
            (failure) => failAudioTranscription(failure, captureTurnRef.current ?? undefined, false), captureController.signal);
          if (connectionAttemptIdRef.current !== connectionAttemptId) { capture.close(); return; }
          completedAudioCaptureRef.current = capture;
        } finally { transcriptionControllersRef.current.delete(captureController); }
      }
      setIsMuted(false);
      mediaStream.getTracks().forEach((track) => {
        track.enabled = true;
        peerConnection.addTrack(track, mediaStream);
      });

      setupStage = 'browser';
      const dataChannel = peerConnection.createDataChannel('oai-events');
      dataChannelRef.current = dataChannel;
      dataChannel.addEventListener('open', () => {
        if (connectionAttemptIdRef.current !== connectionAttemptId || activeHostContextRef.current !== connectionHost) {
          return;
        }
        resetRecoveryWhenReady();
        setError(null);
        setConnectionState('connected');
        if (supportsBufferedNarration(activeSession) && !narrationPlaybackRef.current && !narrationPlaybackFailedRef.current) {
          narrationPlaybackStartupTimerRef.current = window.setTimeout(() => {
            narrationPlaybackStartupTimerRef.current = null;
            if (connectionAttemptIdRef.current !== connectionAttemptId || activeHostContextRef.current !== connectionHost
              || narrationPlaybackRef.current) return;
            failNarrationTransportRef.current(new Error('Voice narration audio did not start. Your progress is preserved. Please reconnect to continue.'));
          }, 5000);
        }
        if ((shouldCreateCompletionClosingResponse || hasPendingSignedNarration(activeSession))
          && activeSession.ai_safety?.status === 'pending') {
          scheduleAiSafetyPolling(activeSession.ai_safety.turn_id);
        }
        const connectionNotice = pendingConnectionNoticeRef.current;
        if (connectionNotice) {
          pendingConnectionNoticeRef.current = null;
          if (connectionNotice.questionId === undefined || connectionNotice.questionId === (activeSession.current_question?.id ?? null)) {
            const noticeSession = { ...activeSession, current_target: null, narration_token: null,
              narration_text: null, command_action: connectionNotice.commandAction,
              spoken_response_mode: connectionNotice.spokenResponseMode };
            const responseContext = makeAssistantResponseContext(noticeSession, connectionNotice.purpose);
            const closingFailure = connectionNotice.purpose === 'transcription_failure'
              && connectionNotice.resumeAutomaticClosing === true && allowsAutomaticCompletion(activeSession);
            const activityFailure = ['transcription_failure', 'save_failure'].includes(connectionNotice.purpose)
              && (voiceRecoveryIsActivity(activeSession) || closingFailure);
            if (activityFailure) activityRecoveryNoticeRef.current = { noticeContext: responseContext,
              activityContext: makeAssistantResponseContext(activeSession, closingFailure ? activeSession.assistant_turn_directive?.response_purpose : undefined),
              speechGeneration: userSpeechGenerationRef.current };
            sendRealtimeResponse(activityFailure ? conversationalVoiceRecoveryInstructions(closingFailure ? JSON.stringify({ activity_type: 'module_closing' })
              : voiceRecoveryActivitySource(activeSession), true,
              { personality: activeSession.voice_personality, responseLength: activeSession.voice_response_length })
              : connectionNotice.instructions, { responseSession: noticeSession, responseContext, responsePurpose: connectionNotice.purpose });
            return;
          }
        }
        const recovered = restoreVoiceDraft(activeSession);
        if (recovered) {
          const snapshot: CompletedTurnSnapshot = {
            questionId: recovered.questionId, targetContentId: recovered.contentId,
            targetRevision: recovered.targetRevision,
            userTranscript: recovered.transcript, assistantTranscript: '',
            inputAudioItemId: recovered.inputAudioItemId,
            speechGeneration: userSpeechGenerationRef.current,
            connectionAttemptId, confirmation: recovered.confirmation,
            runtimeGeneration: recovered.runtimeGeneration, transcriptionModel: recovered.transcriptionModel,
            capturedAt: recovered.capturedAt,
          };
          rememberCapturedTranscript(snapshot, activeSession.id);
          if (recovered.finalized === false || recovered.retryable === false
            || (recovered.recoveryAttempts ?? 0) >= 1
            || attemptedRecoveredCapturesRef.current.has(recovered.inputAudioItemId)) {
            currentUserTranscriptRef.current = recovered.transcript;
            setUserTranscript(recovered.transcript);
            setLiveUserDebugEntry(null);
            setError(recovered.finalized === false
              ? 'The last audio capture was incomplete. Say your answer again; no incomplete transcript was saved as an answer.'
              : 'Your previous words are preserved. Please say a new answer or ask to repeat the question.');
            const noticeSession = { ...activeSession, spoken_response_mode: 'conversational' as const, command_action: null,
              current_target: null, narration_token: null, narration_text: null };
            const responseContext = makeAssistantResponseContext(noticeSession, 'save_failure');
            if (voiceRecoveryIsActivity(activeSession)) activityRecoveryNoticeRef.current = { noticeContext: responseContext,
              activityContext: makeAssistantResponseContext(activeSession), speechGeneration: userSpeechGenerationRef.current };
            sendRealtimeResponse(voiceSaveRecoveryInstructions(activeSession), { responsePurpose: 'save_failure',
              responseSession: noticeSession, responseContext });
            return;
          }
          attemptedRecoveredCapturesRef.current.add(recovered.inputAudioItemId);
          storeVoiceDraft({ ...recovered, recoveryAttempts: (recovered.recoveryAttempts ?? 0) + 1 });
          lastUnsavedTurnRef.current = snapshot;
          void processCompletedTurnRef.current?.(snapshot);
          return;
        }
        if (pendingSpokenMessagesRef.current.length) {
          flushSpokenMessagesRef.current();
          return;
        }
        if (activeSession.instruction_block) {
          if (shouldCreateCompletionClosingResponse) {
            sendRealtimeResponse(getSpokenReplyInstructions(activeSession), {
              responsePurpose: activeSession.assistant_turn_directive?.response_purpose,
              disconnectAfterResponse: activeSession.assistant_turn_directive?.disconnect_after_response,
              responseSession: activeSession,
            });
          } else if (activeSession.content_pending || activeSession.current_target?.content_pending
            || (activeSession.current_target?.question_type === 'roleplay' && learnerTranscriptHandlerRef.current)) {
            setAssistantPhaseValue('idle');
          } else if (shouldCreateOpeningResponseRef.current
            // A saved caption establishes text history, not delivery. A fresh
            // connection still owes the current incomplete signed passage.
            || (!capturePausedRef.current && hasPendingSignedNarration(activeSession))) {
            sendRealtimeResponse(getSpokenReplyInstructions(activeSession), { resetUser: true, responseSession: activeSession });
          } else {
            sendRealtimeSessionUpdate(activeSession.instruction_block);
            setAssistantPhaseValue('listening');
          }
        }
      });
      dataChannel.addEventListener('close', () => {
        if (connectionAttemptIdRef.current !== connectionAttemptId) {
          return;
        }
        disconnect({ recoverable: !narrationPlaybackFailedRef.current });
        setAssistantPhaseValue('idle');
        setConnectionState('disconnected');
      });
      dataChannel.addEventListener('message', (messageEvent) => {
        if (connectionAttemptIdRef.current !== connectionAttemptId) {
          return;
        }
        try {
          const event = JSON.parse(messageEvent.data) as Record<string, unknown>;
          handleRealtimeEventRef.current?.(event);
        } catch (parseError) {
          console.error('Failed to parse realtime event', parseError);
        }
      });
      peerConnection.onconnectionstatechange = () => {
        if (connectionAttemptIdRef.current !== connectionAttemptId) return;
        if (peerConnection.connectionState === 'failed' || peerConnection.connectionState === 'disconnected') {
          disconnect({ recoverable: !narrationPlaybackFailedRef.current });
        }
      };

      setupStage = 'offer';
      const offer = await peerConnection.createOffer();
      if (connectionAttemptIdRef.current !== connectionAttemptId) return;
      await peerConnection.setLocalDescription(offer);
      if (connectionAttemptIdRef.current !== connectionAttemptId) return;

      setupStage = 'signaling';
      const response = await fetch(activeSession.rtc_endpoint, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${activeSession.client_secret}`,
          'Content-Type': 'application/sdp',
        },
      });
      if (!response.ok) {
        throw new VoiceConnectionHttpError(response.status);
      }

      const answer = {
        type: 'answer' as const,
        sdp: await response.text(),
      };
      if (connectionAttemptIdRef.current !== connectionAttemptId) {
        return;
      }
      setupStage = 'answer';
      await peerConnection.setRemoteDescription(answer);
    } catch (connectError) {
      if (connectionAttemptIdRef.current !== connectionAttemptId) {
        return;
      }
      const described = describeVoiceConnectionFailure(connectError, setupStage);
      const retrySetup = described.retryable && automaticReconnectCountRef.current < 3;
      disconnect({ updateState: false, recoverable: true, preservePendingHostMessages: retrySetup,
        connectionFailure: { error: connectError, stage: setupStage } });
      setIsReconnecting(retrySetup);
      if (described.microphoneDenied) {
        setMicPermission('denied');
      }
      setError(described.message);
      setAssistantPhaseValue('idle');
      setConnectionState('error');
    }
  }, [
    clearLiveTranscript,
    context,
    disconnect,
    fetchSession,
    flowName,
    flowRunId,
    failAudioTranscription,
    bufferedNarrationIsCurrent,
    makeAssistantResponseContext,
    rememberConnectionFailure,
    rememberCapturedTranscript,
    resetRecoveryWhenReady,
    sendRealtimeResponse,
    sendRealtimeSessionUpdate,
    scheduleAiSafetyPolling,
    session,
    setAssistantPhaseValue,
  ]);

  useEffect(() => {
    if (
      connectionState === 'idle'
      && !recoveryContextHoldRef.current
      && session?.client_secret
      && (
        (session?.status === 'active' && session?.current_target)
        || isCompletionClosingDirective(session?.assistant_turn_directive)
      )
    ) {
      void connect();
    }
  }, [connect, connectionState, session]);

  useEffect(() => {
    if (
      session?.status
      && session.status !== 'active'
      && connectionState === 'connected'
      && !completionClosingPending
      && !pendingDisconnectAfterResponseRef.current
    ) {
      disconnect();
    }
  }, [completionClosingPending, connectionState, disconnect, session?.status]);

  const reconnect = useCallback(async () => {
    const explicitlyRetryNarration = narrationPlaybackFailedRef.current;
    recoveryContextHoldRef.current = false;
    automaticReconnectCountRef.current = 0;
    reconnectFailureAnnouncedRef.current = false;
    disconnect();
    intentionalDisconnectRef.current = false;
    const attempt = connectionAttemptIdRef.current;
    const revision = connectionRecoveryRevisionRef.current;
    const host = activeHostContextRef.current;
    const currentContext = connectionRecoveryContext(activeSessionRef.current);
    const ownsReconnect = () => attempt === connectionAttemptIdRef.current
      && revision === connectionRecoveryRevisionRef.current && !intentionalDisconnectRef.current
      && host === activeHostContextRef.current
      && currentContext === connectionRecoveryContext(activeSessionRef.current);
    setConnectionState('connecting');
    setError(null);
    try {
      const next = await fetchSession(attempt, ownsReconnect);
      if (ownsReconnect() && explicitlyRetryNarration && (next.narration_token ?? next.current_target?.narration_token)) {
        // An explicit reconnect may retry an unheard passage. Caption history
        // alone never proves it played; automatic reconnect does not set this.
        shouldCreateOpeningResponseRef.current = true;
        lastRequestedQuestionRef.current = undefined;
        lastRequestedTargetRevisionRef.current = undefined;
      }
      if (attempt === connectionAttemptIdRef.current && revision === connectionRecoveryRevisionRef.current
        && !intentionalDisconnectRef.current
        && host === activeHostContextRef.current
        && connectionRecoveryContext(next) === connectionRecoveryContext(activeSessionRef.current)) setConnectionState('idle');
    } catch (failure) {
      if (!ownsReconnect()) return;
      const described = rememberConnectionFailure(failure, 'session');
      setError(described.message);
      setConnectionState('error');
    }
  }, [disconnect, fetchSession, rememberConnectionFailure]);

  const recoveryContext = connectionRecoveryContext(session);
  const recoveryWithoutSession = session === null;
  useEffect(() => {
    const wait = closingSafetyWaitRef.current;
    if ((aiSafetyPollOwnershipRef.current && !aiSafetyPollOwnershipRef.current())
      || (aiSafetyClosingScopeRef.current && !aiSafetyClosingScopeRef.current.isCurrent())) stopAiSafetyPolling();
    if (wait?.notice === connectionNoticeRef.current && !wait?.isCurrent()) cancelConnectionNotice();
    flushClosingSafetyNoticeRef.current();
  }, [cancelConnectionNotice, connectionState, hostContext, recoveryContext, session?.ai_safety?.status,
    session?.ai_safety?.turn_id, stopAiSafetyPolling, uiMode]);
  useEffect(() => {
    const failure = connectionFailureRef.current;
    if ((failure && (failure.context !== recoveryContext || failure.host !== hostContext)) || uiMode === 'crisis') invalidateConnectionRecovery();
  }, [hostContext, invalidateConnectionRecovery, recoveryContext, uiMode]);

  useEffect(() => {
    const failure = connectionFailureRef.current;
    if ((connectionState !== 'error' && connectionState !== 'disconnected')
      || intentionalDisconnectRef.current
      || (session?.status !== 'active' && !completionClosingPending && !(recoveryWithoutSession && failure?.stage === 'session'))
      || uiMode === 'crisis') return;
    if (!failure || failure.context !== recoveryContext) return;
    const ownsRecovery = () => connectionFailureRef.current === failure
      && failure.attempt === connectionAttemptIdRef.current && failure.revision === connectionRecoveryRevisionRef.current
      && failure.host === activeHostContextRef.current
      && failure.context === connectionRecoveryContext(activeSessionRef.current) && !intentionalDisconnectRef.current;
    if (!ownsRecovery()) return;
    if (!failure.retryable || micPermission === 'denied' || automaticReconnectCountRef.current >= 3) {
      setIsReconnecting(false);
      if (!reconnectFailureAnnouncedRef.current) {
        reconnectFailureAnnouncedRef.current = true;
        if (typeof window.speechSynthesis !== 'undefined' && typeof SpeechSynthesisUtterance !== 'undefined') {
          cancelConnectionNotice();
          const notice = new SpeechSynthesisUtterance(`Voice Mode could not connect. ${failure.recoveryHint} Your saved progress is safe.`);
          notice.lang = 'en-US';
          const release = () => { if (connectionNoticeRef.current === notice) connectionNoticeRef.current = null; };
          notice.onend = release;
          notice.onerror = release;
          notice.onstart = () => { if (connectionNoticeRef.current === notice && !ownsRecovery()) cancelConnectionNotice(); };
          connectionNoticeRef.current = notice;
          window.speechSynthesis.speak(notice);
        }
      }
      return;
    }
    setIsReconnecting(true);
    const delay = [1000, 2000, 4000][automaticReconnectCountRef.current];
    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      if (!ownsRecovery()) return;
      automaticReconnectCountRef.current += 1;
      void fetchSession(failure.attempt, ownsRecovery, (next) => {
        // This request still owned the prior context when its result was applied.
        // Keep that ownership through the canonical update's passive effects.
        failure.context = connectionRecoveryContext(next);
      }).then((next) => {
        // A fresh canonical result can legitimately advance the target. The
        // pre-apply guard prevents an older request overwriting newer controls.
        if (connectionFailureRef.current === failure && failure.attempt === connectionAttemptIdRef.current
          && failure.revision === connectionRecoveryRevisionRef.current && !intentionalDisconnectRef.current
          && failure.host === activeHostContextRef.current
          && connectionRecoveryContext(next) === connectionRecoveryContext(activeSessionRef.current)) {
          connectionFailureRef.current = null;
          setConnectionState('idle');
        }
      }).catch((refreshError) => {
        if (!ownsRecovery()) return;
        const described = rememberConnectionFailure(refreshError, 'session');
        setConnectionState('error');
        setError(described.message);
        setReconnectRevision((previous) => previous + 1);
      });
    }, delay);
    return () => {
      if (reconnectTimerRef.current !== null) window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    };
  }, [cancelConnectionNotice, completionClosingPending, connectionState, fetchSession, hostContext, micPermission, reconnectRevision,
    recoveryContext, recoveryWithoutSession, rememberConnectionFailure, session?.status, uiMode]);

  useEffect(() => {
    if (connectionState !== 'connected' || turnProcessing || !session || !currentTarget
      || !canPrepareGeneratedContent(session) || capturePausedRef.current
      || isPersistingTurnRef.current || pendingTranscriptionCountRef.current || userSpeechActiveRef.current
      || pendingCompletedTurnsRef.current.length || transcribedTurnsRef.current.length) return;
    const key = `${session.id}:${session.runtime_generation ?? 0}:${currentTarget.question_id}:${connectionAttemptIdRef.current}`;
    if (preparedContentRef.current?.key === key) return;
    const preparation = { key, status: 'pending' as 'pending' | 'failed' | 'complete' };
    preparedContentRef.current = preparation;
    const attempt = connectionAttemptIdRef.current;
    const intent = contentPreparationIntentRef.current;
    const speechGeneration = userSpeechGenerationRef.current;
    setTurnProcessing(true);
    void executeCommand('prepare_content').then((prepared) => {
      preparation.status = prepared ? 'complete' : 'failed';
      const active = activeSessionRef.current;
      if (prepared) return;
      if (!active || attempt !== connectionAttemptIdRef.current
        || contentPreparationIntentRef.current !== intent || userSpeechGenerationRef.current !== speechGeneration
        || active.id !== session.id || (active.runtime_generation ?? 0) !== (session.runtime_generation ?? 0)
        || active.current_question?.id !== currentTarget.question_id
        || !canPrepareGeneratedContent(active) || capturePausedRef.current) {
        // A newer intent owns recovery. Once it settles, one eligible fetch can
        // use the server's cached result; an old attempt must not latch it out.
        if (preparedContentRef.current === preparation) {
          preparedContentRef.current = null;
          setContentPreparationRevision((revision) => revision + 1);
        }
        return;
      }
      const notice = { ...active, spoken_response_mode: 'conversational' as const,
        narration_token: null, narration_text: null, current_target: null };
      sendRealtimeResponse(voiceGeneratedContentFailureInstructions(active), {
        responseSession: notice, responsePurpose: 'content_preparation_failure',
      });
    }).finally(() => {
      if ((preparedContentRef.current === preparation || preparedContentRef.current === null)
        && attempt === connectionAttemptIdRef.current
        && !isPersistingTurnRef.current) setTurnProcessing(false);
    });
  }, [connectionState, contentPreparationRevision, currentTarget, executeCommand, sendRealtimeResponse, session, turnProcessing]);

  const flushPendingDraft = useCallback(async (): Promise<boolean> => {
    if (captureTurnRef.current) {
      userSpeechActiveRef.current = false;
      finishCompletedTurnAudio(captureTurnRef.current.inputAudioItemId);
    }
    const deadline = Date.now() + 5000;
    while ((pendingTranscriptionCountRef.current || isPersistingTurnRef.current
      || pendingCompletedTurnsRef.current.length || transcribedTurnsRef.current.length) && Date.now() < deadline) {
      flushTranscribedTurnsRef.current();
      await new Promise<void>((resolve) => window.setTimeout(resolve, 50));
    }
    const pending = lastUnsavedTurnRef.current;
    if (pending && !isPersistingTurnRef.current && Date.now() < deadline) {
      await processCompletedTurnRef.current?.({ ...pending, connectionAttemptId: connectionAttemptIdRef.current });
    }
    preserveDraft();
    const saved = !pendingTranscriptionCountRef.current && !isPersistingTurnRef.current
      && !pendingCompletedTurnsRef.current.length && !transcribedTurnsRef.current.length && !lastUnsavedTurnRef.current;
    if (!saved) setError('Your latest words are still waiting to save. Please reconnect before leaving this conversation.');
    return saved;
  }, [finishCompletedTurnAudio, preserveDraft]);

  const disconnectAsync = useCallback(async () => {
    if (!await flushPendingDraft()) throw new Error('Unsaved voice capture could not be flushed.');
    disconnect();
  }, [disconnect, flushPendingDraft]);

  const ownsSpokenHostMessage = useCallback((message: SpokenHostMessage) => {
    const owner = message.owner;
    if (!owner) return true;
    const active = activeSessionRef.current;
    const interrupted = interruptedPresentationRef.current;
    return Boolean(active && active.status === 'active' && getBootstrapUiMode(active) !== 'crisis'
      && !active.roleplay_safety_status && !capturePausedRef.current
      && owner.host === activeHostContextRef.current && owner.sessionId === active.id
      && owner.questionId === (active.current_question?.id ?? null)
      && owner.targetRevision === (active.current_target?.target_revision ?? undefined)
      && owner.generation === (active.runtime_generation ?? 0)
      && (owner.speechGeneration === userSpeechGenerationRef.current
        || (interrupted?.bufferedCapture && interrupted.bufferedCapture.context === message.responseContext
          && interrupted.speechGeneration === userSpeechGenerationRef.current
          && owner.speechGeneration < userSpeechGenerationRef.current))
      && owner.intent === contentPreparationIntentRef.current);
  }, []);
  ownsSpokenHostMessageRef.current = ownsSpokenHostMessage;

  const flushSpokenMessages = useCallback(() => {
    const active = activeSessionRef.current;
    if (!active || dataChannelRef.current?.readyState !== 'open' || activeSpokenMessageRef.current) return;
    if (active.ai_safety?.status === 'pending') return;
    let message = pendingSpokenMessagesRef.current.shift();
    while (message && !ownsSpokenHostMessage(message)) {
      message.onInterrupted?.();
      message = pendingSpokenMessagesRef.current.shift();
    }
    if (!message) return;
    if (assistantOutputAudioActiveRef.current || assistantPhaseRef.current === 'responding') cancelRealtimeResponse();
    activeSpokenMessageRef.current = message;
    const responseSession: AssistantResponseSession = { ...active,
      spoken_response_mode: message.owner ? 'conversational' : 'exact',
      narration_token: null, narration_text: null, current_target: null, command_action: null };
    const responseContext = makeAssistantResponseContext(responseSession, 'roleplay_host');
    message.responseContext = responseContext;
    const started = sendRealtimeResponse(message.owner ? message.text : exactVoicePassageInstructions(message.text), {
      responsePurpose: 'roleplay_host',
      responseSession, responseContext,
    });
    if (!started) {
      activeSpokenMessageRef.current = null;
      pendingSpokenMessagesRef.current.unshift(message);
    }
  }, [cancelRealtimeResponse, makeAssistantResponseContext, ownsSpokenHostMessage, sendRealtimeResponse]);
  flushSpokenMessagesRef.current = flushSpokenMessages;

  finishAutomaticClosingRef.current = (capture) => {
    const clearTimer = () => {
      if (capture.closingSettleTimer !== undefined) window.clearTimeout(capture.closingSettleTimer);
      capture.closingSettleTimer = undefined;
    };
    const active = activeSessionRef.current;
    if (!capture.context.autoFinishAfterResponse || capture.context.responsePurpose !== 'completion_closing'
      || capture.automaticFinishSuperseded || capture.cancelled || capture.replay || capture.playbackMuted
      || currentAssistantResponseRef.current !== capture || !isAssistantResponseCurrentConnection(capture.context)
      || !allowsAutomaticCompletion(active) || capturePausedRef.current || !browserAudioPlayableRef.current
      || audioElementRef.current?.muted || audioElementRef.current?.volume === 0) {
      const ownsCompletionState = currentAssistantResponseRef.current === capture
        && (!pendingAutomaticClosingRef.current || pendingAutomaticClosingRef.current === capture);
      clearTimer();
      if (pendingAutomaticClosingRef.current === capture) pendingAutomaticClosingRef.current = null;
      if (capture.automaticFinishReady) {
        capture.automaticFinishReady = false;
        if (ownsCompletionState) {
          setAutomaticCompletionReady(false);
          setTerminalActionReady(null);
        }
      }
      return false;
    }
    if (!capture.generationDone || capture.generationSucceeded !== true || !capture.audioStarted || !capture.audioStopped
      || !capture.audioStartedOwned || !capture.audioStoppedOwned) return false;
    pendingAutomaticClosingRef.current = capture;
    const remaining = (capture.closingReadyAt ?? Date.now() + 250) - Date.now();
    if (remaining > 0) {
      if (capture.closingSettleTimer === undefined) capture.closingSettleTimer = window.setTimeout(() => {
        capture.closingSettleTimer = undefined;
        finishAutomaticClosingRef.current(capture);
      }, remaining);
      return true;
    }
    clearTimer();
    if (active?.ai_safety?.status === 'pending') {
      const key = JSON.stringify([activeHostContextRef.current, connectionAttemptIdRef.current, active.id,
        active.runtime_generation ?? 0, active.ai_safety.turn_id, connectionRecoveryContext(active)]);
      if (aiSafetyPollStartedAtRef.current === null
        && !(closingSafetyWaitRef.current?.key === key && closingSafetyWaitRef.current.expired)) {
        scheduleAiSafetyPolling(active.ai_safety.turn_id);
      }
      return false;
    }
    if (userSpeechActiveRef.current || captureTurnRef.current || pendingTranscriptionCountRef.current
      || streamingTurnContextsRef.current.size || isPersistingTurnRef.current || lastUnsavedTurnRef.current
      || transcribedTurnsRef.current.length || pendingCompletedTurnsRef.current.length
      || activeGenerationRef.current || blockedProviderResponseRef.current || assistantOutputAudioActiveRef.current
      || queuedAssistantResponsesRef.current.length || pendingAssistantOnlyResponseRef.current
      || pendingSpokenMessagesRef.current.length || activeSpokenMessageRef.current) return false;
    if (!capture.automaticFinishReady) {
      capture.automaticFinishReady = true;
      setAutomaticCompletionReady(true);
      setTerminalActionReady('finish');
    }
    return true;
  };

  useEffect(() => {
    const closing = pendingAutomaticClosingRef.current;
    if (closing) finishAutomaticClosingRef.current(closing);
  }, [session]);

  settledPlaybackRef.current = (capture) => {
    if (capture.context.autoFinishAfterResponse) {
      finishAutomaticClosingRef.current(capture);
      return;
    }
    const hostMessage = activeSpokenMessageRef.current;
    if (hostMessage?.owner && hostMessage.responseContext === capture.context && capture.generationDone
      && (capture.playbackMuted || capture.cancelled || capture.replay || !browserAudioPlayableRef.current
        || !isAssistantResponseCurrentConnection(capture.context))) {
      activeSpokenMessageRef.current = null;
      hostMessage.onInterrupted?.();
      return;
    }
    if (!capture.generationDone || !capture.audioStarted || !capture.audioStopped
      || capture.playbackMuted || capture.cancelled || capture.replay || !browserAudioPlayableRef.current
      || !isAssistantResponseCurrentConnection(capture.context)) return;
    if (capture.generationSucceeded && capture.audioStartedOwned && capture.audioStoppedOwned) resetRecoveryWhenReady(true);
    const recovery = activityRecoveryNoticeRef.current;
    if (recovery && recovery.noticeContext === capture.context) {
      activityRecoveryNoticeRef.current = null;
      interruptedPresentationRef.current = { context: recovery.activityContext,
        speechGeneration: userSpeechGenerationRef.current, noSpeechSeen: true };
      flushAssistantResponsesRef.current();
    }
    if (capture.context.commandAction === 'finish' || capture.context.commandAction === 'leave') {
      setCompletionClosingPending(false);
      setTerminalActionReady(capture.context.commandAction);
    }
    if (capture.context.responsePurpose === 'roleplay_host' && activeSpokenMessageRef.current) {
      const delivered = activeSpokenMessageRef.current;
      if (delivered.responseContext !== capture.context) return;
      if (capture.bufferedOwner && (activeSessionRef.current?.ai_safety?.status === 'pending'
        || userSpeechActiveRef.current || captureTurnRef.current || pendingTranscriptionCountRef.current
        || streamingTurnContextsRef.current.size || isPersistingTurnRef.current || lastUnsavedTurnRef.current
        || transcribedTurnsRef.current.length || pendingCompletedTurnsRef.current.length)) return;
      activeSpokenMessageRef.current = null;
      if (delivered.owner && (!ownsSpokenHostMessage(delivered) || !capture.generationSucceeded
        || !capture.audioStartedOwned || !capture.audioStoppedOwned
        || activeSessionRef.current?.ai_safety?.status === 'pending'
        || userSpeechActiveRef.current || captureTurnRef.current || pendingTranscriptionCountRef.current
        || streamingTurnContextsRef.current.size || isPersistingTurnRef.current || lastUnsavedTurnRef.current
        || transcribedTurnsRef.current.length || pendingCompletedTurnsRef.current.length)) delivered.onInterrupted?.();
      else delivered.resolve();
      flushSpokenMessagesRef.current();
    }
  };

  const speak = useCallback((message: string): Promise<void> => {
    if (!message.trim()) return Promise.resolve();
    return new Promise<void>((resolve, reject) => {
      pendingSpokenMessagesRef.current.push({ text: message, resolve, reject });
      flushSpokenMessagesRef.current();
    });
  }, []);

  const speakConversational = useCallback((instructions: string): Promise<boolean> => {
    const active = activeSessionRef.current;
    if (!instructions.trim() || !active) return Promise.resolve(false);
    return new Promise<boolean>((resolve, reject) => {
      pendingSpokenMessagesRef.current.push({ text: instructions, resolve: () => resolve(true), reject,
        onInterrupted: () => resolve(false),
        owner: { host: activeHostContextRef.current, sessionId: active.id,
          questionId: active.current_question?.id ?? null, targetRevision: active.current_target?.target_revision ?? undefined,
          generation: active.runtime_generation ?? 0,
          speechGeneration: userSpeechGenerationRef.current, intent: contentPreparationIntentRef.current } });
      flushSpokenMessagesRef.current();
    });
  }, []);

  useEffect(() => {
    const active = activeSpokenMessageRef.current;
    if (active?.owner && !ownsSpokenHostMessage(active)) {
      activeSpokenMessageRef.current = null;
      active.onInterrupted?.();
      queuedAssistantResponsesRef.current = queuedAssistantResponsesRef.current
        .filter((request) => request.context !== active.responseContext);
      cancelRealtimeResponse();
    }
    pendingSpokenMessagesRef.current = pendingSpokenMessagesRef.current.filter((message) => {
      if (ownsSpokenHostMessage(message)) return true;
      message.onInterrupted?.();
      return false;
    });
    flushSpokenMessagesRef.current();
  }, [cancelRealtimeResponse, hostContext, ownsSpokenHostMessage, session]);

  const canFinishAutomatically = useCallback(() => {
    const capture = pendingAutomaticClosingRef.current;
    return Boolean(capture?.automaticFinishReady && finishAutomaticClosingRef.current(capture));
  }, []);

  const toggleMute = useCallback(() => {
    const nextMutedState = !isMuted;
    localStreamRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = !nextMutedState;
    });
    setIsMuted(nextMutedState);
  }, [isMuted]);

  const closingSafetyWait = closingSafetyWaitRef.current;
  const closingSafetyError = closingSafetyWait && closingSafetyTimeoutKey === closingSafetyWait.key
    && closingSafetyWait.expired && closingSafetyWait.isCurrent() ? CLOSING_SAFETY_TIMEOUT_MESSAGE : null;
  return {
    loading,
    error: error ?? closingSafetyError,
    inputError,
    session,
    connectionState,
    isReconnecting,
    micPermission,
    isMuted,
    assistantPhase,
    completionClosingPending,
    turnProcessing,
    userTranscript,
    assistantTranscript,
    turnHistory,
    assistantDebugHistory,
    liveUserDebugEntry,
    liveAssistantDebugEntry,
    recentRealtimeEvents,
    uiMode,
    resourcesPath,
    ctaLabel,
    currentQuestion,
    currentTarget,
    progress,
    contentSchema,
    itemStates,
    instructionBlock,
    latestExtraction,
    connect,
    reconnect,
    refreshSession,
    clearCrisisPause,
    resetVoiceRun,
    setDebugRealtimeModelOverride,
    setDebugTranscriptionModelOverride,
    disconnect,
    toggleMute,
    interruptAssistant,
    replayAssistantMessage,
    clearLiveTranscript,
    capturePaused,
    executeCommand,
    flushPendingDraft,
    disconnectAsync,
    speak,
    speakConversational,
    terminalActionReady,
    automaticCompletionReady,
    canFinishAutomatically,
  };
};
